import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

const translations = {
  en: {
    'network.online': 'Online',
    'network.offline': 'Offline',
    'connectivity.online': 'Connected',
    'connectivity.offline': 'No network',
    'availability.online': 'Available',
    'availability.away': 'Away',
    'availability.offline': 'Do not disturb',
    'availability.menuTitle': 'Your status',
    'floatingChat.toggle': 'Private messages',
    'floatingChat.panelTitle': 'Private messages',
    'floatingChat.back': 'Back',
    'floatingChat.sectionRecent': 'Recent',
    'floatingChat.sectionBrokers': 'Brokers & clients',
    'floatingChat.sectionPrivateLenders': 'Private lenders & investors',
    'floatingChat.sectionDealShares': 'Deal shares',
    'floatingChat.dealSharesHint': 'Open one of your buildings to see people who added this deal via your share link.',
    'floatingChat.noSharesForProperty': 'No share link for this building yet.',
    'floatingChat.emptyBrokers': 'No linked brokers or clients yet.',
    'floatingChat.emptyPrivateLenders': 'No placements yet.',
    'floatingChat.emptyDealShares': 'No one else has added this deal yet.',
    'feedback.toggle': 'Feedback',
    'feedback.panelTitle': 'Send feedback',
    'feedback.categoryLabel': 'Category',
    'feedback.messageLabel': 'Message',
    'feedback.messagePlaceholder': 'What should we improve? What went wrong? What do you want to see?',
    'feedback.category.improvement': 'Improvement',
    'feedback.category.bug': 'Bug / problem',
    'feedback.category.feature': 'New feature',
    'feedback.includeConsole': 'Include console logs (last 200 events)',
    'feedback.capture': 'Screenshot',
    'feedback.capturing': 'Capturing…',
    'feedback.captureOk': 'Screenshot captured.',
    'feedback.captureError': 'Unable to capture a screenshot. You can still send without it.',
    'feedback.screenshotAttached': 'Screenshot attached',
    'feedback.screenshotAlt': 'Attached screenshot preview',
    'feedback.remove': 'Remove',
    'feedback.send': 'Send',
    'feedback.sending': 'Sending…',
    'feedback.sendError': 'Unable to send feedback. Please try again.',
    'feedback.sent': 'Thanks — feedback saved.',
    'feedbackAdmin.title': 'Feedback (admin)',
    'feedbackAdmin.exportJson': 'Export JSON',
    'feedbackAdmin.loaded': 'Loaded:',
    'feedbackAdmin.empty': 'No feedback yet.',
    'feedbackAdmin.colWhen': 'When',
    'feedbackAdmin.colAuthor': 'Submitted by',
    'feedbackAdmin.colCategory': 'Category',
    'feedbackAdmin.colMessage': 'Message',
    'feedbackAdmin.colScreenshot': 'Screenshot',
    'feedbackAdmin.colConsole': 'Console',
    'feedbackAdmin.colStatus': 'Status',
    'feedbackAdmin.colActions': 'Actions',
    'feedbackAdmin.openPage': 'Open page',
    'feedbackAdmin.openScreenshot': 'Open screenshot',
    'feedbackAdmin.consoleEvents': 'events',
    'feedbackAdmin.triage': 'Triage',
    'feedbackAdmin.markTriaged': 'Mark as triaged',
    'feedbackAdmin.untriage': 'Un-triage',
    'feedbackAdmin.markNew': 'Mark as new',
    'feedbackAdmin.working': 'Working…',
    'feedbackAdmin.actionError': 'Action failed (permissions or network).',
    'feedbackAdmin.copyCursorPrompt': 'Copy Cursor prompt',
    'feedbackAdmin.copied': 'Copied',
    'feedbackAdmin.copyFailed': 'Copy failed',
    'feedbackAdmin.showConsole': 'Show console logs',
    'feedbackAdmin.filterAll': 'All',
    'feedbackAdmin.filterNew': 'New',
    'feedbackAdmin.filterTriaged': 'Triaged',
    'feedbackAdmin.filterArchived': 'Archived',
    'feedbackAdmin.archive': 'Archive',
    'feedbackAdmin.unarchive': 'Unarchive',
    'feedbackAdmin.unknownAuthor': 'Unknown user',
    'directChat.title': 'Private message',
    'directChat.sensitiveHint':
      'Only you and the other person can read this thread. Use it for negotiation or sensitive topics.',
    'directChat.empty': 'No private messages yet.',
    'directChat.placeholder': 'Write a private message…',
    'directChat.sendShortcutHint': 'Send (Ctrl+Enter or ⌘+Enter)',
    'directChat.modalTitle': 'Private conversation',
    'directChat.ensureError': 'Could not open this conversation. The link between accounts may be missing or out of date.',
    'directChat.noResolvableShareToken':
      'No active share link connects you and this person for a private chat. Ask them to open your invite again.',
    'directChat.ensureErrorBrokerClient':
      'This chat uses your broker invitation link. The stored link may be outdated, or your client record is not fully linked. Ask your broker to resend the invitation, or sign in with the same email as the invitation. If you already did, try signing out and back in.',
    'directChat.recipientsSection': 'Private messages with recipients',
    'directChat.messageRecipient': 'Private message',
    'directChat.messageOwner': 'Message the deal owner',
    'directChat.inboxTitle': 'Private messages',
    'directChat.inboxEmpty': 'No conversations yet.',
    'directChat.peerFallback': 'Contact',
    'directChat.goToDeal': 'Go to deal',
    'directChat.goToSharedDeal': 'Go to shared deal',
    'directChat.loadError': 'Unable to load messages. Check your connection.',
    'directChat.you': 'You',
    'directChat.send': 'Send',
    'directChat.loginToParticipate': 'Log in to read and send messages.',
    'header.privateMessages': 'Messages',
    'clients.messagePrivate': 'Message',
    'fundInvestors.messagePrivate': 'Message',
    'investorFunds.messageManager': 'Message fund manager',
    'profile.notifyDirectMessageEmail': 'Email me when someone sends a private message',
    'profile.purchaseOffersModule': 'Purchase offer workflow (mandated listings)',
    'profile.purchaseOffersModuleHint':
      'Turn on to declare a sales mandate on new analyses and to share links with indicative and formal PDF offers. You can turn this off later; deals already started under this workflow remain available.',
    'auth.login.title': 'Login',
    'auth.login.error': 'Failed to log in',
    'auth.login.invitedByBroker': 'A broker invited you. Log in to link your account to them.',
    'auth.linkingBroker': 'Linking you to your broker…',
    'auth.linkingInvestorAccount': 'Linking your investor account…',
    'auth.linkingBrokerError': 'Could not link to your broker. You can try again later or go to the home page.',
    'auth.invitationWrongAccount':
      'You are signed in with a different account than the one this invitation was sent to ({email}). Sign out, then open the link again, or use a private browsing window.',
    'auth.invitationWrongAccountHelp': 'Use Log out below, then sign in with the invited email or open the invitation link in a private window.',
    'auth.invitationLinkPermissionHelp':
      'This invitation could not be completed (access denied). If you are signed in with the wrong account, sign out and open the link again with the invited email ({email}).',
    'auth.invitationSignupEmailMismatch':
      'This invitation was sent to {email}. Create an account or sign in with that exact email address.',
    'auth.goToHome': 'Go to home',
    'auth.google.error': 'Unable to log in with Google',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.login.submit': 'Log In',
    'auth.google': 'Continue with Google',
    'auth.forgotPassword': 'Forgot password?',
    'auth.createAccount': 'Create an account',
    'auth.signup.title': 'Sign Up',
    'auth.signup.error': 'Unable to create account',
    'auth.signup.invitedByBroker': 'Your broker or lender invited you. Create your account to add your property as an investor.',
    'auth.signup.submit': 'Sign Up',
    'auth.error.emailAlreadyInUse': 'An account already exists for this email.',
    'auth.error.emailAlreadyInUseUseLogin': 'This email is already registered. Log in to accept your broker\'s invitation.',
    'auth.signup.useLoginInstead': 'Log in instead',
    'signup.accountType.label': 'Account type',
    'signup.accountType.investisseur': 'Real estate investor',
    'signup.accountType.required': 'Please select an account type.',
    'signup.accountType.comingSoon': 'Coming soon',
    'signup.accountType.comingSoonBadge': 'Soon',
    'signup.accountType.teaserHint':
      'Broker and private lender access is coming soon. For now, sign up as a real estate investor.',
    'signup.accountType.notAvailable': 'This account type is not available yet.',
    'signup.legal.acceptPrefix': 'I agree to the',
    'signup.legal.acceptAnd': 'and the',
    'signup.legal.acceptSuffix': '.',
    'signup.legal.required': 'You must accept the Terms of Use and Privacy Policy to continue.',
    'legal.terms': 'Terms of Use',
    'legal.privacy': 'Privacy Policy',
    'legal.back': 'Back',
    'signup.verification.intro':
      'Broker and private lender accounts require verification. An administrator will review your request before granting access.',
    'signup.verification.companyName': 'Company name',
    'signup.verification.phone': 'Phone number',
    'signup.verification.address': 'Business address',
    'signup.verification.companyRequired': 'Company name is required.',
    'signup.verification.phoneRequired': 'Phone number is required.',
    'signup.verification.addressRequired': 'A complete address is required.',
    'approval.pending.title': 'Account pending approval',
    'approval.pending.message':
      'Your account request has been submitted. An administrator will review your information and approve access shortly. You will receive an email once your account is activated.',
    'approval.pending.requestedType': 'Requested account type',
    'approval.rejected.title': 'Account not approved',
    'approval.rejected.message':
      'Your account request was not approved. Contact support at admin@rentalyzer.ca if you have questions.',
    'admin.approvals.title': 'Account approvals',
    'admin.approvals.empty': 'No pending requests.',
    'admin.approvals.approve': 'Approve',
    'admin.approvals.reject': 'Reject',
    'admin.approvals.company': 'Company',
    'admin.approvals.phone': 'Phone',
    'admin.approvals.address': 'Address',
    'admin.approvals.email': 'Email',
    'admin.approvals.persona': 'Account type',
    'admin.approvals.rejectReasonPrompt': 'Reason for rejection (optional)',
    'admin.approvals.approved': 'Account approved.',
    'admin.approvals.rejected': 'Account rejected.',
    'profile.companyName': 'Company name',
    'auth.error.weakPassword': 'Password must be at least 6 characters.',
    'auth.error.invalidEmail': 'Invalid email address.',
    'auth.error.operationNotAllowed': 'Email/password sign-up is disabled. Contact support.',
    'auth.error.tooManyRequests': 'Too many attempts. Try again later.',
    'auth.error.accountExistsWithDifferentCredential': 'This email is already used with another sign-in method. Try logging in with email/password or use the same method you used before.',
    'auth.haveAccount': 'Already have an account?',
    'auth.login.link': 'Log In',
    'auth.reset.title': 'Reset Password',
    'auth.reset.success': 'Check your email for further instructions.',
    'auth.reset.error': 'Failed to reset password',
    'auth.reset.submit': 'Reset',
    'auth.completeReset.title': 'Choose a new password',
    'auth.completeReset.subtitle': 'For {email}',
    'auth.completeReset.newPassword': 'New password',
    'auth.completeReset.confirmPassword': 'Confirm password',
    'auth.completeReset.submit': 'Save new password',
    'auth.completeReset.success': 'Your password has been updated. Redirecting to sign in…',
    'auth.completeReset.error': 'Unable to update your password. Please try again.',
    'auth.completeReset.mismatch': 'Passwords do not match.',
    'auth.completeReset.invalidLink': 'This reset link is invalid or has already been used.',
    'auth.completeReset.expiredLink': 'This reset link has expired. Please request a new one.',
    'auth.completeReset.weakPassword': 'Password is too weak (minimum 6 characters).',
    'auth.backToLogin': 'Back to login',
    'auth.logout': 'Log out',
    'onboarding.title': 'Welcome to Rentalyzer',
    'onboarding.question': 'How do you use Rentalyzer?',
    'onboarding.continue': 'Continue',
    'tour.progress': '{{current}} / {{total}}',
    'tour.actions.skip': 'Skip',
    'tour.actions.next': 'Next',
    'tour.actions.previous': 'Previous',
    'tour.actions.done': 'Done',
    'tour.actions.resume': 'Resume tutorial',
    'tour.assistant.title': 'Help guide',
    'tour.assistant.subtitle': 'Interactive tours',
    'tour.assistant.pulseHint': 'Need help? Tours are here.',
    'tour.names.welcome': 'Getting started',
    'tour.names.dashboard': 'Building dashboard',
    'tour.names.createProject': 'Create a project',
    'tour.names.analysis': 'Financing scenario',
    'tour.names.report': 'Read the report',
    'tour.names.settings': 'Settings',
    'tour.status.completed': 'Completed',
    'tour.status.skipped': 'Skipped',
    'tour.status.pending': 'Not started',
    'tour.status.soon': 'Soon',
    'tour.welcome.intro.title': 'Welcome to Rentalyzer',
    'tour.welcome.intro.description': 'Your smart assistant for profitable real estate investments.',
    'tour.welcome.intro.description.investisseur': 'This is where we analyze the profitability of your deals.',
    'tour.welcome.intro.description.courtier_immo': 'Manage client deals, analyses, and reports from here.',
    'tour.welcome.intro.description.courtier_hypo': 'Track borrower properties and financing files here.',
    'tour.welcome.intro.description.preteur_prive': 'Overview of your loans, borrowers, and financed buildings.',
    'tour.welcome.create.title': 'New deal',
    'tour.welcome.create.description':
      'Click here to add a deal. You will choose whether it is a property to analyze (purchase) or a building you already own.',
    'tour.welcome.tabs.title': 'Your sections',
    'tour.welcome.tabs.description': 'Switch between your deals, placements, and shared projects.',
    'tour.welcome.profile.title': 'Profile & presence',
    'tour.welcome.profile.description': 'Your photo, language, and preferences. The Online/Away status is visible in chat.',
    'tour.welcome.profile.description.investisseur': 'Your photo and preferences. Set En ligne, Absent, or Hors ligne — visible to contacts in chat.',
    'tour.welcome.nav.title': 'Professional tools',
    'tour.welcome.nav.description': 'Access clients, checklists, and files from the top menu.',
    'tour.welcome.nav.preteur_prive.description': 'Manage borrowers, loans, and capital investors from the menu.',
    'tour.welcome.nav.courtier_immo.description': 'Access clients, checklists, and pocket campaigns from the menu.',
    'tour.welcome.nav.courtier_hypo.description': 'Access borrowers, checklists, and shared deals from the menu.',
    'tour.createProject.intro.title': 'New building analysis',
    'tour.createProject.intro.description':
      'On this page, you add a property to analyze. We will ask for several details about the building. Be precise — the quality of your profitability analysis depends on the accuracy of this step.',
    'tour.createProject.header.title': 'Property form',
    'tour.createProject.header.description': 'This is where you describe the building you want to analyze.',
    'tour.createProject.basic.title': 'Basic information',
    'tour.createProject.basic.description':
      'Enter the property details and select the CMHC benchmarks you want to use for your analysis.',
    'tour.createProject.revenue.title': 'Enter revenue',
    'tour.createProject.revenue.description':
      'Enter the property\'s income. Remember to include all ancillary revenue (garage, parking, internet, laundry, etc.).',
    'tour.createProject.expenses.title': 'Enter operating expenses',
    'tour.createProject.expenses.description':
      'Enter your operating expenses. The more accurate your figures, the more reliable your deal\'s performance will be.',
    'tour.createProject.mode.title': 'Advanced mode (revenue & expenses)',
    'tour.createProject.mode.description':
      'Click here to break down revenue and expenses in detail for a sharper analysis and better alignment with CMHC standards.',
    'tour.createProject.save.title': 'Save and add photos / description',
    'tour.createProject.save.description':
      'Once the property is saved, you can add photos and a description. AI can help you draft the marketing text.',
    'tour.createProject.owned.intro.title': 'Property you own',
    'tour.createProject.owned.intro.description':
      'On this page, you describe a building you already hold. Enter its estimated market value, income, expenses, and current mortgage (or none). There is no purchase price or acquisition costs.',
    'tour.createProject.owned.basic.title': 'Building & estimated value',
    'tour.createProject.owned.basic.description':
      'Enter the address, number of units, and estimated market value. This value is used for cap rate, LTV, and return on equity.',
    'tour.createProject.owned.financing.title': 'Current financing',
    'tour.createProject.owned.financing.description':
      'Choose in-place financing (outstanding balance and monthly payment) or no mortgage. This is not a purchase assumption — it reflects the loan already on the property.',
    'tour.createProject.owned.save.title': 'Save and open the report',
    'tour.createProject.owned.save.description':
      'Saving creates a “Current situation” scenario and opens the performance report based on your equity.',
    'tour.dashboard.overview.title': 'Analyzed deal',
    'tour.dashboard.overview.description':
      'View the key indicators for your deal analysis here.',
    'tour.dashboard.intro.title': 'Building dashboard',
    'tour.dashboard.intro.description': 'Overview of this property before you run financing scenarios.',
    'tour.dashboard.title.title': 'Your property',
    'tour.dashboard.title.description': 'The address and context for this analysis.',
    'tour.dashboard.kpis.title': 'Key metrics',
    'tour.dashboard.kpis.description': 'Price, income, cap rate, and other indicators at a glance.',
    'tour.dashboard.scenario.title': 'Create your scenarios',
    'tour.dashboard.scenario.description':
      'Click here to build financing scenarios and model the property\'s profitability.',
    'tour.dashboard.dataRoom.title': 'Relevant documents',
    'tour.dashboard.dataRoom.description':
      'Use the data room to store files related to the property and organize them by category.',
    'tour.dashboard.rentRegister.title': 'Rent register',
    'tour.dashboard.rentRegister.description':
      'Record rent, inclusions, and ancillary services for each unit. Only the deal owner can edit this register; shared access is view-only.',
    'tour.dashboard.edit.title': 'Edit deal information',
    'tour.dashboard.edit.description':
      'Click here to update price, revenue, expenses, photos, and description.',
    'tour.dashboard.owned.overview.title': 'Owned property',
    'tour.dashboard.owned.overview.description':
      'Key metrics for a building you hold: estimated value, net equity, LTV, and income.',
    'tour.dashboard.owned.rentRegister.description':
      'Track rent and inclusions per unit for this owned property. Useful for operations and refinancing files.',
    'tour.dashboard.owned.scenario.title': 'Refinancing & optimization',
    'tour.dashboard.owned.scenario.description':
      'Create additional scenarios to model refinancing, renewal, or optimization. Your current financing is already saved as “Current situation”.',
    'tour.dashboard.owned.edit.title': 'Edit property details',
    'tour.dashboard.owned.edit.description':
      'Update market value, income, expenses, and in-place financing for this owned building.',
    'tour.analysis.intro.title': 'Creating a financing scenario',
    'tour.analysis.intro.description':
      'This section lets you build a financing scenario to analyze the property\'s profitability.',
    'tour.analysis.title.title': 'Name your scenario',
    'tour.analysis.title.description':
      'Give your scenario a clear name so you can identify it easily later.',
    'tour.analysis.header.title': 'Scenario setup',
    'tour.analysis.header.description': 'Name your scenario to find it later among other versions.',
    'tour.analysis.propertyData.title': 'Review deal data',
    'tour.analysis.propertyData.description':
      'Review the data entered for this deal. To change deal information, return to the building dashboard and edit the property details.',
    'tour.analysis.financing.title': 'Deal financing',
    'tour.analysis.financing.description':
      'Enter the financing details for this real estate deal.',
    'tour.analysis.acquisition.title': 'Acquisition costs',
    'tour.analysis.acquisition.description':
      'Add your acquisition costs to determine the property\'s total acquisition cost. Every acquisition expense matters for an accurate picture of your investment returns.',
    'tour.analysis.save.title': 'Generate the report',
    'tour.analysis.save.description': 'Save to open the full profitability report.',
    'tour.report.intro.title': 'Profitability analysis report',
    'tour.report.intro.description':
      'This is your deal\'s profitability analysis report. It brings together the property\'s performance indicators, including how they evolve over time.',
    'tour.report.enteredInfo.title': 'Entered information',
    'tour.report.enteredInfo.description':
      'A summary of the data entered for this deal: price, revenue, expenses, and financing parameters.',
    'tour.report.edit.title': 'Edit scenario',
    'tour.report.edit.description':
      'Return to the financing scenario to adjust terms and acquisition costs, then refresh your profitability analysis.',
    'tour.report.header.title': 'Report overview',
    'tour.report.header.description': 'Summary of the property and scenario you are viewing.',
    'tour.report.indicators.title': 'Key indicators',
    'tour.report.indicators.description':
      'The main metrics for this deal. At a glance, they show whether the project meets your baseline criteria.',
    'tour.report.financialSummary.title': 'Financial summary',
    'tour.report.financialSummary.description':
      'Review the cash flow generated by the analyzed deal.',
    'tour.report.financingSummary.title': 'Financing summary',
    'tour.report.financingSummary.description':
      'The financing structure for this scenario: down payment, loan, rate, amortization, and total investment cost.',
    'tour.report.futureReturns.title': 'Return analysis',
    'tour.report.futureReturns.description':
      'Project returns by adjusting assumptions for revenue growth, expense growth, and property value appreciation.',
    'tour.report.amortization.title': 'Amortization schedule',
    'tour.report.amortization.description':
      'Mortgage amortization combined with property value growth based on the appreciation assumption above.',
    'tour.report.subScenarios.title': 'Sub-scenarios',
    'tour.report.subScenarios.description':
      'Go further with an optimization, refinancing, or mortgage renewal sub-scenario, then fold it into the return models above.',
    'tour.report.pdf.title': 'Generate a report',
    'tour.report.pdf.description':
      'Export a PDF to present your analysis and validate your assumptions with partners or lenders.',
    'tour.report.owned.intro.title': 'Owned property performance report',
    'tour.report.owned.intro.description':
      'This report measures performance on your equity: cash flow, cash-on-cash, loan paydown, and projected returns — without acquisition costs.',
    'tour.report.owned.enteredInfo.description':
      'Summary of estimated market value, income, expenses, and current financing (in-place mortgage or none).',
    'tour.report.owned.indicators.description':
      'Focus on net equity, cash flow, CoC on equity, LTV, and total return for a held property.',
    'tour.report.owned.financingSummary.description':
      'Shows outstanding loan, net equity, and LTV. Acquisition fees do not apply to owned properties.',
    'tour.report.owned.subScenarios.title': 'Refinancing & optimization',
    'tour.report.owned.subScenarios.description':
      'Add sub-scenarios to model refinancing, renewal, or operational improvements on this owned building.',
    'tour.report.owned.futureReturns.description':
      'Project multi-year returns starting from today’s estimated value and your current equity.',
    'tour.settings.intro.title': 'Profile & preferences',
    'tour.settings.intro.description': 'Customize how Rentalyzer works for you.',
    'tour.settings.persona.title': 'Your persona',
    'tour.settings.persona.description': 'Choose the role that best matches how you use Rentalyzer.',
    'tour.settings.language.title': 'Language',
    'tour.settings.language.description': 'Switch between French and English anytime.',
    'tour.assistant.startError': 'Could not start this tour. Open the relevant screen first, then try again.',
    'tour.assistant.needsContext': 'Navigate to the relevant screen for best results',
    'tour.assistant.recommended': 'Recommended next step',
    'tour.assistant.suggestionsTitle': 'Tips for you',
    'tour.assistant.allTours': 'All tours',
    'tour.assistant.aiComingSoon': 'AI assistant coming soon — contextual tips for now.',
    'tour.assistant.greeting': 'Need a hand? Pick a tour or follow the suggestion below.',
    'tour.assistant.greeting.investisseur': 'Analyze deals faster — tours adapt to your investor workflow.',
    'tour.assistant.greeting.courtier_immo': 'Guide for real estate brokers: clients, deals, and reports.',
    'tour.assistant.greeting.courtier_hypo': 'Guide for mortgage brokers: borrower files and shared deals.',
    'tour.assistant.greeting.preteur_prive': 'Guide for private lenders: loans, borrowers, and capital investors.',
    'tour.assistant.suggest.welcome': 'Start with the home tour',
    'tour.assistant.suggest.createProject': 'Learn to create a deal (analysis or owned)',
    'tour.assistant.suggest.dashboard': 'Explore the building dashboard',
    'tour.assistant.suggest.analysis': 'Set up a financing scenario',
    'tour.assistant.suggest.report': 'Read and export your report',
    'tour.assistant.suggest.settings': 'Adjust profile and preferences',
    'tour.assistant.tip.firstDeal':
      'Create your first deal — either a purchase to analyze or a property you already own — to unlock the full path.',
    'tour.assistant.tip.firstDealBroker':
      'Add a client property (purchase or owned) or your own deal to begin.',
    'tour.assistant.tip.lenderHome': 'Use Loan management and Borrowers tabs for your lending activity.',
    'tour.assistant.tip.brokerPdf': 'Export a PDF report to present the deal to your client.',
    'tour.checklist.title': 'Getting started',
    'tour.checklist.createDeal': 'Create a deal (to analyze or already owned)',
    'tour.checklist.runScenario': 'Save a financing scenario',
    'tour.checklist.exportPdf': 'Generate a PDF report',
    'tour.welcome.create.description.investisseur':
      'Click here to add a deal. Choose a property to analyze (purchase) or a building you already own.',
    'tour.welcome.create.description.courtier_immo':
      'Start a new deal for you or a client. You can analyze a purchase or document a property already owned.',
    'tour.welcome.create.description.courtier_hypo':
      'Launch an analysis for a borrower property — either a purchase underwriting or a building they already hold.',
    'tour.welcome.create.description.preteur_prive':
      'Model a building before structuring a private loan, including properties already owned by the borrower.',
    'tour.welcome.hero.description.investisseur': 'Your hub for deals, shared projects, and portfolio tools.',
    'tour.welcome.hero.description.courtier_immo': 'Centralize client deals, pocket campaigns, and shared files.',
    'tour.welcome.hero.description.courtier_hypo': 'Track borrower buildings and financing dossiers.',
    'tour.welcome.hero.description.preteur_prive': 'Access loans, borrowers, and financed properties.',
    'tour.createProject.intro.description.investisseur':
      'On this page, you add a property to analyze. Be precise with revenue and expenses — your profitability metrics depend on it.',
    'tour.createProject.intro.description.courtier_immo':
      'On this page, you add a property to analyze. Enter accurate data to produce a reliable analysis you can share with your client.',
    'tour.createProject.intro.description.courtier_hypo':
      'On this page, you document the property before submitting to lenders. Data accuracy is essential.',
    'tour.createProject.intro.description.preteur_prive':
      'On this page, you record the asset under evaluation. Precise figures make financing easier to structure.',
    'tour.createProject.save.description.investisseur':
      'Once saved, add photos and a description, then move on to your financing scenario.',
    'tour.createProject.save.description.courtier_immo':
      'Once saved, add photos and a description to enrich the file you share with your client.',
    'tour.createProject.save.description.courtier_hypo':
      'Once saved, add photos and a description to complete the borrower file.',
    'tour.createProject.save.description.preteur_prive':
      'Once saved, add photos and a description before continuing your loan evaluation.',
    'tour.dashboard.scenario.description.investisseur':
      'Model mortgage terms here to analyze cash flow and returns for this deal.',
    'tour.dashboard.scenario.description.courtier_immo':
      'Build scenarios here to compare financing options with your client.',
    'tour.dashboard.scenario.description.courtier_hypo':
      'Create scenarios here to prepare financing assumptions for the dossier.',
    'tour.dashboard.scenario.description.preteur_prive':
      'Define loan terms here before generating your profitability report.',
    'tour.analysis.intro.description.investisseur':
      'Build a financing scenario here to model cash flow and returns for this deal.',
    'tour.analysis.intro.description.courtier_immo':
      'Create a financing scenario here to produce a client-ready profitability analysis.',
    'tour.analysis.intro.description.courtier_hypo':
      'Set up a financing scenario here to align terms with lender criteria.',
    'tour.analysis.intro.description.preteur_prive':
      'Define a financing scenario here with private-lender terms and acquisition costs.',
    'tour.report.pdf.description.investisseur': 'Export a PDF for your partners or your investment file.',
    'tour.report.pdf.description.courtier_immo': 'Generate a polished PDF for your client presentation.',
    'tour.report.pdf.description.courtier_hypo': 'Share a PDF summary with the borrower or lender.',
    'tour.report.pdf.description.preteur_prive': 'Download the report for your credit committee or investor.',
    'persona.courtier_immo': 'Real estate broker',
    'persona.investisseur': 'Investor',
    'persona.courtier_hypo': 'Mortgage broker',
    'persona.preteur_prive': 'Private lender',
    'persona.autre': 'Other',
    'profile.title': 'My profile',
    'profile.menuItem': 'My profile',
    'pwa.install': 'Install the app',
    'profile.displayName': 'Display name',
    'profile.displayNamePlaceholder': 'Your name or company',
    'profile.avatarUrl': 'Avatar (image URL)',
    'profile.persona': 'Profile type',
    'profile.language': 'Language',
    'profile.save': 'Save',
    'profile.tabPersonal': 'Personal info',
    'profile.tabCompanies': 'My companies',
    'profile.personalAddressSection': 'Personal address',
    'profile.personalAddressHint': 'This is your home or mailing address as an individual. It is separate from your companies’ addresses. Start typing the street for Google address suggestions.',
    'profile.phone': 'Phone',
    'profile.addressStreet': 'Street and number',
    'profile.addressCity': 'City',
    'profile.addressProvince': 'Province / state',
    'profile.addressPostalCode': 'Postal code',
    'profile.addressCountry': 'Country',
    'profile.bio': 'About you',
    'profile.bioPlaceholder': 'A few words about yourself (optional)',
    'profile.emailReadOnly': 'Email (sign-in)',
    'profile.saveError': 'Failed to save profile',
    'profile.saveSuccessVerse': 'A breath, a tap — your profile finds a quiet nest in the cloud above.',
    'profile.unknown': 'User',
    'profile.deleteAccount': 'Delete my account',
    'profile.deleteAccountWarning': 'This will permanently delete your account and all your data: your profile, all your properties and your deals. This action cannot be undone.',
    'profile.deleteAccountConfirm': 'Yes, delete my account and all my data',
    'profile.deleteAccountError': 'Failed to delete account. If you signed in a long time ago, try signing out, signing back in, then delete again.',
    'profile.deleteAccountBrokerHasClients': 'You cannot delete your account while you have active clients. Remove or transfer all clients in My Clients first.',
    'profile.deleting': 'Deleting…',
    'profile.deleteAccountPasswordHint': 'Enter your password to confirm your identity',
    'profile.deleteAccountReauthGoogle': 'You will be asked to sign in with Google again to confirm your identity.',
    'about.back': 'Back',
    'about.tagline': 'Actuary by training, entrepreneur and real estate investor',
    'about.intro': 'Passionate about financial analysis and real estate, I developed Rentalyzer to provide investors with a simple and reliable tool to validate the actual returns of their projects.',
    'about.mvvTitle': 'Application mission, vision, and values',
    'about.mvv1': 'Rentalyzer was born from the desire to democratize real estate profitability analysis and help investors make informed decisions.',
    'about.mvv2': 'In the long term, the goal is to support investors in understanding their investments and validating the actual returns they generate.',
    'about.valuesLabel': 'Values',
    'about.valuesList': 'Simplicity, Reliability, Ethics',
    'about.backgroundTitle': 'Professional background',
    'about.education': "Holder of a bachelor's degree in actuarial science",
    'about.entrepreneur': 'Entrepreneur since the age of 20',
    'about.investor': 'Experienced real estate investor',
    'about.lender': 'Private lender (mortgage investment fund or similar)',
    'about.contactTitle': 'Contact',
    'about.email': 'Email',
    'about.phone': 'Phone',
    'about.linkedin': 'LinkedIn',
    'about.photoAlt': 'Developer photo',
    'loading': 'Loading...',
    'errorBoundary.message': 'Something went wrong. Try refreshing the page.',
    'errorBoundary.refresh': 'Refresh page',
    'property.import.success': 'Property imported successfully',
    'share.link.copied': 'Share link copied to clipboard',
    'share.error': 'Error while sharing',
    'home.title': 'Multifamily Real Estate Profitability Analysis Tool',
    'home.subtitle': 'Rentalyzer, your smart assistant for successful multifamily real estate investments',
    'home.empty.title': '🚀 Start your first deal',
    'home.step1.title': '1. Enter your data',
    'home.step1.text': 'Price, income, expenses – we guide you every step of the way',
    'home.step2.title': '2. Get the analysis',
    'home.step2.text': 'Profitability, cash flow, personalized recommendations',
    'home.step3.title': '3. Generate the report',
    'home.step3.text': 'Professional report ready to present',
    'home.new.button': 'Analyze my first building',
    'home.existing.title': 'My Deals',
    'home.existing.new': 'New deal',
    'home.sectionMyAnalyses': 'My Deals',
    'home.mandatesFilter': 'Mandated listings only',
    'home.mandatesFilterHint': 'Show only buildings flagged with a broker sales mandate.',
    'home.mandatesFilterEmpty': 'No buildings match this filter.',
    'home.sectionPocketCampaigns': 'Pocket campaigns',
    'home.pocketCampaignsTitle': 'Recent pocket campaigns',
    'home.pocketCampaignsIntro':
      'Metrics per campaign (emails sent, opens, clicks, signups, interest requests). Resubmit opens the VIP / pocket list tab directly so you can send again after fixing an error or updating the deal.',
    'home.pocketCampaignsEmpty': 'No pocket campaigns yet. Send a pocket deal from a property’s Share → VIP / pocket tab.',
    'home.pocketCampaignProperty': 'Property',
    'home.pocketCampaignPropertyMissing': 'Unknown or deleted property',
    'home.pocketCampaignActions': 'Actions',
    'home.pocketCampaignResubmit': 'Resubmit',
    'home.sectionClientProperties': 'Client Properties',
    'home.clientPropertiesCount': '{{count}} properties',
    'home.clientPropertiesCount_one': '1 property',
    'home.clientPropertiesCount_other': '{{count}} properties',
    'home.noClientProperties': 'No properties shared by clients yet.',
    'home.clientDeleted': 'Deleted client',
    'home.unknownClient': 'Unknown client',
    'home.noMyAnalyses': 'No deals yet. Create one with the button above.',
    'home.share': 'Share',
    'home.newBadge': 'New',
    'home.delete': 'Delete',
    'home.address.unset': 'Address not specified',
    'home.units': 'units',
    'home.pricePerDoor': '$/door',
    'home.perYear': '$/year',
    'home.about': 'ABOUT',
    'home.clients': 'My clients',
    'home.borrowers': 'My borrowers',
    'home.checklists': 'My checklists',
    'home.marketParams': 'Market parameters',
    'header.goToHome': 'Home',
    'header.menu': 'Menu',
    'clients.title': 'My clients',
    'clients.add': 'Add client',
    'clients.name': 'Name',
    'clients.address': 'Address',
    'clients.email': 'Email',
    'clients.noClients': 'No clients yet.',
    'clients.propertiesLinked': 'Properties linked',
    'clients.inviteLinkCreated': 'Invitation created. An email has been sent to the client (or copy the link below).',
    'clients.inviteLinkCopy': 'Send this link to your client so they can sign up as an investor and add their property.',
    'clients.inviteQueueError': 'Could not queue invitation email:',
    'clients.copyLink': 'Copy link',
    'clients.sending': 'Sending…',
    'clients.resendInvitation': 'Resend invitation email',
    'clients.bulkInviteAllSummary':
      '{n} client(s) have not linked their account yet — you can queue invitation emails for all of them.',
    'clients.bulkInviteAllSummaryBorrower':
      '{n} borrower(s) have not linked their account yet — you can queue invitation emails for all of them.',
    'clients.bulkInviteAllButton': 'Invite all pending (email)',
    'clients.bulkInviteAllConfirm':
      'Send invitation emails to everyone who has not linked their account yet? Each will receive the same signup link as for a single resend.',
    'clients.bulkInviteAllSending': 'Sending…',
    'clients.bulkInviteAllResultOk': '{n} invitation email(s) queued.',
    'clients.bulkInviteAllResultPartial':
      '{ok} invitation(s) queued, {failed} failed — check the console or try again for those.',
    'clients.invitationResent': 'Sent!',
    'clients.invitationResentConfirm': 'Invitation resent to',
    'clients.edit': 'Edit',
    'clients.delete': 'Delete',
    'clients.titleBorrowers': 'My borrowers',
    'clients.addBorrower': 'Add borrower',
    'clients.noBorrowers': 'No borrowers yet.',
    'clients.inviteLinkCopyBorrower':
      'Send this link so the borrower can create their account. Draft buildings you added will appear on their dashboard after they sign up.',
    'clients.propertiesLinkedBorrower': 'Buildings linked',
    'clients.stagedSectionTitle': 'Draft buildings (before borrower signs up)',
    'clients.stagedSectionHelp':
      'Only the address is required. Analysis fields can be completed later. Offers and loans can be created in the app once the building exists under the borrower’s account.',
    'clients.stagedBuildingsCount': 'Drafts',
    'clients.stagedAddress': 'Street address',
    'clients.stagedCity': 'City',
    'clients.stagedProvince': 'Province',
    'clients.stagedPostal': 'Postal code',
    'clients.stagedNotes': 'Notes (optional)',
    'clients.stagedAddButton': 'Save draft building',
    'clients.stagedDelete': 'Remove',
    'clients.stagedAfterSignupNote':
      'This borrower is linked. Drafts were converted to buildings when they signed up.',
    'clients.stagedAddressAutocompleteHint':
      'Start typing the street address — pick a suggestion to fill city, province and postal code (Canada).',
    'clients.stagedExpandAddButton': 'Add a draft building',
    'clients.stagedFormOpenLabel': 'New draft building',
    'clients.stagedDoneButton': 'Done',
    'clients.stagedLoanTranchesTitle': 'Loan tranches (optional)',
    'clients.stagedLoanTranchesHelp': 'Add at least one tranche with principal and start date to migrate loans when the borrower links their account.',
    'clients.stagedTranchePrincipal': 'Principal ($)',
    'clients.stagedTrancheRate': 'Rate % p.a.',
    'clients.stagedTrancheNote': 'Note',
    'clients.stagedTrancheAdd': 'Add tranche',
    'clients.stagedTrancheRemove': 'Remove tranche',
    'clients.stagedTrancheMaturityAfterStart':
      'When a maturity date is set, it must be after the start date (and the start date is required).',
    'clients.stagedTranchesSummary': '{count} tranche(s) · {total} principal',
    'clients.importBorrowersButton': 'Import from file',
    'clients.importBorrowersTitle': 'Import borrowers from a file',
    'clients.importBorrowersHint':
      'Upload a CSV or Excel file (.csv, .xlsx, .xls) from your lender files. The assistant reads the table and proposes rows to review before anything is saved.',
    'clients.importBorrowersTemplateRecommended':
      'Downloading the example Excel file (.xlsx) is recommended (optional). Your own export works if the table is readable.',
    'clients.importBorrowersDownloadTemplate': 'Download example Excel (.xlsx)',
    'clients.importBorrowersExpectedTitle': 'Your file will work best if it includes (as columns or in a table):',
    'clients.importBorrowersExpectedBorrower': 'Borrower — name of the person or company',
    'clients.importBorrowersExpectedEmail': 'Email — used to link buildings and tranches to one borrower',
    'clients.importBorrowersExpectedAddress':
      'Building address — civic address and/or project name (dossier) for each property',
    'clients.importBorrowersExpectedTranches':
      'Loan tranches for each building — start date and end (maturity) date for each tranche',
    'clients.importBorrowersExpectedAmount': 'Principal — amount lent on each tranche',
    'clients.importBorrowersExpectedRate': 'Interest rate — annual % for each tranche',
    'clients.importChooseFile': 'Choose file',
    'clients.importAnalyzing': 'Analyzing…',
    'clients.importPreviewHelp':
      'Email is the link key: several rows with the same email create one borrower and one draft building per row (when « Draft building » is on). Uncheck rows to skip. When only a project name exists, use « Project » as the draft line; civic address + project are both kept in notes.',
    'clients.importConfirm': 'Create selected borrowers',
    'clients.importName': 'Name',
    'clients.importEmail': 'Email',
    'clients.importProjectTitle': 'Project',
    'clients.importStreet': 'Street address',
    'clients.importTranchesColumn': 'Loan tranches',
    'clients.importTrancheAt': 'at',
    'clients.importTrancheDateLine': 'From {start} to {end}',
    'clients.importRowSubtotal': 'Subtotal',
    'clients.importPreviewTotalPrincipal': 'Total principal (all rows)',
    'clients.importPreviewTotalSelected': 'Total principal (checked rows only)',
    'clients.importIncludeBuilding': 'Draft building',
    'clients.importDuplicateEmail': 'This email is already a borrower.',
    'clients.importExistingBorrowerBuildingsOnly':
      'Already a borrower: this row only adds a draft building (no new account).',
    'clients.importSameEmailGroupedHint':
      'Same email as a row above: grouped under one borrower; this line adds a draft building if checked.',
    'clients.importNoEmail': 'Valid email required.',
    'clients.importSuccess': '{n} borrower(s) created.',
    'clients.importSuccessBuildings': '{n} draft building(s) added.',
    'clients.importSuccessBorrowersAndBuildings': '{b} borrower(s) created, {d} draft building(s) added.',
    'clients.importNothingCreated': 'Nothing was saved. Check emails, selection, and draft building rows.',
    'clients.importPartialSummary':
      '{br} new borrower(s), {bd} draft building(s); {bad} row(s) had issues (e.g. validation or save failed).',
    'clients.importNoSelection': 'No row selected.',
    'clients.importNoteProjectPrefix': 'Project: ',
    'checklists.title': 'My checklists',
    'checklists.add': 'New checklist',
    'checklists.name': 'Name',
    'checklists.description': 'Description',
    'checklists.items': 'Items',
    'checklists.itemLabel': 'Item',
    'checklists.requireFile': 'Require a file',
    'checklists.setDefault': 'Set as default',
    'checklists.isDefault': 'Default',
    'checklists.noChecklists': 'No checklists yet.',
    'checklists.deleteConfirm': 'Delete this checklist?',
    'financementDossier.request': 'Send Financing Checklist',
    'financementDossier.sendChecklist': 'Send Financing Checklist',
    'financementDossier.requestDescription': 'Ask this client to complete a financing dossier using one of your checklists.',
    'financementDossier.selectChecklist': 'Select checklist',
    'financementDossier.requested': 'Dossier requested',
    'financementDossier.sectionTitle': 'Financing dossiers',
    'financementDossier.open': 'Open',
    'financementDossier.status_not_started': 'Not started',
    'financementDossier.status_in_progress': 'In progress',
    'financementDossier.status_submitted': 'Submitted',
    'financementDossier.noDossiersYet': 'No dossiers yet. Open "Financing dossier" above when a broker has requested one.',
    'financementDossier.chooseFromProject': 'Choose from Data Room',
    'financementDossier.markSubmitted': 'Mark as submitted',
    'financementDossier.loadError': 'Unable to load the dossier.',
    'financementDossier.notFound': 'Dossier not found.',
    'financementDossier.propertyUnavailable': 'Property unavailable.',
    'financementDossier.updateChecklist': 'Update checklist',
    'financementDossier.cancelRequest': 'Cancel request',
    'financementDossier.activeRequestDescription': 'You already have an active financing request for this property. Update its checklist or cancel it.',
    'financementDossier.existingRequestsForProperty': 'Existing requests for this property',
    'financementDossier.manageExistingHint': 'You can open, update the checklist, or cancel each request below.',
    'financementDossier.cancelRequestConfirm': 'Are you sure you want to cancel this request? The client will no longer see it.',
    'financementDossier.selectChecklistWithItems': 'Select a checklist with at least one item.',
    'privateLenderOffer.makeOffer': 'Make a financing offer',
    'privateLenderOffer.comment': 'Comment (optional)',
    'privateLenderOffer.commentPlaceholder': 'Summarize your offer or add any relevant information for the investor.',
    'privateLenderOffer.principalAmount': 'Loan amount',
    'privateLenderOffer.interestRate': 'Interest rate (%)',
    'privateLenderOffer.termMonths': 'Term (months)',
    'privateLenderOffer.loanStartDate': 'Loan start date',
    'privateLenderOffer.loanMaturityDate': 'Loan maturity date (optional)',
    'privateLenderOffer.paymentFrequency': 'Interest payment frequency',
    'privateLenderOffer.paymentFrequency_monthly': 'Monthly',
    'privateLenderOffer.paymentFrequency_quarterly': 'Quarterly',
    'privateLenderOffer.interestPaymentDue': 'Interest payment due',
    'privateLenderOffer.interestPaymentDue_end': 'End of month',
    'privateLenderOffer.interestPaymentDue_beginning': 'Beginning of month',
    'privateLenderOffer.interestCalculationMode': 'Interest calculation',
    'privateLenderOffer.interestCalculationMode_daily': 'Daily (prorated, 365-day year)',
    'privateLenderOffer.interestCalculationMode_monthly': 'Monthly (fixed amount per full month)',
    'privateLenderOffer.interestCalculationMode_dailyHint':
      'Interest accrues day-by-day using your nominal annual rate ÷ 365 on the outstanding balance, including partial first and last months.',
    'privateLenderOffer.interestCalculationMode_monthlyHint':
      'Each full calendar month uses the same interest: principal × (annual rate ÷ 100) ÷ 12. The first and last months are prorated by day if the loan does not start on the 1st or end on the last day of the month. Mid-month capital additions use daily proration for that month.',
    'privateLenderOffer.interestPaymentDueStandardNote': 'Interest payments are due at the end of each month.',
    'privateLenderOffer.contractFile': 'Loan contract (PDF)',
    'privateLenderOffer.chooseContract': 'Choose contract file',
    'privateLenderOffer.submit': 'Submit offer',
    'privateLenderOffer.submitting': 'Submitting…',
    'privateLenderOffer.contractRequired': 'Please attach the loan contract (PDF).',
    'privateLenderOffer.offersForProperty': 'Your financing offers for this property',
    'financementDossier.checklistsSentToClient': 'Financing checklists sent to this client',
    'privateLenderOffer.sectionTitle': 'Financing offers',
    'privateLenderOffer.status_pending': 'Pending',
    'privateLenderOffer.status_accepted': 'Accepted',
    'privateLenderOffer.status_rejected': 'Rejected',
    'privateLenderOffer.status_cancelled': 'Cancelled',
    'privateLenderOffer.status_deal_cancelled': 'Cancelled',
    'privateLenderOffer.cancelContract': 'Cancel contract',
    'privateLenderOffer.cancelOffer': 'Cancel offer',
    'privateLenderOffer.cancelOfferConfirm': 'Cancel this financing offer? The investor will see it as cancelled.',
    'privateLenderOffer.accept': 'Accept',
    'privateLenderOffer.reject': 'Reject',
    'privateLenderOffer.uploadSignedContract': 'Upload signed contract',
    'privateLenderOffer.downloadSignedContract': 'Download signed contract',
    'privateLenderOffer.chooseSignedContract': 'Choose signed contract file',
    'privateLenderOffer.summaryDisclaimer': 'This summary is for information only. It is important to read and fully understand the contract. This summary does not constitute legal or financial advice.',
    'privateLenderOffer.summarizeContract': 'Summarize contract',
    'privateLenderOffer.summarizeWithAI': 'Summarize contract with AI',
    'privateLenderOffer.summaryTitle': 'Contract summary',
    'privateLenderOffer.summarizing': 'Summarizing…',
    'privateLenderOffer.summaryError': 'Could not generate summary.',
    'privateLenderOffer.downloadContract': 'Download contract',
    'privateLenderOffer.fillOrVerifyWithAI': 'Fill / Verify with AI',
    'privateLenderOffer.extracting': 'Extracting…',
    'privateLenderOffer.documentDoesNotCorroborate': 'The document does not corroborate this information.',
    'privateLenderOffer.conformitySuccess': 'Everything is conforming to the contract.',
    'privateLenderLoans.title': 'Loan management',
    'privateLenderLoans.stagingDraftSummaryTitle': 'Draft buildings (borrowers)',
    'privateLenderLoans.stagingDraftSummaryBody':
      '{buildings} draft building(s) with loan tranches — principal staged: {principal}. Manage them under Borrowers.',
    'privateLenderLoans.activeLoans': 'Active loans',
    'privateLenderLoans.totalCapital': 'Total capital',
    'privateLenderLoans.awaitingDisbursement': 'Awaiting disbursement',
    'privateLenderLoans.awaitingDisbursementBlock': 'Awaiting disbursement',
    'privateLenderLoans.markDisbursed': 'Mark funds disbursed',
    'privateLenderLoans.maturitySoon': 'Maturity within 90 days',
    'privateLenderLoans.days': 'days',
    'privateLenderLoans.colProperty': 'Property',
    'privateLenderLoans.colAmount': 'Amount',
    'privateLenderLoans.colRate': 'Rate',
    'privateLenderLoans.colStart': 'Start',
    'privateLenderLoans.colMaturity': 'Maturity',
    'privateLenderLoans.colDisbursed': 'Disbursed',
    'privateLenderLoans.colActions': 'Actions',
    'privateLenderLoans.noLoans': 'No active loans.',
    'privateLenderLoans.detail': 'Detail',
    'privateLenderLoans.detailTitle': 'Loan detail',
    'privateLenderLoans.yes': 'Yes',
    'privateLenderLoans.no': 'No',
    'privateLenderLoans.tranches': 'Tranches',
    'privateLenderLoans.addTranche': 'Add capital tranche',
    'privateLenderLoans.noTranches': 'No tranches.',
    'privateLenderLoans.trancheInitial': 'Initial',
    'privateLenderLoans.trancheAdditional': 'Additional',
    'privateLenderLoans.addTrancheTitle': 'Add capital tranche',
    'privateLenderLoans.trancheAmount': 'Amount',
    'privateLenderLoans.trancheStartDate': 'Start date',
    'privateLenderLoans.trancheRate': 'Interest rate (%)',
    'privateLenderLoans.trancheNote': 'Note',
    'privateLenderLoans.addTrancheSubmit': 'Add tranche',
    'privateLenderLoans.trancheAmountDateRequired': 'Amount and start date are required.',
    'privateLenderLoans.trancheType': 'Type',
    'privateLenderLoans.trancheEffectiveDate': 'Effective date',
    'privateLenderLoans.deleteTranche': 'Delete',
    'privateLenderLoans.deleteTrancheConfirm': 'Remove this tranche? It can only be deleted before its effective date.',
    'privateLenderLoans.cancelDeal': 'Cancel loan',
    'privateLenderLoans.cancelDealConfirm': 'Cancel this loan contract? It has not been disbursed. This action cannot be undone.',
    'privateLenderLoans.interestPaymentSection': 'Interest payment',
    'privateLenderLoans.interestExpected': 'Expected',
    'privateLenderLoans.expectedBy': 'Expected by',
    'privateLenderLoans.interestStatus_pending': 'Pending',
    'privateLenderLoans.interestStatus_paid': 'Received',
    'privateLenderLoans.interestStatus_partial': 'Partial',
    'privateLenderLoans.interestStatus_overpaid': 'Overpaid',
    'privateLenderLoans.markPaymentReceived': 'Mark as received',
    'privateLenderLoans.paidAmount': 'Amount received',
    'privateLenderLoans.paidAt': 'Date received',
    'privateLenderLoans.discrepancyNote': 'Note (if different from expected)',
    'privateLenderLoans.savePayment': 'Save',
    'privateLenderLoans.status': 'Status',
    'privateLenderLoans.scheduleExpected': 'Expected ($)',
    'privateLenderLoans.scheduleExpectedBy': 'Expected by',
    'privateLenderLoans.scheduleReceived': 'Received ($)',
    'privateLenderLoans.noSchedule': 'No schedule.',
    'privateLenderLoans.schedulePeriod': 'Period',
    'interestReport.title': 'Interest received report',
    'interestReport.back': 'Back to loans',
    'interestReport.year': 'Year',
    'interestReport.loading': 'Loading report…',
    'interestReport.empty': 'No data for the selected year.',
    'interestReport.errorPermission': 'You do not have permission to load this report. Please ensure you are logged in as the lender.',
    'interestReport.errorPermissionTranches': 'Permission denied while loading loan tranches. Check Firestore: financedDeals documents must have lenderUid and ownerUid; financedDealTranches must have dealId pointing to that deal.',
    'interestReport.errorPermissionPayments': 'Permission denied while loading interest payments. Check Firestore: interestPayments documents must have lenderUid equal to the lender’s user id.',
    'interestReport.errorLoad': 'Could not load the report. Please try again.',
    'interestReport.colProperty': 'Property / Borrower',
    'interestReport.colCapital': 'Capital',
    'interestReport.colMonth': 'Month',
    'interestReport.colExpected': 'Expected interest',
    'interestReport.colReceived': 'Interest received',
    'interestReport.colStatus': 'Status',
    'interestReport.colNote': 'Discrepancy note',
    'interestReport.status_pending': 'Pending',
    'interestReport.status_paid': 'Received',
    'interestReport.status_partial': 'Partial',
    'interestReport.status_overpaid': 'Overpaid',
    'interestReport.showReport': 'Interest received report',
    'lenderSettings.title': 'Payment settings',
    'lenderSettings.help': 'Optional. Used for Interac payment reference (REF-XXX-YYYYYY) and payment email.',
    'lenderSettings.fundCode': 'Fund code',
    'lenderSettings.fundCodeHelp': 'Short code (e.g. FND1, ABC). Used in the Interac reference for each loan.',
    'lenderSettings.paymentEmailPrefix': 'Payment email (prefix)',
    'lenderSettings.paymentEmailHelp': 'Your payment notifications address will be prefix@rentalyzer.ca',
    'lenderSettings.paymentEmailPrefixTaken': 'This prefix is already in use by someone else.',
    'lenderSettings.autoMarkPayments': 'Auto-mark payments from Interac emails (when available)',
    'lenderSettings.moreInfo': 'More information',
    'lenderSettings.autoMarkInfoModalTitle': 'How automatic marking of Interac payments works',
    'lenderSettings.autoMarkInfoModalIntro': 'When this option is enabled, Rentalyzer can mark interest payments as received automatically when a notification email for an Interac transfer is received at your payment address (prefix@rentalyzer.ca).',
    'lenderSettings.autoMarkInfoModalStep1': 'Share with each borrower the reference shown in the loan detail (REF-XXX-YYYYYY). They must enter this reference exactly in the message/note of their Interac transfer.',
    'lenderSettings.autoMarkInfoModalStep2': 'When the notification email arrives at your payment address, our system looks for the reference and the amount, finds the loan, and creates or updates the interest payment for the current period.',
    'lenderSettings.autoMarkInfoModalStep3': 'You can still mark payments manually at any time. Automatic marking is an optional convenience.',
    'lenderSettings.save': 'Save',
    'lenderSettings.saving': 'Saving…',
    'lenderSettings.saved': 'Settings saved.',
    'dealPayment.directDepositTitle': 'Reference for direct deposit of interest',
    'dealPayment.refInterac': 'Interac reference',
    'dealPayment.refInteracHelp': 'Enter this reference exactly in the message/note of your Interac transfer so the lender can match the payment.',
    'dealPayment.refInteracHelpLender': 'Share this reference with the borrower so they can enter it in the message of their Interac transfer.',
    'investorFunds.title': 'My placement',
    'investorFunds.empty': 'No placements yet.',
    'investorFunds.colPlacement': 'Start date',
    'investorFunds.colInvested': 'Invested',
    'investorFunds.colTotalRepaid': 'Total repaid',
    'investorFunds.colBalance': 'Balance',
    'investorFunds.colFundManager': 'Fund manager',
    'investorFunds.colAmountInvested': 'Amount invested',
    'investorFunds.colAverageYield': 'Average yield (12 mo.)',
    'investorFunds.yieldNotAvailable': '—',
    'investorFunds.managePlacement': 'Manage this placement',
    'investorFunds.capitalInvestorOnlyMessage': 'You are connected as a capital investor. Your placements are shown below.',
    'investorFunds.loading': 'Loading…',
    'investorFunds.errorLoad': 'Could not load placement details.',
    'investorFunds.totalPlacements': 'Total placements',
    'investorFunds.investingEntity': 'Investing as',
    'investorFunds.investingIndividual': 'Individual (personal)',
    'investorFunds.investingCompany': 'Company',
    'investorFunds.selectCompany': 'Select company',
    'investorFunds.saveDefaultHolder': 'Save as default for this placement',
    'investorFunds.defaultHolderSaved': 'Default holder saved.',
    'investorFunds.defaultHolderSavedReattest': 'Holder saved. Because the holder changed, please complete the qualified-investor attestation again.',
    'investorFunds.defaultHolderSavedLockedReminder':
      'This “Investing as” choice is now fixed for this placement on your side. Only your fund manager can change it if a correction is needed.',
    'investorFunds.holderSaveIrreversibleWarning':
      'After you save, you will not be able to change “Investing as” yourself. Make sure the choice matches how this investment is held before continuing.',
    'investorFunds.holderSaveConfirmCheckbox':
      'I understand that after saving, I cannot change this myself for this placement.',
    'investorFunds.holderLockedReadOnlyHint':
      'This placement’s holder is saved and locked on your side. To correct it, contact your fund manager.',
    'investorFunds.holderCommitAckRequired': 'Please confirm that you understand this choice cannot be changed after saving.',
    'investorFunds.attestRequiresHolderSave':
      'Save “Investing as” for this placement first (with confirmation), then you can complete the qualified-investor attestation.',
    'investorFunds.qualifiedViaActivePlacementBody':
      'This placement is already active in the fund (capital received). You do not need to complete the in-app qualified-investor self-attestation for this step. If your fund manager requests supporting documents, they will appear in the steps below.',
    'investorFunds.noCompaniesHint': 'Add companies under Profile → My companies.',
    'investorFunds.openProfileCompaniesLink': 'Open profile — My companies',
    'investorFunds.openProfileCompaniesHelp':
      'Your placement stays open behind this window. When you are done, close the profile and pick your company in the list above.',
    'investorFunds.allocatedInterestTitle': 'Allocated interest (last 24 months)',
    'investorFunds.allocatedInterestEmpty': 'No yield calculated yet. Ask your fund manager to click “Update yields (month)”.',
    'investorFunds.allocatedMonthCol': 'Month',
    'investorFunds.allocatedInterestCol': 'Interest',
    'investorFunds.allocatedShareCol': 'Share',
    'investorFunds.openPeriodReport': 'Monthly portfolio report',
    'investorFunds.reportNotPublished': 'Report not yet available',
    'capitalInvestors.recomputePeriodLabel': 'Period to process (calendar month)',
    'capitalInvestors.recomputeButton': 'Update monthly yields',
    'capitalInvestors.recomputeProgress': 'Updating…',
    'capitalInvestors.recomputePeriodHelp':
      'Typically the previous month after borrowers pay (e.g. in April, select March). You can re-run for the same month when payments arrive late.',
    'capitalInvestors.publishReportTitle': 'Investor-facing report',
    'capitalInvestors.publishReportHelp':
      'After updating yields, publish when you want capital investors to open the detailed monthly report for this period.',
    'capitalInvestors.managerCommentLabel': 'Note to investors (optional)',
    'capitalInvestors.publishButton': 'Publish to investors',
    'capitalInvestors.unpublishButton': 'Unpublish',
    'capitalInvestors.reportPublished': 'Published',
    'capitalInvestors.reportDraft': 'Not published',
    'capitalInvestors.summaryMissing': 'No data for this period yet — run update yields first.',
    'fundPeriodReport.modalTitle': 'Monthly income report',
    'fundPeriodReport.close': 'Close',
    'fundPeriodReport.period': 'Period',
    'fundPeriodReport.loading': 'Loading…',
    'fundPeriodReport.tabLoans': 'Loan portfolio',
    'fundPeriodReport.tabOverview': 'Fund overview',
    'fundPeriodReport.legendRenewal': 'Renewal (new term)',
    'fundPeriodReport.legendMatured': 'Maturity in period — no renewal detected',
    'fundPeriodReport.colBorrower': 'Borrower',
    'fundPeriodReport.colProperty': 'Property',
    'fundPeriodReport.colLoanDate': 'Loan date',
    'fundPeriodReport.colMaturity': 'Maturity',
    'fundPeriodReport.colPrincipal': 'Principal',
    'fundPeriodReport.colRate': 'Rate',
    'fundPeriodReport.colDividends': 'Dividends',
    'fundPeriodReport.colInterest': 'Interest received',
    'fundPeriodReport.colQuota': 'Manager quota',
    'fundPeriodReport.colPaidInvestors': 'Paid to investors',
    'fundPeriodReport.totals': 'Totals',
    'fundPeriodReport.emptyLoans': 'No disbursed loans overlapped this month.',
    'fundPeriodReport.quotaNote': 'Manager quota uses {pct}% of interest received (configurable under loan payment settings).',
    'fundPeriodReport.outstandingTitle': 'Total loans outstanding (end of period)',
    'fundPeriodReport.outstandingHint': 'Sum of principal for loans still active on the last day of the month.',
    'fundPeriodReport.fundMovementsTitle': 'Fund capital (commanditaires)',
    'fundPeriodReport.capitalPrev': 'Capital at end of previous period',
    'fundPeriodReport.capitalAdded': 'Additional capital during the period',
    'fundPeriodReport.capitalAddedTooltip':
      'Returns on capital added mid-period are allocated pro rata by day in the fund (same rule as yield calculation).',
    'fundPeriodReport.prorataHint': 'prorated by days in fund',
    'fundPeriodReport.capitalRepaid': 'Capital returned to partners',
    'fundPeriodReport.capitalEnd': 'Total capital at end of period',
    'fundPeriodReport.upcomingTitle': 'Upcoming (indicative)',
    'fundPeriodReport.upcomingAdditions': 'Additional capital not yet received (proposals)',
    'fundPeriodReport.upcomingWithdrawals': 'Pending withdrawal requests',
    'fundPeriodReport.upcomingFootnote':
      'Withdrawals are not yet tied to a target period in the app; amounts shown are all pending requests. A dedicated workflow can refine this later.',
    'fundPeriodReport.managerComment': 'Message from the fund manager',
    'lenderSettings.fundManagerQuotaPct': 'Manager interest quota (%)',
    'lenderSettings.fundManagerQuotaPctHelp':
      'Applied to interest received in the monthly investor report (e.g. 20%). Default 20%.',
    'lenderSettings.interestPeriodAnchorDay': 'Interest period starts on day of month',
    'lenderSettings.interestPeriodAnchorDayHelp':
      '1 = calendar month. Example: 15 = period from the 15th through the 14th of the following month (mid-month anchor). Max 28.',
    'lenderSettings.fundInConfiguration': 'Fund in setup mode (no automatic invitation emails)',
    'lenderSettings.fundInConfigurationHelp':
      'When enabled, adding a borrower or capital investor does not queue invitation emails. You can still resend manually.',
    'investorFunds.modalTabOverview': 'Overview',
    'investorFunds.modalTabProfile': 'Holder & banking',
    'investorFunds.holderBankingStepAttestation': 'Qualified investor attestation',
    'investorFunds.holderBankingEditHolder': 'Edit',
    'investorFunds.holderBankingContractUnlockHint':
      'Open the contract PDF or generate an AI summary to unlock the void cheque upload below.',
    'investorFunds.modalTabRequests': 'Requests',
    'investorFunds.requestsNeedAttestation':
      'Complete the qualified investor attestation in the “Holder & banking” tab before requesting withdrawals or additional capital.',
    'investorFunds.contractSectionTitle': 'Investment contract',
    'investorFunds.contractNoFile': 'No contract document is available for this placement.',
    'investorFunds.contractSummaryEmptyHint':
      'Generate a one-time AI summary of the contract PDF. It will be saved for your next visits.',
    'fundInvestors.investingHolder': 'Holder',
    'fundInvestors.investingPersonal': 'Personal',
    'companies.title': 'My companies',
    'companies.hint': 'Add the legal entities through which you invest. You can then choose personal or a company for each placement and transaction.',
    'companies.legalName': 'Legal name',
    'companies.address': 'Business address',
    'companies.addressAutocompleteHint': 'Start typing — suggestions appear right away.',
    'companies.addressFirstHint':
      'Pick an address from the Google suggestions below; business registration fields appear only after you select one.',
    'companies.regNumbersIntro': 'Choose the province or territory to see the right label for the provincial number. Both fields are optional.',
    'companies.province': 'Province or territory',
    'companies.provincePlaceholder': 'Select…',
    'companies.federalReg': 'Federal Business Number (BN — CRA)',
    'companies.provincialReg.default': 'Provincial or territorial registration number',
    'companies.provincialReg.AB': 'Alberta Corporate Access Number',
    'companies.provincialReg.BC': 'BC Business / Incorporation number',
    'companies.provincialReg.MB': 'Manitoba business registration number',
    'companies.provincialReg.NB': 'New Brunswick business number',
    'companies.provincialReg.NL': 'Newfoundland and Labrador registry number',
    'companies.provincialReg.NS': 'Nova Scotia Registry of Joint Stock Companies number',
    'companies.provincialReg.NT': 'Northwest Territories business registry',
    'companies.provincialReg.NU': 'Nunavut business registry number',
    'companies.provincialReg.ON': 'Ontario Business Identification Number (BIN)',
    'companies.provincialReg.PE': 'Prince Edward Island business registration number',
    'companies.provincialReg.QC': 'NEQ — Québec enterprise number',
    'companies.provincialReg.SK': 'Saskatchewan corporate registry number',
    'companies.provincialReg.YT': 'Yukon business registry number',
    'companies.listFederal': 'Federal',
    'companies.listProvincial': 'Provincial',
    'companies.region.AB': 'Alberta',
    'companies.region.BC': 'British Columbia',
    'companies.region.MB': 'Manitoba',
    'companies.region.NB': 'New Brunswick',
    'companies.region.NL': 'Newfoundland and Labrador',
    'companies.region.NS': 'Nova Scotia',
    'companies.region.NT': 'Northwest Territories',
    'companies.region.NU': 'Nunavut',
    'companies.region.ON': 'Ontario',
    'companies.region.PE': 'Prince Edward Island',
    'companies.region.QC': 'Quebec',
    'companies.region.SK': 'Saskatchewan',
    'companies.region.YT': 'Yukon',
    'companies.add': 'Add company',
    'companies.save': 'Save',
    'companies.edit': 'Edit',
    'companies.delete': 'Delete',
    'companies.deleteConfirm': 'Delete this company?',
    'companies.saveError': 'Could not save.',
    'companies.deleteError': 'Could not delete.',
    'fundInvestors.empty': 'No capital investors yet. Invite one to get started.',
    'fundInvestors.invite.button': 'Invite investor',
    'fundInvestors.invite.title': 'Invite capital investor',
    'fundInvestors.invite.email': 'Email',
    'fundInvestors.invite.displayName': 'Display name',
    'fundInvestors.invite.displayNamePlaceholder': 'Name of the investor',
    'fundInvestors.invite.investedAmount': 'Invested amount ($)',
    'fundInvestors.invite.interestStartDate': 'Interest start date',
    'fundInvestors.invite.contractPdf': 'Contract (PDF, optional)',
    'fundInvestors.invite.submit': 'Create invitation',
    'fundInvestors.invite.sending': 'Creating…',
    'fundInvestors.invite.successMessage': 'Invitation created. Email sending will be available soon.',
    'fundInvestors.invite.skippedEmailFundConfig':
      'Investor record created. No invitation email was sent (fund in setup mode).',
    'fundInvestors.fundInConfigurationBanner':
      'Fund in setup mode: invitation emails are not sent automatically when you add investors or borrowers. You can still resend manually where available.',
    'fundInvestors.invite.errorEmail': 'Please enter a valid email address.',
    'fundInvestors.invite.errorAmount': 'Please enter a valid amount.',
    'fundInvestors.invite.errorGeneric': 'An error occurred. Please try again.',
    'fundInvestors.legacyImport.button': 'Record active placement',
    'fundInvestors.legacyImport.title': 'Add historical capital investor',
    'fundInvestors.legacyImport.disclaimer':
      'Use this for investors already in your fund. Onboarding is marked complete; past interest periods are not reconstructed in Rentalyzer. Allocations apply from data you enter going forward.',
    'fundInvestors.legacyImport.tranchesTitle': 'Capital tranches',
    'fundInvestors.legacyImport.addTranche': 'Add tranche',
    'fundInvestors.legacyImport.trancheAmount': 'Amount ($)',
    'fundInvestors.legacyImport.trancheNote': 'Note',
    'fundInvestors.legacyImport.sendInvitation': 'Send invitation email (optional)',
    'fundInvestors.legacyImport.sendInvitationDisabledFundConfig': 'disabled in setup mode',
    'fundInvestors.legacyImport.submit': 'Create placement',
    'fundInvestors.legacyImport.successMessage': 'Historical placement created.',
    'fundInvestors.legacyImport.errorTranche': 'Each filled row needs a positive amount and a start date.',
    'fundInvestors.legacyImport.errorNeedTranche': 'Add at least one capital tranche with amount and date.',
    'fundInvestors.legacyImport.badge': 'Active placement',
    'fundInvestors.legacyImport.badgeHint':
      'Imported for tracking; prior interest history in Rentalyzer may be incomplete.',
    'fundInvestors.legacyImport.modalNote':
      'This placement was imported as already active. Fund movements below may not list every historical tranche; capital weights for allocations use tranche records.',
    'fundInvestors.legacyImport.listStatusNote':
      'Contract and fund deposit dates are not tracked in Rentalyzer for this placement (recorded outside the invitation flow).',
    'fundInvestors.fileImport.button': 'Import from file',
    'fundInvestors.fileImport.title': 'Import capital investors from a file',
    'fundInvestors.fileImport.hint':
      'Upload a CSV or Excel file (.csv, .xlsx, .xls). We extract investor names, emails (when present), each capital tranche amount, and start dates. Review the preview before creating placements.',
    'fundInvestors.fileImport.templateRecommended':
      'Downloading the example Excel file (.xlsx) is recommended (optional). Your own export works if the table is readable.',
    'fundInvestors.fileImport.downloadTemplate': 'Download example Excel (.xlsx)',
    'fundInvestors.fileImport.expectedTitle': 'What we look for',
    'fundInvestors.fileImport.expectedName': 'Investor name or label',
    'fundInvestors.fileImport.expectedEmail': 'Email (recommended — required to create the investor in the app)',
    'fundInvestors.fileImport.expectedIdentifier': 'Internal ID or account number if no email appears in the file (you can add the email in the preview)',
    'fundInvestors.fileImport.expectedTranches': 'Each capital contribution as its own line with amount',
    'fundInvestors.fileImport.expectedDates': 'Start / effective date per tranche (YYYY-MM-DD)',
    'fundInvestors.fileImport.expectedKind':
      'Whether a line is an initial subscription or a later top-up when the document says so (otherwise we still store tranches by date; the earliest becomes the initial tranche)',
    'fundInvestors.fileImport.analyzing': 'Analyzing…',
    'fundInvestors.fileImport.chooseFile': 'Choose file',
    'fundInvestors.fileImport.previewHelp':
      'Uncheck rows you do not want. Fix missing emails before committing. Rows that match an investor already in your list are skipped.',
    'fundInvestors.fileImport.previewTotalAll': 'Total capital (all rows)',
    'fundInvestors.fileImport.previewTotalSelected': 'Total capital (checked rows only)',
    'fundInvestors.fileImport.colDisplayName': 'Display name',
    'fundInvestors.fileImport.colTotal': 'Total',
    'fundInvestors.fileImport.colTranches': 'Tranches',
    'fundInvestors.fileImport.colStatus': 'Status',
    'fundInvestors.fileImport.emailPlaceholder': 'name@example.com',
    'fundInvestors.fileImport.duplicateEmail': 'Already in fund',
    'fundInvestors.fileImport.badEmail': 'Invalid email',
    'fundInvestors.fileImport.noTranches': 'No tranches',
    'fundInvestors.fileImport.trancheInitial': 'initial',
    'fundInvestors.fileImport.trancheAddition': 'addition',
    'fundInvestors.fileImport.trancheUnspecified': 'unspecified',
    'fundInvestors.fileImport.back': 'Back',
    'fundInvestors.fileImport.commit': 'Create selected placements',
    'fundInvestors.fileImport.noRowsAfterMerge': 'No investors with valid tranches were found.',
    'fundInvestors.fileImport.partialSummary':
      'Created {ok} placement(s). Skipped {skip} duplicate(s). {bad} row(s) failed.',
    'fundInvestors.fileImport.successCount': 'Created {n} placement(s).',
    'fundInvestors.fileImport.onlyDuplicates': 'No new placements — all selected emails already exist.',
    'fundInvestors.fileImport.nothingCreated': 'Could not create placements. Check emails and tranches.',
    'fundInvestors.fileImport.noSelection': 'Nothing selected to import.',
    'fundInvestors.statusInvited': 'Invited',
    'fundInvestors.statusInvitationAccepted': 'Invitation accepted',
    'fundInvestors.statusSigned': 'Contract signed',
    'fundInvestors.statusFundsReceived': 'Funds received',
    'fundInvestors.table.email': 'Investor',
    'fundInvestors.table.amount': 'Amount',
    'fundInvestors.table.startDate': 'Start date',
    'fundInvestors.table.status': 'Status',
    'fundInvestors.table.actions': 'Actions',
    'fundInvestors.actions.markSigned': 'Mark contract signed',
    'fundInvestors.actions.markSignedExplainer':
      'Confirms the contractual step in the app (e.g. paper contract or before the final PDF is uploaded). If you upload the fully executed PDF in Manage investor, status updates automatically.',
    'fundInvestors.actions.markSignedRedundantHint':
      'A fully executed contract PDF is already on file — status should move to “Contract signed” automatically after refresh.',
    'fundInvestors.actions.markSignedSyncStuck':
      'Finalize: your record still shows “Invitation accepted” while the fully executed PDF is on file — click to set status to Contract signed.',
    'fundInvestors.onboarding.stepAttestation': 'holder & qualified-investor attestation',
    'fundInvestors.onboarding.stepQualDocsInvestor': 'qualification documents (awaiting investor)',
    'fundInvestors.onboarding.stepQualDocsReview': 'qualification documents (your review)',
    'fundInvestors.onboarding.stepContractInvestorSigned': 'contract — investor’s signed PDF',
    'fundInvestors.onboarding.stepContractFullyExecuted': 'contract — upload fully executed PDF',
    'fundInvestors.onboarding.stepCheque': 'void cheque / banking specimen',
    'fundInvestors.onboarding.stepConfirmContractStatus':
      'Next: click “Mark contract signed” when the placement agreement step is complete (e.g. no contract file in the app, or funds flow you track outside these uploads).',
    'fundInvestors.actions.markFundsReceived': 'Mark funds received',
    'fundInvestors.signedAt': 'Signed on',
    'fundInvestors.fundsReceivedAt': 'Funds received on',
    'fundInvestors.resendInvitation': 'Resend invitation',
    'fundInvestors.resendSending': 'Sending…',
    'fundInvestors.resendSuccess': 'Sent!',
    'fundInvestors.bulkInviteAllSummary':
      '{n} investor(s) have not linked their Rentalyzer account yet (including imported placements) — you can queue invitation emails for all of them.',
    'fundInvestors.bulkInviteAllButton': 'Invite all pending (email)',
    'fundInvestors.bulkInviteAllConfirm':
      'Send invitation emails to all investors who have not linked their account yet? Each will receive a new signup link.',
    'fundInvestors.bulkInviteAllSending': 'Sending…',
    'fundInvestors.bulkInviteAllResultOk': '{n} invitation email(s) queued.',
    'fundInvestors.bulkInviteAllResultPartial':
      '{ok} invitation(s) queued, {failed} failed — check the console or try again for those.',
    'fundInvestors.manageRepayments': 'Manage repayments',
    'fundInvestors.manageCapitalInvestor': 'Manage capital investor',
    'fundInvestors.lenderModalTabOverview': 'Overview',
    'fundInvestors.lenderModalTabDocuments': 'Documents & banking',
    'fundInvestors.lenderModalTabRequests': 'Requests',
    'fundInvestors.lenderModalInvestorInfo': 'Investor details',
    'fundInvestors.lenderModalContract': 'Placement contract',
    'fundInvestors.statusWithdrawalRequest': 'Withdrawal request',
    'fundInvestors.statusCapitalAdditionRequest': 'Capital addition request',
    'fundInvestors.transaction.title': 'Transaction information',
    'fundInvestors.transaction.date': 'Date',
    'fundInvestors.transaction.amount': 'Amount',
    'fundInvestors.transaction.submit': 'Confirm',
    'fundInvestors.transaction.submitting': 'Saving…',
    'fundInvestors.transaction.noFutureDate': 'Date cannot be in the future.',
    'fundInvestors.transaction.errorDate': 'Please enter a date.',
    'fundInvestors.transaction.confirmReceipt': 'Confirm receipt of funds',
    'fundInvestors.transaction.receivedOn': 'Received on',
    'fundInvestors.fundMovements.title': 'Fund movements',
    'fundInvestors.fundMovements.empty': 'No movements yet.',
    'fundInvestors.fundMovements.date': 'Date',
    'fundInvestors.fundMovements.amount': 'Amount',
    'fundInvestors.fundMovements.type': 'Type',
    'fundInvestors.fundMovements.typeWithdrawal': 'Withdrawal',
    'fundInvestors.fundMovements.typeCapital': 'Capital addition',
    'fundInvestors.fundMovements.typeInitialSubscription': 'Initial subscription',
    'fundInvestors.chequeSpecimen.title': 'Void cheque specimen',
    'fundInvestors.chequeSpecimen.hint': 'Upload a void cheque or bank specimen (PDF or image) so your fund manager can deposit interest to your account.',
    'fundInvestors.chequeSpecimen.upload': 'Upload / replace',
    'fundInvestors.chequeSpecimen.uploading': 'Uploading…',
    'fundInvestors.chequeSpecimen.currentFile': 'Current file',
    'fundInvestors.chequeSpecimen.open': 'Open',
    'fundInvestors.chequeSpecimen.updatedAt': 'Last updated',
    'fundInvestors.chequeSpecimen.none': 'No specimen on file yet.',
    'fundInvestors.chequeSpecimen.uploadSuccess': 'Specimen saved. Your fund manager will be notified.',
    'fundInvestors.chequeSpecimen.uploadError': 'Upload failed. Please try again.',
    'fundInvestors.chequeSpecimen.pendingBadge': 'Cheque specimen updated',
    'fundInvestors.chequeSpecimen.lenderSection': 'Investor cheque specimen',
    'fundInvestors.chequeSpecimen.acknowledge': 'Mark as reviewed',
    'fundInvestors.chequeSpecimen.acknowledging': 'Saving…',
    'fundInvestors.contractSigned.investorWorkflowHint':
      'Download the contract template, sign it, then upload your signed PDF here. After the fund manager countersigns and uploads the fully executed copy, both parties can open the final version.',
    'fundInvestors.contractSigned.fullyExecutedAvailable': 'Fully executed contract on file',
    'fundInvestors.contractSigned.downloadFullyExecuted': 'Open fully executed contract (PDF)',
    'fundInvestors.contractSigned.templateLabel': 'Contract template (unsigned)',
    'fundInvestors.contractSigned.uploadSignedLabel': 'Upload signed PDF (your signature)',
    'fundInvestors.contractSigned.replaceSignedUpload': 'Replace signed PDF',
    'fundInvestors.contractSigned.openMySigned': 'Open my signed copy',
    'fundInvestors.contractSigned.uploading': 'Uploading…',
    'fundInvestors.contractSigned.waitForManager':
      'After you upload, the fund manager will review, sign, and upload the finalized contract. You will then be able to open the fully executed PDF.',
    'fundInvestors.contractSigned.pdfOnly': 'Please choose a PDF file.',
    'fundInvestors.contractSigned.uploadSuccess': 'Signed contract uploaded. Your fund manager has been notified.',
    'fundInvestors.contractSigned.uploadError': 'Upload failed. Please try again.',
    'fundInvestors.contractSigned.fileTooLarge': 'File is too large (max 20 MB).',
    'fundInvestors.contractSigned.lenderWorkflowHint':
      'Open the investor’s signed PDF, complete your signature, then upload the fully executed PDF so both parties can access it in the placement folder.',
    'fundInvestors.contractSigned.pendingInvestorSignedBadge': 'Investor signed contract',
    'fundInvestors.contractSigned.pendingInvestorSignedNotice':
      'The investor uploaded a signed contract copy — open it, countersign if applicable, then upload the fully executed PDF.',
    'fundInvestors.contractSigned.lenderBlockMarkSignedNeedInvestorSigned':
      'Wait for the investor to upload their signed contract (PDF) before marking the contract as signed.',
    'fundInvestors.contractSigned.investorSignedBlockTitle': 'Investor-signed copy',
    'fundInvestors.contractSigned.noInvestorSignedYet':
      'No investor-signed PDF on file yet. The investor must upload their signed copy before you can finalize.',
    'fundInvestors.contractSigned.openInvestorSigned': 'Open investor-signed PDF',
    'fundInvestors.contractSigned.investorSignedUploadedAt': 'Uploaded by investor on',
    'fundInvestors.contractSigned.fullyExecutedBlockTitle': 'Fully executed contract (both signatures)',
    'fundInvestors.contractSigned.fullyExecutedHint':
      'Upload the final PDF after all parties have signed. This replaces any previous fully executed file.',
    'fundInvestors.contractSigned.fullyExecutedUploadedAt': 'Uploaded on',
    'fundInvestors.contractSigned.openFullyExecuted': 'Open fully executed PDF',
    'fundInvestors.contractSigned.uploadFullyExecutedLabel': 'Upload fully executed PDF',
    'fundInvestors.contractSigned.replaceFullyExecutedUpload': 'Replace fully executed PDF',
    'fundInvestors.contractSigned.lenderUploadFullyExecutedSuccess':
      'Fully executed contract saved. The investor can now open it from their placement.',
    'fundInvestors.qualified.title': 'Qualified investor attestation required',
    'fundInvestors.qualified.intro':
      'Before using your placements, confirm how you qualify under Canadian private-placement rules (e.g. NI 45-106). This is a self-declaration in the app — it does not replace legal advice or your fund manager’s compliance process.',
    'fundInvestors.qualified.placementsLabel': 'Placements pending attestation',
    'fundInvestors.qualified.categoryLabel': 'I qualify as (self-reported category)',
    'fundInvestors.qualified.categoryAccredited': 'Accredited investor',
    'fundInvestors.qualified.categoryEligible': 'Eligible investor',
    'fundInvestors.qualified.categoryInstitution': 'Non-individual / institution (permissible under applicable exemptions)',
    'fundInvestors.qualified.ni45106Title': 'NI 45-106 (prospectus exemption) — official information links',
    'fundInvestors.qualified.ni45106Intro':
      'Review the official text below before submitting your attestation. This is general information and not legal advice.',
    'fundInvestors.qualified.ni45106Fr': 'Read NI 45-106 (French, official)',
    'fundInvestors.qualified.ni45106En': 'Read NI 45-106 (English, official)',
    'fundInvestors.qualified.categoryExplanationTitle': 'What is the difference between these categories?',
    'fundInvestors.qualified.categoryExplanationAccredited':
      'Accredited investor: you certify that you meet criteria typically based on income/net assets, which may allow participation under applicable prospectus exemptions.',
    'fundInvestors.qualified.categoryExplanationEligible':
      'Eligible investor: you certify that you meet the specific “eligible investor” criteria under Canadian private-placement rules (e.g., how the exemption applies).',
    'fundInvestors.qualified.categoryExplanationInstitution':
      'Non-individual / institution: you certify that the investor is a corporation or an institution that can qualify under applicable exemptions (not as an individual).',
    'fundInvestors.qualificationDocs.title': 'Qualification document checklist (NI 45-106)',
    'fundInvestors.qualificationDocs.intro':
      'If your private lender needs supporting documents to validate your qualified-investor category, they can request them here. Upload the documents below and submit when ready.',
    'fundInvestors.qualificationDocs.requestedCategory': 'Requested category',
    'fundInvestors.qualificationDocs.status_not_requested': 'No document request yet.',
    'fundInvestors.qualificationDocs.status_requested': 'Request sent. Please upload the documents below.',
    'fundInvestors.qualificationDocs.status_submitted': 'Documents submitted. Your lender will review them.',
    'fundInvestors.qualificationDocs.status_reviewed': 'Documents reviewed.',
    'fundInvestors.qualificationDocs.requestButton': 'Request supporting documents',
    'fundInvestors.qualificationDocs.waitForAttestation': 'Complete the qualified investor attestation first.',
    'fundInvestors.qualificationDocs.open': 'Open',
    'fundInvestors.qualificationDocs.notUploaded': 'Not provided yet.',
    'fundInvestors.qualificationDocs.uploadLabel': 'Upload a document',
    'fundInvestors.qualificationDocs.replaceLabel': 'Replace document',
    'fundInvestors.qualificationDocs.uploading': 'Uploading…',
    'fundInvestors.qualificationDocs.fileOnFile': 'File on file',
    'fundInvestors.qualificationDocs.filesOnRecord': 'Documents on file',
    'fundInvestors.qualificationDocs.uploadSuccess': 'Document uploaded.',
    'fundInvestors.qualificationDocs.uploadError': 'Upload failed. Please try again.',
    'fundInvestors.qualificationDocs.submitButton': 'Submit checklist',
    'fundInvestors.qualificationDocs.submitMissing': 'Please upload all required documents before submitting.',
    'fundInvestors.qualificationDocs.markReviewed': 'Mark as reviewed',
    'fundInvestors.qualificationDocs.requestError': 'Could not request the checklist. Please try again.',
    'fundInvestors.qualificationDocs.reviewError': 'Could not mark as reviewed. Please try again.',
    'fundInvestors.qualificationDocs.noCategoryError': 'No category found for this investor.',
    'fundInvestors.qualificationDocs.reviewedBadge': 'Reviewed',
    'fundInvestors.qualificationDocs.itemProofIncome': 'Proof of income (e.g., tax returns / statements)',
    'fundInvestors.qualificationDocs.itemProofNetWorth': 'Proof of net worth (e.g., net asset statement)',
    'fundInvestors.qualificationDocs.itemProofEligible': 'Proof of eligibility (e.g., calculations / supporting statements)',
    'fundInvestors.qualificationDocs.itemInstitutionDocs': 'Institution documentation',
    'fundInvestors.qualificationDocs.itemAuthorizationDocs': 'Authorization / resolution',
    'fundInvestors.qualified.checkboxQualification':
      'I confirm that I meet the criteria for the category selected above (as defined under applicable Canadian securities laws) in connection with this investment.',
    'fundInvestors.qualified.checkboxNotAdvice':
      'I understand that Rentalyzer does not provide legal, regulatory, or investment advice.',
    'fundInvestors.qualified.disclaimer':
      'If you are unsure whether you qualify, consult a qualified professional before investing. Your fund manager may request additional documentation.',
    'fundInvestors.qualified.submit': 'Submit attestation for all listed placements',
    'fundInvestors.qualified.submitSinglePlacement': 'Submit attestation for this placement',
    'fundInvestors.qualified.submitting': 'Submitting…',
    'fundInvestors.qualified.errorConfirm': 'Please check all boxes to continue.',
    'fundInvestors.qualified.errorGeneric': 'Could not save attestation. Please try again.',
    'fundInvestors.qualified.errorEmailMismatch':
      'Your account email must match the email used for this invitation. Sign in with the invited address or contact your fund manager.',
    'fundInvestors.qualified.completeFirst': 'Complete the qualified investor attestation in the “Holder & banking” tab first.',
    'fundInvestors.qualified.attestationInModalHint':
      'For each placement, open Manage → Holder & banking: choose who holds the investment (individual or company), save, then complete the qualified investor attestation before using withdrawal or capital requests.',
    'fundInvestors.qualified.holderLockedHint':
      'The investing holder cannot be changed after your attestation (tax reporting). To use another entity, your fund manager must close this placement and set up a new one.',
    'fundInvestors.qualified.holderChangeResetsAttestationHint':
      'You can change who holds this investment. If you save a different holder, you will need to complete the qualified-investor attestation again.',
    'fundInvestors.qualified.saveCompanyBeforeAttest':
      'Save your company as the investing holder for this placement before attesting. If your screen does not match what is saved, click Save first.',
    'fundInvestors.qualified.syncHolderBeforeAttest':
      'Your selections do not match the saved holder. Click “Save as default for this placement” before attesting.',
    'fundInvestors.qualified.attestedSummaryTitle': 'Qualified investor attestation',
    'fundInvestors.qualified.attestedOn': 'Self-attested on',
    'fundInvestors.qualified.errorHolderCompanyRequired':
      'This placement is set to invest as a company but no company is saved. Select a company and save before attesting.',
    'fundInvestors.qualified.lenderPendingBadge': 'Qualification pending',
    'fundInvestors.qualified.lenderFieldLabel': 'Qualified investor attestation',
    'fundInvestors.qualified.lenderPending': 'Not completed by investor yet.',
    'fundInvestors.qualified.lenderAttested': 'Self-attested on',
    'fundInvestors.qualified.lenderSatisfiedViaFundsReceived':
      'Satisfied — placement marked as funds received (no in-app self-attestation on file).',
    'fundInvestors.qualified.lenderCategoryAccredited': 'Accredited',
    'fundInvestors.qualified.lenderCategoryEligible': 'Eligible',
    'fundInvestors.qualified.lenderCategoryInstitution': 'Institution',
    'fundInvestors.qualified.lenderBlockMarkSignedNeedLink':
      'The investor must accept the invitation and link their account before you can mark the contract as signed.',
    'fundInvestors.qualified.lenderBlockMarkSignedNeedAttestation':
      'The investor must complete the qualified investor attestation before you can mark the contract as signed.',
    'fundInvestors.repayments.title': 'Capital repayments',
    'fundInvestors.repayments.invested': 'Invested',
    'fundInvestors.repayments.investedInitially': 'Init. Investment',
    'fundInvestors.repayments.capitalAdded': 'Cap. Added',
    'fundInvestors.repayments.totalRepaid': 'Cap Repaid',
    'fundInvestors.repayments.balance': 'Balance',
    'fundInvestors.repayments.listTitle': 'Repayment history',
    'fundInvestors.repayments.empty': 'No repayments yet.',
    'fundInvestors.repayments.date': 'Date',
    'fundInvestors.repayments.amount': 'Amount',
    'fundInvestors.repayments.type': 'Type',
    'fundInvestors.repayments.typePartial': 'Partial',
    'fundInvestors.repayments.typeFull': 'Full',
    'fundInvestors.repayments.note': 'Note',
    'fundInvestors.repayments.notePlaceholder': 'Optional note',
    'fundInvestors.repayments.addTitle': 'Add repayment',
    'fundInvestors.repayments.add': 'Add repayment',
    'fundInvestors.repayments.adding': 'Adding…',
    'fundInvestors.repayments.errorAmount': 'Please enter a valid amount.',
    'withdrawalRequests.title': 'Fund withdrawal requests',
    'withdrawalRequests.empty': 'No withdrawal requests.',
    'withdrawalRequests.requestButton': 'Request fund withdrawal',
    'withdrawalRequests.amountOptional': 'Amount (optional)',
    'withdrawalRequests.submit': 'Submit request',
    'withdrawalRequests.submitting': 'Submitting…',
    'withdrawalRequests.statusPending': 'Pending',
    'withdrawalRequests.statusCompleted': 'Completed',
    'withdrawalRequests.statusCancelled': 'Cancelled',
    'withdrawalRequests.markCompleted': 'Mark as completed',
    'withdrawalRequests.colInvestor': 'Investor',
    'withdrawalRequests.colDate': 'Date',
    'withdrawalRequests.colAmount': 'Amount',
    'withdrawalRequests.colStatus': 'Status',
    'withdrawalRequests.commentOptional': 'Comment (optional)',
    'withdrawalRequests.commentPlaceholder': 'Add a comment for the fund manager…',
    'withdrawalRequests.investorComment': 'Investor comment',
    'withdrawalRequests.repaidOn': 'Repaid on',
    'capitalAddition.title': 'Capital addition proposals',
    'capitalAddition.empty': 'No proposals.',
    'capitalAddition.proposeButton': 'Propose additional funds',
    'capitalAddition.proposedAmount': 'Amount ($)',
    'capitalAddition.availableFromDate': 'Available from date',
    'capitalAddition.submit': 'Submit proposal',
    'capitalAddition.submitting': 'Submitting…',
    'capitalAddition.statusPending': 'Pending',
    'capitalAddition.statusAccepted': 'Accepted',
    'capitalAddition.statusRejected': 'Rejected',
    'capitalAddition.accept': 'Accept',
    'capitalAddition.reject': 'Reject',
    'capitalAddition.colInvestor': 'Investor',
    'capitalAddition.colAmount': 'Amount',
    'capitalAddition.colAvailableFrom': 'Available from',
    'capitalAddition.colStatus': 'Status',
    'confirm': 'Confirm',
    'deleteProperty.title': 'Delete property',
    'deleteProperty.confirmMessage': 'Are you sure you want to delete this property? This action cannot be undone.',
    'deleteProperty.warningIntro': 'This property has:',
    'deleteProperty.warningBrokers': 'Shared with {count} broker(s)',
    'deleteProperty.warningDossiers': '{count} financing dossier(s)',
    'deleteProperty.warningProjectDocuments': 'Data Room',
    'deleteProperty.warningOutro': 'Deleting will revoke broker access. Dossiers and documents will no longer be accessible.',
    'deleteProperty.deleteButton': 'Delete property',
    'deleteProperty.deleteAnyway': 'Delete anyway',
    'duplicateProperty.title': 'Duplicate property',
    'duplicateProperty.description': 'Choose what to include in the copy:',
    'duplicateProperty.noSharingHint': 'Shares, broker access, financing dossiers and data room will not be copied.',
    'duplicateProperty.withoutScenarios': 'Property only',
    'duplicateProperty.withoutScenariosHint': 'Copy building data without financing scenarios.',
    'duplicateProperty.withScenarios': 'Property and scenarios',
    'duplicateProperty.withScenariosHint': 'Also copy all financing scenarios (marked as copies).',
    'home.duplicate': 'Duplicate',
    'clients.deleteConfirm': 'Delete this client?',
    'shareModal.title': 'Share this property',
    'shareModal.recipientEmail': 'Recipient email address',
    'shareModal.send': 'Send',
    'shareModal.emailRequired': 'Please enter an email address.',
    'shareModal.sentToAccount': 'The property has been added to the dashboard of {email}.',
    'shareModal.sentByEmail': 'A link has been sent by email to {email}.',
    'shareModal.access': 'Access',
    'shareModal.accessRead': 'Read only',
    'shareModal.accessWrite': 'Read and write',
    'shareModal.ownedReadOnlyNote':
      'Owned properties are shared read-only. Recipients see your latest saved data.',
    'shareModal.ownedHint':
      'Useful for financing or co-investor review. Deal assignment and sales mandate are not available for owned properties.',
    'shareModal.includeNdaHintOwned':
      'If checked, you must attach or select an NDA. Recipients will need it before data room access.',
    'shareModal.allowSubScenarios': 'Allow editing of sub-scenarios (refinancing, optimization)',
    'shareModal.createLink': 'Create link',
    'shareModal.linkCopied': 'Link copied to clipboard.',
    'shareModal.shareOnFacebook': 'Share on Facebook',
    'shareModal.facebookSuggestedMessage': 'Here is a property I am sharing with you via the Rentalyzer platform.',
    'shareModal.copyMessageForPost': 'Copy this message for your post',
    'shareModal.orShareByLink': 'Or share via link',
    'shareModal.recipientCount': '{count} person has this in their dashboard',
    'shareModal.recipientCount_plural': '{count} people have this in their dashboard',
    'shareModal.shareLinkLabel': 'Share link',
    'shareModal.tabEmail': 'By email',
    'shareModal.tabLink': 'By link',
    'shareModal.sharedOptionsIntro': 'The access and scenario settings above apply to both email and link sharing.',
    'shareModal.linkTabHint': 'Create a link you can copy, send in a message, or post on social media.',
    'shareModal.shareForCession': 'Share to assign this deal',
    'shareModal.shareForCessionHint':
      'Recipients can request more information and access to the data room for a possible assignment of the purchase promise.',
    'shareModal.brokerShareOptionsTitle': 'Other options',
    'shareModal.includeNda': 'Include an NDA',
    'shareModal.includeNdaHint':
      'If checked, you must attach or select an NDA file. Recipients will need it before the data room when sharing for assignment.',
    'shareModal.ndaRequired': 'Please attach or select an NDA file.',
    'shareModal.anonymizeAddress': 'Hide address (show as **** *************, City (QC), *** ***)',
    'shareModal.attachNda': 'Attach NDA (optional)',
    'shareModal.chooseNdaFile': 'Choose file',
    'shareModal.removeNda': 'Remove',
    'shareModal.tabVip': 'VIP list (pocket deals)',
    'shareModal.vipRequiresCession':
      'Enable « Share to assign this deal » or « Share under a sales mandate » above so VIP recipients can request information.',
    'shareModal.shareForMandatedSale': 'Share under a sales mandate (offers workflow)',
    'shareModal.shareForMandatedSaleHint':
      'Recipients can request information, use the data room when you allow it, then submit an indicative offer and formal PDF steps. Cannot be combined with « Share to assign this deal ».',
    'shareModal.mandatedSaleRequiresModule': 'Turn on « Purchase offer workflow » in your profile to use a sales mandate on shares.',
    'shareModal.mandateFromPropertyNote':
      'This building is flagged with a sales mandate on the analysis. New shares will use the mandated-sale workflow automatically (when you share as a broker).',
    'shareModal.vipNoRecipients': 'No contacts with « Pocket deals » enabled. Add or update clients in My clients.',
    'shareModal.vipQueued': 'Queued {n} pocket deal email(s). They will arrive shortly.',
    'shareModal.vipDefaultSubject': 'Pocket deal — shared with you (Rentalyzer)',
    'shareModal.vipSubject': 'Email subject',
    'shareModal.vipPreheader': 'Preheader (preview text)',
    'shareModal.vipIntro': 'Short intro (optional)',
    'shareModal.vipIncludeDescription': 'Include marketing description',
    'shareModal.vipIncludeImage': 'Include building photo',
    'shareModal.vipIncludeMetrics': 'Include key figures table',
    'shareModal.vipIncludeScenario': 'Include scenario summary',
    'shareModal.vipEmailLanguages': 'Email language(s)',
    'shareModal.vipEmailLangFr': 'French',
    'shareModal.vipEmailLangEn': 'English',
    'shareModal.vipEmailLanguagesHint':
      'If both are selected, one email includes French and English sections. If the marketing description is not yet in English, it will be translated once and saved for next time.',
    'shareModal.vipEmailLangRequired': 'Select at least one language for the email.',
    'shareModal.vipScenarioLabel': 'Scenario',
    'shareModal.vipSendButton': 'Send to VIP list',
    'shareModal.vipRecipientsCount': '{count} recipient(s) with pocket deals enabled',
    'shareModal.vipHelp':
      'Each contact receives a personal link. If you enable assignment (cession) or a sales mandate, the same options apply to VIP emails. Otherwise pocket stays contact-only.',
    'shareModal.vipMetricsTitle': 'Recent pocket campaigns',
    'shareModal.vipMetricsNote':
      'Opens use a tracking pixel; many inboxes block images, so open rates are often understated. Clicks are the main conversion signal.',
    'shareModal.vipMetricsEmpty': 'No campaigns yet. After you send, counts appear here.',
    'shareModal.vipMetricsCampaign': 'Campaign',
    'shareModal.vipMetricsUpdated': 'Updated',
    'shareModal.vipMetricsSent': 'Sent',
    'shareModal.vipMetricsOpens': 'Opens',
    'shareModal.vipMetricsClicks': 'Clicks',
    'shareModal.vipMetricsSignups': 'Sign-ups',
    'shareModal.vipMetricsInterests': 'Info requests',
    'clients.sendPocketDeals': 'Send pocket deals',
    'clients.skipAutoInvite': 'Do not send Rentalyzer invitation email now',
    'clients.addressOptionalHint': 'Optional — may be used for personalization later.',
    'clients.importVipButton': 'Import VIP contacts',
    'clients.importVipTitle': 'Import VIP contacts (AI)',
    'clients.importVipAiHint':
      'Upload a file and Rentalyzer will use AI to find names, emails, and optional addresses. Review the preview before adding contacts (pocket deals opt-in, CASL audit).',
    'clients.importVipFormatsTitle': 'Supported files',
    'clients.importVipFormatExcel': 'Excel (.xlsx, .xls)',
    'clients.importVipFormatSheets': 'Google Sheets — download or export as Excel or CSV first',
    'clients.importVipFormatWord': 'Word (.docx only; save as .docx if you have an older .doc)',
    'clients.importVipFormatCsv': 'CSV or plain text (.csv, .txt)',
    'clients.importVipAddressNote':
      'Email is enough to send pocket deals. Address is optional and can be used later for personalization or segmentation.',
    'clients.importVipDownloadTemplate': 'Download Excel template',
    'clients.importVipAnalyzing': 'Analyzing file…',
    'clients.importVipPreviewHelp': 'Uncheck rows you do not want to import. Fix emails if needed; only valid addresses can be added.',
    'clients.importVipColName': 'Name',
    'clients.importVipColEmail': 'Email',
    'clients.importVipColAddress': 'Address (optional)',
    'clients.importVipColNotes': 'Notes',
    'clients.importVipColStatus': 'Status',
    'clients.importVipAddressPlaceholder': 'Optional',
    'clients.importVipBadEmail': 'Invalid email',
    'clients.importVipBack': 'Back',
    'clients.importPickFile': 'Choose file',
    'clients.importVipEmpty': 'No contacts with a valid email were found. Try another file or column layout.',
    'clients.importVipExists': 'Already in your list',
    'clients.importVipCommit': 'Add selected contacts',
    'clients.vipOnRentalyzer': 'On Rentalyzer',
    'clients.vipPocketOnly': 'Pocket list only',
    'basicInfo.marketingTitle': 'Marketing description (listing)',
    'basicInfo.marketingHint':
      'Used in pocket deal emails and reports. Use the toolbar for formatting. « Generate with AI » sends the current text (and property facts) to the model as instructions; the result replaces the editor.',
    'basicInfo.marketingEditorPlaceholder':
      'Write bullet points, tone, or a rough draft here. Use the toolbar for bold, headings, lists, links. Then click Generate with AI to produce a full listing description (replaces this text).',
    'basicInfo.marketingAiGenerate': 'Generate with AI',
    'basicInfo.marketingAiTranslateEn': 'Translate to English (preview)',
    'basicInfo.marketingAiTranslateFr': 'Translate to French (preview)',
    'basicInfo.marketingAiApply': 'Replace with translation',
    'basicInfo.marketingAiDisclaimer': 'AI-generated content — review before sending.',
    'basicInfo.marketingAiLoading': 'Generating…',
    'basicInfo.marketingTranslationPreview': 'Translation preview',
    'basicInfo.marketingToolbarBold': 'Bold',
    'basicInfo.marketingToolbarItalic': 'Italic',
    'basicInfo.marketingToolbarStrike': 'Strikethrough',
    'basicInfo.marketingToolbarH2': 'Heading 2',
    'basicInfo.marketingToolbarH3': 'Heading 3',
    'basicInfo.marketingToolbarBulletList': 'Bullet list',
    'basicInfo.marketingToolbarOrderedList': 'Numbered list',
    'basicInfo.marketingToolbarQuote': 'Quote',
    'basicInfo.marketingToolbarHr': 'Horizontal line',
    'basicInfo.marketingToolbarUndo': 'Undo',
    'basicInfo.marketingToolbarRedo': 'Redo',
    'basicInfo.marketingToolbarLink': 'Link',
    'basicInfo.marketingToolbarUnsetLink': 'Remove link',
    'basicInfo.marketingLinkPrompt': 'Link URL (leave empty to remove)',
    'myFiles.title': 'My Files',
    'myFiles.hint': 'Store documents here (e.g. NDA template) to reuse when sharing a property.',
    'myFiles.upload': 'Upload',
    'myFiles.uploading': 'Uploading…',
    'myFiles.noFiles': 'No files yet.',
    'myFiles.fileTooBig': 'File must be under 10 MB',
    'myFiles.download': 'Download',
    'myFiles.delete': 'Delete',
    'myFiles.deleteConfirm': 'Delete this file?',
    'myFiles.chooseFromList': 'Choose from My Files',
    'listingOffer.indicativeTitle': 'Indicative offer (non-binding)',
    'listingOffer.price': 'Offered price',
    'listingOffer.conditions': 'Conditions',
    'listingOffer.delays': 'Timelines',
    'listingOffer.comments': 'Comments',
    'listingOffer.representedByBroker': 'I am represented by a broker',
    'listingOffer.brokerEmail': 'Broker email',
    'listingOffer.brokerEmailInvalid': 'Enter a valid broker email.',
    'listingOffer.submitIndicative': 'Submit indicative offer',
    'listingOffer.versionsTitle': 'Offer versions',
    'listingOffer.kindIndicative': 'Indicative',
    'listingOffer.kindFormal': 'Formal PDF',
    'listingOffer.acceptOffer': 'Accept this formal offer',
    'listingOffer.acceptConfirm': 'Accept this offer? Other interested parties will lose data room access for this listing.',
    'listingOffer.addFormalPdf': 'Formal PDF round',
    'listingOffer.uploadFormal': 'Create version and attach first PDF',
    'listingOffer.pdfRequired': 'Choose a PDF file.',
    'listingOffer.appendPdf': 'Append PDF to a version',
    'listingOffer.chooseVersion': 'Choose version',
    'listingOffer.uploadAttachment': 'Upload PDF',
    'listingOffer.submitError': 'Something went wrong. Please try again.',
    'listingOffer.cancelIndicativeForm': 'Cancel',
    'listingOffer.indicativeFormHiddenHint': 'The indicative offer form is hidden. You can show it again when you are ready.',
    'listingOffer.showIndicativeFormAgain': 'Show indicative offer form',
    'listingMandateStatus.active': 'Listing mandate: active',
    'listingMandateStatus.negotiating': 'Listing mandate: in negotiation',
    'listingMandateStatus.offer_accepted': 'Listing mandate: offer accepted',
    'listingMandateStatus.sold': 'Listing mandate: sold',
    'shareInterest.sectionTitle': 'Request more information',
    'shareInterest.listingAccessClosed':
      'Data room access for this listing is closed — another offer was accepted. Thank you for your interest.',
    'shareInterest.requestMoreInfo': 'Request more information from the assignor',
    'shareInterest.requestSentNoNda': 'Your request has been sent. The assignor may grant you access to the Data Room.',
    'shareInterest.ndaRequired': 'The assignor requires a signed NDA. Upload your signed NDA below.',
    'shareInterest.chooseSignedNda': 'Choose signed NDA file',
    'shareInterest.submitNda': 'Submit NDA',
    'shareInterest.ndaPendingApproval': 'Your NDA has been submitted and is pending approval by the assignor.',
    'shareInterest.waitingDocumentAccess': 'NDA approved. Waiting for the assignor to grant access to the Data Room.',
    'shareInterest.documentAccessRefused': 'Your request was refused. If you need access, the assignor must initiate it.',
    'shareInterest.confirmInterest': 'Confirm my interest',
    'shareInterest.confirmReadyForOffers': 'Submit an indicative offer',
    'shareInterest.pocketConfirmContactRequest': 'Confirm my request to the broker',
    'shareInterest.chooseCessionContract': 'Choose cession contract',
    'shareInterest.submitCessionContract': 'Submit cession contract',
    'shareInterest.cessionSubmittedWaiting': 'Cession contract submitted. Waiting for the assignor to finalize.',
    'shareInterest.ownedDiligenceReady':
      'You can review the data room. This shared owned property does not use deal assignment.',
    'shareInterest.sectionTitleBroker': 'Request more information',
    'shareInterest.requestMoreInfoBroker': 'Request more information from the broker',
    'shareInterest.requestSentNoNdaBroker':
      'Your request has been sent. The broker may grant you access to the Data Room.',
    'shareInterest.ndaRequiredBroker': 'The broker requires a signed NDA. Upload your signed NDA below.',
    'shareInterest.ndaPendingApprovalBroker': 'Your NDA has been submitted and is pending approval by the broker.',
    'shareInterest.waitingDocumentAccessBroker':
      'NDA approved. Waiting for the broker to grant access to the Data Room.',
    'shareInterest.documentAccessRefusedBroker':
      'Your request was refused. If you need access, the broker must initiate granting access.',
    'shareInterest.phoneLabel': 'Phone number',
    'shareInterest.phonePlaceholder': 'e.g. (514) 555-1234',
    'shareInterest.phoneRequired': 'Enter a valid phone number (at least 10 digits).',
    'shareInterest.pocketDealNoCessionUpload':
      'This pocket listing does not use the formal assignment contract step here. Contact the broker for next steps.',
    'shareInterest.pocketRequestSentBroker':
      'Your request has been sent. The broker may contact you using the information you provided.',
    'shareInterest.pocketContactConsentProfilePhone':
      'By continuing, you agree that we may share your name, email and the phone number on your Rentalyzer profile with the broker so they can contact you.',
    'shareInterest.pocketContactConsentWithPhone':
      'By continuing, you agree that we may share your name, email and the phone number below with the broker so they can contact you. It will be saved to your profile.',
    'shareInterest.pocketNdaSubmittedBroker': 'The broker is processing your request.',
    'shareInterest.pocketNdaPendingBroker': 'Waiting for the broker.',
    'shareInterest.pocketWaitingFollowUpBroker': 'Follow-up in progress with the broker.',
    'shareInterest.pocketAccessRefusedBroker': 'The broker declined this request.',
    'shareInterest.pocketConfirmInterest': 'Confirm my request to the broker',
    'share.brokerPocketInterestsTitle': 'Contact requests (shared deals)',
    'share.brokerPocketInterestPhone': 'Phone',
    'share.brokerPocketInterestShareHint':
      'Investors who asked to be contacted about a deal you shared for this building.',
    'pocketInterest.status_requested': 'Contact requested',
    'pocketInterest.status_nda_submitted': 'In progress',
    'pocketInterest.status_nda_approved': 'In progress',
    'pocketInterest.status_document_access_granted': 'Follow-up in progress',
    'pocketInterest.status_document_access_refused': 'Declined',
    'pocketInterest.status_interest_confirmed': 'Interest confirmed',
    'pocketInterest.status_cession_submitted': 'Step completed',
    'cession.cedentSectionTitle': 'Requests and document access',
    'cession.interestsForShare': 'Requests for this share',
    'cession.approveNda': 'Approve NDA',
    'cession.grantDocumentAccess': 'Grant document access',
    'cession.grantAccessNoNdaWarning': 'This share has no NDA. Granting access allows the recipient to view the Data Room without a confidentiality agreement. Only grant access if you accept this risk.',
    'cession.grantAccessNoNdaConfirm': 'Grant document access without an NDA? The recipient will be able to view the entire Data Room.',
    'cession.revokeDocumentAccess': 'Revoke document access',
    'cession.refuseDocumentAccess': 'Refuse document access request',
    'cession.status_requested': 'Requested',
    'cession.status_nda_submitted': 'NDA submitted',
    'cession.status_nda_approved': 'NDA approved',
    'cession.status_document_access_granted': 'Document access granted',
    'cession.status_document_access_refused': 'Document access refused',
    'cession.status_interest_confirmed': 'Interest confirmed',
    'cession.status_cession_submitted': 'Cession contract submitted',
    'cession.status_listing_access_revoked': 'Listing access closed (another offer accepted)',
    'cession.peopleWithDocumentAccess': 'People with document access',
    'cession.noInterests': 'No requests yet.',
    'cession.finalizeCession': 'Finalize cession and assign to this investor',
    'cession.finalizeConfirm': 'Assign this building to this investor as the assignee? They will see it in My Deals and can share with their broker.',
    'home.sharedWithYou': 'Shared with you',
    'home.cessionRevoked': 'Cession completed – access revoked',
    'home.tabSharedWithMe': 'Shared with me',
    'home.sectionFinancedProjects': 'Financed Projects',
    'home.sectionFinancedProperties': 'Financed Properties',
    'home.sectionLoansManagement': 'Loan management',
    'home.privateLenderEmptyHubHelp':
      'You have no personal deals yet. Use Loan management to add borrowers and draft buildings, and Capital investors for fund onboarding. Invitations can wait until you are ready.',
    'home.sectionCapitalInvestors': 'Capital investors',
    'home.noFinancedProperties': 'No financed properties yet.',
    'home.noSharedWithMe': 'No properties shared with you yet.',
    'home.removeFromDashboard': 'Remove from my dashboard',
    'home.new': 'New',
    'home.sharedWithCount': 'Shared with {count} person',
    'home.sharedWithCount_plural': 'Shared with {count} people',
    'home.sharedWithBroker': 'Shared with 1 broker',
    'home.sharedWithBrokers': 'Shared with {count} brokers',
    'home.sharedWithCountAndBroker': 'Shared with {count} person(s) & 1 broker',
    'home.sharedShort': 'Shared',
    'home.shareAddedBanner': 'Building added to your shared properties.',
    'home.pocketDealLinkEmailMismatchBanner':
      'Your Rentalyzer account email does not match the address this pocket deal was sent to. You can still view the deal, but the broker link will not activate until you sign in with that same address—or ask your broker to send the deal to your current email.',
    'shareMethodChoice.title': 'How do you want to share?',
    'shareMethodChoice.description': 'Share this property with your broker, or send a link by email or on social media.',
    'shareMethodChoice.withBroker': 'Share with my broker',
    'shareMethodChoice.withBrokerHint': 'Your broker will see it in their client list.',
    'shareMethodChoice.byEmail': 'Share by email and social media',
    'shareMethodChoice.byEmailHint': 'Email a link or paste it anywhere (social media, messaging, etc.).',
    'shareWithBroker.title': 'Share with your broker',
    'shareWithBroker.description': 'Your broker will be able to view this property and add financing scenarios.',
    'shareWithBroker.includeScenarios': 'Also share my financing scenarios',
    'shareWithBroker.confirm': 'Share with broker',
    'shareWithBroker.invitationLinked': 'You are now linked to your broker. You can share properties from the home screen.',
    'shareWithBroker.pickBroker': 'Share with which broker?',
    'shareWithBroker.pickBrokerHint': 'Choose the broker you want to share this property with.',
    'shareWithBroker.brokerLabel': 'Broker {index}',
    'shareWithBroker.alreadyShared': 'Already shared',
    'shareWithBroker.alreadySharedWithThisBroker': 'This property is already shared with this broker.',
    'sharedView.badge': 'Shared file (read-only)',
    'sharedView.badgeWrite': 'Shared file (you can edit)',
    'sharedView.loginToEdit': 'Shared file. Log in to edit.',
    'sharedView.writtenBy': 'Shared by the broker. You cannot edit.',
    'sharedView.addToDashboard': 'Add this building to your Rentalyzer dashboard:',
    'sharedView.removeProperty': 'Remove property',
    'marketParams.title': 'Market parameters',
    'marketParams.newVersion': 'New version',
    'marketParams.versionLabel': 'Version label',
    'marketParams.updatedAt': 'Updated',
    'marketParams.useInPdf': 'Use in PDF',
    'marketParams.defaultRates': 'Default rates (reference only)',
    'home.cmb.title': 'CMB rate',
    'home.cmb.error': 'Unable to load rates',
    'home.cmb.empty': 'CMB rate not available yet',
    'property.brokerSaleMandate': 'I have a sales mandate for this building as a real estate broker',
    'property.brokerSaleMandateHint':
      'Shown only when purchase-offer workflow is enabled. New shares for this property will follow the mandated-sale path without asking again in the share dialog.',
    'property.form.title': 'New Property Analysis',
    'property.form.titleOwned': 'My Property',
    'property.form.simpleMode': 'Simple mode',
    'property.form.advancedMode': 'Advanced mode',
    'property.alert.fillAddress': 'Please fill in the complete address (address, city, province, postal code).',
    'property.alert.ownedValueMissing': 'Estimated market value is empty. Continue anyway?',
    'dealPurpose.modal.title': 'What kind of deal?',
    'dealPurpose.modal.subtitle': 'Choose a purchase analysis, a property you already own, or import from a listing.',
    'dealPurpose.acquisition.title': 'Property to analyze',
    'dealPurpose.acquisition.description': 'Underwrite a building you are considering buying — purchase price, acquisition costs, and financing scenarios.',
    'dealPurpose.owned.title': 'Property I own',
    'dealPurpose.owned.description': 'Enter the details of a building you already hold — market value, income, expenses, and existing mortgage (or none).',
    'dealPurpose.importListing.title': 'Import from a listing',
    'dealPurpose.importListing.description':
      'Paste a broker URL, listing text, or an offering-memorandum PDF. We prefill the form; you review before saving.',
    'listingImport.title': 'Import from a listing',
    'listingImport.subtitle':
      'Use a public listing URL when the site allows it. If the page is blocked, paste the text or attach the PDF — then review every figure.',
    'listingImport.urlLabel': 'Listing URL (optional)',
    'listingImport.urlPlaceholder': 'https://…',
    'listingImport.urlHint':
      'Many broker sites (including PMML) block automated reads. Keep the URL anyway, and paste the numbers below.',
    'listingImport.textLabel': 'Listing text (recommended)',
    'listingImport.textPlaceholder':
      'Paste price, unit count, income, taxes, and expenses from the listing or offering memorandum.',
    'listingImport.fileLabel': 'PDF or document (optional)',
    'listingImport.fileHint': 'PDF, Word (.docx), or text — max 8 MB',
    'listingImport.extract': 'Extract fields',
    'listingImport.extracting': 'Extracting…',
    'listingImport.extractAgain': 'Extract again',
    'listingImport.apply': 'Open in the form',
    'listingImport.completeTitle': 'Analysis complete',
    'listingImport.completeHint':
      'The extracted figures are shown below. Review them, then open the form.',
    'listingImport.showSource': 'Edit source',
    'listingImport.hideSource': 'Hide source',
    'listingImport.sourceTextUsed': 'Pasted text',
    'listingImport.previewTitle': 'Extracted fields',
    'listingImport.previewHint': 'Nothing is saved yet. Check these values, then open the form to finish financing and details.',
    'listingImport.previewEmpty': 'No summary rows — open the form to inspect the full extract.',
    'listingImport.fetchOk': 'The listing page was read successfully.',
    'listingImport.fetchBlocked':
      'The website blocked the automatic read (anti-bot). Extraction used the text or file you provided.',
    'listingImport.fetchFailed': 'The listing URL could not be loaded. Extraction used the text or file you provided.',
    'listingImport.needsMoreContent':
      'Could not read enough listing content. Paste the description and numbers from the page, or attach the PDF.',
    'listingImport.errorNeedSource': 'Add a URL, pasted text, or a file.',
    'listingImport.errorFileTooLarge': 'File is too large (max 8 MB).',
    'listingImport.errorNoFields': 'No usable fields were found. Add more listing text and try again.',
    'listingImport.errorQuota': 'Daily listing-import limit reached. Try again tomorrow.',
    'listingImport.errorAuth': 'Sign in to import a listing.',
    'listingImport.errorNetwork':
      'Could not reach the import service from this site (often a CORS or network block). Try again after the Cloud Function is redeployed, or paste the listing text if the page itself is blocked.',
    'listingImport.formBannerTitle': 'Prefill from a listing',
    'listingImport.formBannerBody': 'Paste a URL, listing text, or PDF to fill price, units, income, and expenses. Review before saving.',
    'listingImport.formBannerButton': 'Import listing',
    'listingImport.formWarningsTitle': 'Imported from a listing — review before saving',
    'dealPurpose.badge.owned': 'Owned',
    'ownedFinancing.title': 'Current financing',
    'ownedFinancing.type': 'Mortgage status',
    'ownedFinancing.type.existing': 'In-place financing',
    'ownedFinancing.type.none': 'No mortgage',
    'ownedFinancing.loanBalance': 'Outstanding loan balance',
    'ownedFinancing.monthlyPayment': 'Monthly mortgage payment',
    'ownedFinancing.noneHint': 'This property is modeled with no debt. Cash flow equals net operating income.',
    'ownedFinancing.currentSituationTitle': 'Current situation',
    'property.marketingMediaLockedTitle': 'Presentation & photos',
    'property.marketingMediaLockedBody':
      'Save the building once with the button below. You will then be able to add several photos and the marketing description.',
    'back': 'Back',
    'cancel': 'Cancel',
    'close': 'Close',
    'save': 'Save',
    'edit': 'Edit',
    'building.dashboard.title': 'Building Dashboard',
    'building.purchasePrice': 'Purchase Price',
    'building.estimatedMarketValue': 'Estimated market value',
    'building.netEquity': 'Net equity',
    'building.ltv': 'LTV',
    'building.numberOfUnits': 'Number of units',
    'building.pricePerDoor': 'Price per door',
    'building.grossIncome': 'Gross income',
    'building.totalExpenses': 'Total expenses',
    'building.netIncome': 'Net income (NOI)',
    'building.grm': 'GRM',
    'building.nim': 'NIM',
    'building.capRate': 'Cap Rate',
    'building.createScenario': 'Create Financing Scenario',
    'building.createScenarioOwned': 'Create refinancing / optimization scenario',
    'building.scenarios': 'Scenarios',
    'building.clientSubScenarios': 'Client sub-scenarios',
    'building.subScenariosFromShares': 'Sub-scenarios from shared links',
    'building.createdBy': 'Created by',
    'building.createdViaShare': 'Created via share by',
    'building.propertyOfClient': 'Property of',
    'building.projectDocuments': 'Data Room',
    'building.rentRegister': 'Rent register',
    'rentRegister.title': 'Rent register',
    'rentRegister.subtitle': 'One row per unit — enter monthly rent and ancillary services for each dwelling.',
    'rentRegister.subtitleReadOnly': 'Per-unit rent register (read-only).',
    'rentRegister.unit': 'Unit',
    'rentRegister.unitNumber': 'Unit {n}',
    'rentRegister.rentPrice': 'Monthly rent',
    'rentRegister.inclusions': 'Inclusions',
    'rentRegister.inclusionsPlaceholder': 'Heat, hot water, parking…',
    'rentRegister.ancillaryRevenue': 'Ancillary service revenue',
    'rentRegister.ancillaryDetails': 'Ancillary service details',
    'rentRegister.ancillaryDetailsPlaceholder': 'Storage, parking, internet…',
    'rentRegister.total': 'Total',
    'rentRegister.grandTotal': 'Grand total',
    'rentRegister.noUnitsHint': 'Set the number of units in the property details before filling in the rent register.',
    'rentRegister.noUnitsError': 'The number of units must be greater than zero.',
    'rentRegister.saveError': 'Unable to save the rent register.',
    'rentRegister.saving': 'Saving…',
    'building.dealRecipientsSection': 'Deal shared with the following recipients',
    'building.dealRecipientsHint':
      'Revoke link access removes the deal from the recipient’s “Shared with me”. Remove professional access to stop a broker or private lender from seeing this building in their client files. You can reinstate link access anytime, and professional access from the same list after you revoke it. You cannot remove a mortgage or real estate broker’s dashboard access while you are their client in an active file (private lenders are excepted).',
    'building.dealRecipientsEmpty': 'No recipients yet.',
    'building.recipientBadgeLinkShare': 'Share link',
    'building.recipientBadgeLinkShareCourtierHypo': 'Share link — mortgage broker',
    'building.recipientBadgeLinkShareCourtierImmo': 'Share link — real estate broker',
    'building.recipientBadgeLinkSharePrivateLender': 'Share link — private lender',
    'building.recipientBadgePrivateLender': 'Private lender',
    'building.recipientBadgeMortgageBroker': 'Mortgage broker',
    'building.recipientBadgeRealEstateBroker': 'Real estate broker',
    'building.recipientBadgeProfessional': 'Professional (dashboard)',
    'building.revokeBrokerAccess': 'Remove professional access',
    'building.restoreBrokerAccess': 'Restore professional access',
    'building.revokeBrokerBlockedClientRelation':
      'You cannot remove this professional’s dashboard access while you are their client in an active brokerage file. Your broker can adjust sharing from their account if needed.',
    'building.revokeBrokerAccessConfirm':
      'Remove this professional’s access to this building on your dashboard? They will no longer see this property under their client files.',
    'building.recipientBadgeProfessionalRevoked': 'Professional access (revoked)',
    'building.revokeRecipientAccess': 'Revoke access to this deal',
    'building.restoreRecipientAccess': 'Restore access to this deal',
    'building.openRecipientChat': 'Open private message',
    'building.recipientChatLinkOnly': 'Private chat is for people who have access via a share link.',
    'building.recipientChatDisabled': 'Private chat is unavailable while access is revoked.',
    'building.recipientActionError': 'Could not update access. Please try again.',
    'building.recipientsBroadcastPanelTitle': 'Recipients & broadcast',
    'building.ownerBroadcastTitle': 'Message all recipients',
    'building.ownerBroadcastHint':
      'Sends the same text to each active recipient’s private conversation with you (chat bubble, bottom right). Not a group thread. Anyone whose access you revoked is not contacted.',
    'building.ownerBroadcastPlaceholder': 'Write a message to everyone who has access via your share links…',
    'building.ownerBroadcastSend': 'Send to all',
    'building.ownerBroadcastSending': 'Sending…',
    'building.ownerBroadcastNoRecipients': 'No active recipients to message.',
    'building.ownerBroadcastSuccess': 'Message sent to {count} recipient(s). They will see it in private messages.',
    'building.ownerBroadcastPartial': 'Sent to {sent}; could not reach {fail} recipient(s).',
    'building.ownerBroadcastError': 'Could not send. Please try again.',
    'building.interestPaymentReceived': 'The lender has indicated they received your interest payment for {month}.',
    'building.nextInterestPayment': 'Next interest payment',
    'building.nextInterestPaymentDueBy': 'due by',
    'building.manageLoan': 'Manage loan',
    'building.loanManagementTitle': 'Loan management',
    'building.interestPaymentsTracking': 'Interest payments',
    'building.period': 'Period',
    'building.dueDate': 'Due date',
    'building.lender': 'Lender',
    'building.paidOn': 'Paid on {date}',
    'building.tranches': 'Tranches',
    'building.trancheInitial': 'Initial tranche',
    'building.trancheAdditional': 'Additional tranche',
    'projectDocuments.title': 'Data Room',
    'projectDocuments.addSection': 'Add',
    'projectDocuments.addSectionModalTitle': 'Project description',
    'projectDocuments.sectionTitlePlaceholder': 'Section title',
    'projectDocuments.sectionHint': 'E.g. Promise to purchase, vendor\'s declaration, current leases, inspections, appraisal, etc.',
    'projectDocuments.createSection': 'Create',
    'projectDocuments.deleteSection': 'Delete section',
    'projectDocuments.deleteSectionConfirm': 'Delete this section and all its files?',
    'projectDocuments.upload': 'Upload',
    'projectDocuments.download': 'Download',
    'projectDocuments.delete': 'Delete',
    'projectDocuments.noFiles': 'No files yet',
    'projectDocuments.uploading': 'Uploading…',
    'projectDocuments.fileTooBig': 'File must be under 10 MB',
    'projectDocuments.readOnlyDisclaimer': 'Consultation only. Do not print or redistribute these documents.',
    'view': 'View',
    'propertyReport.title': 'Profitability Analysis Report',
    'propertyReport.titleOwned': 'Owned Property Performance Report',
    'propertyReport.analyzingProperty': 'Property to analyze',
    'propertyReport.askingPrice': 'Asking price',
    'propertyReport.purchasePrice': 'Purchase price',
    'propertyReport.estimatedMarketValue': 'Estimated market value',
    'propertyReport.netEquity': 'Net equity',
    'propertyReport.numberOfUnits': 'Number of units',
    'propertyReport.pricePerDoor': 'Price per door',
    'propertyReport.valuePerDoor': 'Value per door',
    'propertyReport.totalRevenue': 'Total revenue',
    'propertyReport.totalExpenses': 'Total expenses',
    'propertyReport.debtServiceYear1': 'Year 1 debt service',
    'propertyReport.cashFlow': 'Cash Flow',
    'propertyReport.financing': 'Financing',
    'propertyReport.financingType.conventional': 'Conventional',
    'propertyReport.financingType.cmhc': 'CMHC Standard',
    'propertyReport.financingType.cmhc_aph': 'CMHC APH Select',
    'propertyReport.financingType.private': 'Private loan',
    'propertyReport.financingType.assumption': 'Mortgage assumption',
    'propertyReport.financingType.existing': 'In-place financing',
    'propertyReport.financingType.none': 'No mortgage',
    'propertyReport.maxLoan': 'Maximum loan',
    'propertyReport.outstandingLoan': 'Outstanding loan',
    'propertyReport.monthlyPayment': 'Monthly payment',
    'propertyReport.remainingAmortMonths': 'Remaining amortization (months)',
    'propertyReport.termEnd': 'Term end',
    'propertyReport.loanGranted': 'Loan granted',
    'propertyReport.ltv': 'Loan-to-value ratio',
    'propertyReport.downPayment': 'Down payment',
    'propertyReport.totalInvestment': 'Total investment',
    'propertyReport.averageRentPerDoor': 'Average rent per door',
    'propertyReport.futureReturns.title': 'Future returns',
    'propertyReport.futureReturns.intro':
      'Indicate how your revenue, expenses, and property value might evolve. Rentalyzer automatically calculates your returns over the selected period.',
    'propertyReport.futureReturns.horizonLabel': 'Projection horizon (years)',
    'propertyReport.futureReturns.presetsLabel': 'Quick assumptions',
    'propertyReport.futureReturns.presetsHint': 'You can fine-tune each assumption below.',
    'propertyReport.futureReturns.preset.conservative': 'Conservative',
    'propertyReport.futureReturns.preset.realistic': 'Realistic',
    'propertyReport.futureReturns.preset.optimistic': 'Optimistic',
    'propertyReport.futureReturns.resultsTitle': 'Results over {years} years',
    'propertyReport.futureReturns.projectedAtYear': 'In {years} years',
    'propertyReport.futureReturns.heroNetCaption': 'Projected net income',
    'propertyReport.futureReturns.totalReturnInfo':
      'Total return on your investment over the full period, including cash flow and property appreciation.',
    'propertyReport.futureReturns.annualizedInfo':
      'Average annual return equivalent, smoothed over the selected horizon.',
    'propertyReport.futureReturns.irrInfo':
      'Internal rate of return (IRR) accounts for when money flows in and out. Useful for comparing projects.',
    'propertyReport.futureReturns.modeSimpleDesc': 'Adjust three growth assumptions as annual percentages.',
    'propertyReport.futureReturns.modeAdvancedDesc':
      'Customize revenue, expenses, and value year by year in a detailed table.',
    'propertyReport.futureReturns.advancedOptions': 'Advanced options',
    'propertyReport.futureReturns.annualIncrease': 'Annual increase',
    'propertyReport.futureReturns.showAltMethod': 'Other method ($, cap rate…)',
    'propertyReport.futureReturns.hideAltMethod': 'Use annual % increase',
    'propertyReport.futureReturns.revenueHelp': 'Estimated annual growth of rent and ancillary income.',
    'propertyReport.futureReturns.expenseHelp': 'Taxes, maintenance, management, and other operating costs.',
    'propertyReport.futureReturns.valueHelp': 'Estimated annual appreciation of the property\'s market value.',
    'propertyReport.futureReturns.desc1': 'Adjust assumptions to estimate returns after',
    'propertyReport.futureReturns.desc2': 'years.',
    'propertyReport.futureReturns.revenue': 'Revenue',
    'propertyReport.futureReturns.expense': 'Operating expenses',
    'propertyReport.futureReturns.propertyValue': 'Property value',
    'propertyReport.futureReturns.modeCapRate': 'Cap',
    'propertyReport.futureReturns.annualizedLabel': 'annualized',
    'propertyReport.futureReturns.hintGross': 'Gross',
    'propertyReport.futureReturns.hintEffective': 'Effective',
    'propertyReport.futureReturns.hintNet': 'Net income',
    'propertyReport.futureReturns.hintExpenses': 'expenses',
    'propertyReport.futureReturns.netIncomeCaption': 'Projected net income',
    'propertyReport.futureReturns.disabledSubScenario':
      'Assumptions come from the selected sub-scenario; manual inputs are disabled.',
    'propertyReport.futureReturns.savedScenario': 'Saved return scenario',
    'propertyReport.futureReturns.selectSavedScenario': 'Select a saved scenario',
    'propertyReport.futureReturns.saveCurrentScenario': 'Save scenario',
    'propertyReport.futureReturns.noSavedScenario': '— Custom assumptions —',
    'propertyReport.futureReturns.modeSimple': 'Simple',
    'propertyReport.futureReturns.modeAdvanced': 'Advanced',
    'propertyReport.futureReturns.subScenarioFinancingNote':
      'The selected sub-scenario adjusts financing only (debt, refinance). Revenue, expenses and projected value come from your assumptions below.',
    'propertyReport.futureReturns.advanced.configureGrid': 'Configure year by year',
    'propertyReport.futureReturns.advanced.modalTitle': 'Year-by-year assumptions',
    'propertyReport.futureReturns.advanced.modalDesc':
      'Year 0 is fixed from the current analysis. Enter overrides for years 1 onward ($ = annual amount, % = change vs previous year). Empty cells inherit the previous year.',
    'propertyReport.futureReturns.advanced.applyColumn': 'Apply to all years',
    'propertyReport.futureReturns.advanced.prefillSimple': 'Fill from simple mode',
    'propertyReport.futureReturns.advanced.reset': 'Clear all overrides',
    'propertyReport.futureReturns.advanced.saveGrid': 'Apply',
    'propertyReport.futureReturns.advanced.yearCol': 'Year',
    'propertyReport.futureReturns.advanced.year0': 'Year 0',
    'propertyReport.futureReturns.advanced.clearCell': 'Clear',
    'propertyReport.futureReturns.advanced.negativeNetWarning':
      'One or more years have negative projected net income.',
    'propertyReport.futureReturns.save': 'Save',
    'propertyReport.futureReturns.delete': 'Delete',
    'propertyReport.futureReturns.deleteConfirm': 'Delete this saved return scenario?',
    'propertyReport.futureReturns.saveModalTitle': 'Save return scenario',
    'propertyReport.futureReturns.saveTitleLabel': 'Title',
    'propertyReport.futureReturns.saveTitlePlaceholder': 'e.g. Conservative 5-year',
    'propertyReport.futureReturns.saveOutlookLabel': 'Outlook',
    'propertyReport.futureReturns.saveTitleRequired': 'Title is required.',
    'propertyReport.futureReturns.saveFailed': 'Could not save scenario.',
    'propertyReport.futureReturns.saving': 'Saving…',
    'propertyReport.futureReturns.saveConfirm': 'Save',
    'propertyReport.futureReturns.outlook.pessimistic': 'Pessimistic',
    'propertyReport.futureReturns.outlook.realistic': 'Realistic',
    'propertyReport.futureReturns.outlook.optimistic': 'Optimistic',
    'propertyReport.futureReturns.pdfAssumptions': 'Return assumptions',
    'propertyReport.futureReturns.pdfRevenue': 'Revenue',
    'propertyReport.futureReturns.pdfExpense': 'Expenses',
    'propertyReport.futureReturns.pdfValue': 'Property value',
    'propertyReport.futureReturns.pdfSavedScenario': 'Saved scenario',
    'propertyReport.subScenario': 'Sub-scenario',
    'propertyReport.none': 'None',
    'propertyReport.untitled': 'Untitled',
    'propertyReport.years': 'Years',
    'propertyReport.incomeGrowth': 'Income growth (%)',
    'propertyReport.expenseGrowth': 'Expense growth (%)',
    'propertyReport.valueAppreciation': 'Value appreciation (%)',
    'propertyReport.globalReturn.prefix': 'Overall return over',
    'propertyReport.globalReturn.suffix': 'year(s)',
    'propertyReport.annualizedReturn': 'Annualized return',
    'propertyReport.irrAtYear.prefix': 'IRR at year',
    'propertyReport.irrAtYear.suffix': ' ',
    'propertyReport.irrInfo': 'The internal rate of return (IRR) is the discount rate that makes the net present value of the investment zero.',
    'propertyReport.amortization': 'Amortization',
    'propertyReport.interestRate': 'Interest rate',
    'propertyReport.amortizationYears': 'Amortization (years)',
    'propertyReport.financingTerm': 'Financing term',
    'propertyReport.newScenario': 'New scenario',
    'propertyReport.generatePdf': 'Generate PDF',
    'propertyReport.generatingPdf': 'Generating PDF…',
    'propertyReport.reportType': 'Report type',
    'propertyReport.reportType.client': 'Client',
    'propertyReport.reportType.bank': 'Bank / Underwriting',
    'propertyReport.reportType.advanced': 'Advanced investor',
    'propertyReport.pdfModalTitle': 'Generate PDF report',
    'propertyReport.pdfPreparedForLabel': 'Prepared for (recipient name)',
    'propertyReport.pdfPreparedForPlaceholder': 'e.g. Client name or lender',
    'propertyReport.pdfIncludeSubScenario': 'Include a sub-scenario (extra PDF page)',
    'propertyReport.pdfSubScenarioSelect': 'Sub-scenario to include',
    'propertyReport.pdfSubScenarioPlaceholder': 'Choose a sub-scenario…',
    'propertyReport.pdfModalConfirm': 'Download PDF',
    'propertyReport.pdfSubScenarioPick': 'Choose a sub-scenario or uncheck the option.',
    'propertyReport.pdfSubScenarioInvalid': 'Unable to build this sub-scenario for the PDF.',
    'propertyReport.pdfGenerateError': 'PDF generation failed. Please try again.',
    'propertyReport.chooseScenarioType': 'Choose scenario type',
    'scenario.initialFinancing': 'Initial financing',
    'scenario.refinancing': 'Refinancing',
    'scenario.renewal': 'Mortgage renewal',
    'scenario.optimization': 'Optimization',
    'scenario.other': 'Other',
    'notApplicable': 'N/A',
    'amortization.title': 'Amortization Schedule',
    'amortization.noData': 'Amortization data unavailable.',
    'amortization.period': 'Period (months)',
    'amortization.loanBalance': 'Loan balance',
    'amortization.interestPayment': 'Interest payment',
    'amortization.principalPayment': 'Principal payment',
    'amortization.cumulativePrincipal': 'Cumulative principal',
    'amortization.propertyValue': 'Property value',
    'amortization.equity': 'Equity',
    'keyIndicators.mrb': 'Gross rent multiplier (GRM)',
    'keyIndicators.mrn': 'Net income multiplier (NIM)',
    'keyIndicators.capRate': 'Cap Rate',
    'keyIndicators.coc': 'Cash on Cash return (CoC)',
    'keyIndicators.cocEquity': 'Cash on Cash on equity (CoC)',
    'keyIndicators.netEquity': 'Net equity',
    'keyIndicators.cashFlow': 'Cash flow',
    'keyIndicators.loanPaydownReturn': 'Loan paydown return (1yr)',
    'keyIndicators.appreciationReturn': 'Appreciation return (default 3% of loan value)',
    'keyIndicators.valueGeneratedYear1': 'Value generated after year 1',
    'keyIndicators.valueGeneratedAfterInitialTermYear1': 'Value generated after year (initial financing term + 1)',
    'keyIndicators.valueGeneratedAfterYearLabel': 'Value generated after year',
    'keyIndicators.totalReturn': 'Total return (1 yr)',
    'keyIndicators.loanValueRatio': 'Loan to value ratio (LVR)',
    'keyIndicators.revenueIncrease': 'Revenue increase',
    'keyIndicators.expenseReduction': 'Expense reduction',
    'keyIndicators.noiImprovement': 'NOI improvement',
    'keyIndicators.cocImprovement': 'CoC improvement',
    'keyIndicators.investmentEfficiency': 'Return on work investment',
    'keyIndicators.paybackPeriod': 'Payback period',
    'keyIndicators.years': 'years',
    'keyIndicators.interestRate': 'Loan interest rate',
    'keyIndicators.monthlyInterest': 'Monthly interest paid',
    'keyIndicators.totalInterest': 'Total interest payable',
    'keyIndicators.capitalRequired': 'Capital required',
    'financialSummary.title': 'Financial Summary',
    'financialSummary.grossIncome': 'Gross income',
    'financialSummary.effectiveIncome': 'Effective gross income',
    'financialSummary.operatingExpensesSCHL': 'CMHC operating expenses',
    'financialSummary.totalExpenses': 'Total expenses',
    'financialSummary.netIncomeSCHL': 'CMHC net income (NOI)',
    'financialSummary.effectiveNetIncome': 'Effective net income',
    'financialSummary.netIncome': 'Net income (NOI)',
    'financialSummary.annualDebtService': 'Annual debt service',
    'financialSummary.annualCashFlow': 'Annual cash flow',
    'dealSummary.title': 'Deal summary',
    'dealSummary.verdict.strong': 'Attractive profile',
    'dealSummary.verdict.medium': 'To analyze in detail',
    'dealSummary.verdict.weak': 'High risk / weak profile',
    'dealSummary.strengths': 'Strengths',
    'dealSummary.risks': 'Risks',
    'schlCompliance.title': 'CMHC / APH compliance',
    'schlCompliance.expensesLabel': 'Operating expenses vs CMHC benchmark',
    'schlCompliance.expensesBelow': 'Below benchmark',
    'schlCompliance.expensesWithin': 'Within benchmark range',
    'schlCompliance.expensesAbove': 'Above benchmark',
    'schlCompliance.ltvAphLabel': 'LTV vs APH maximum',
    'schlCompliance.ltvStandardLabel': 'LTV vs CMHC maximum',
    'schlCompliance.status.ok': 'OK',
    'schlCompliance.status.tooHigh': 'Too high',
    'financingSummary.title': 'Financing Summary',
    'financingSummary.financingType': 'Financing type',
    'financingSummary.aphPoints': 'APH points',
    'financingSummary.qualificationRate': 'Qualification rate',
    'financingSummary.interestRate': 'Interest rate',
    'financingSummary.contractualRate': 'Contractual rate',
    'financingSummary.newInterestRate': 'New Interest Rate',
    'financingSummary.amortization': 'Amortization',
    'financingSummary.remainingAmortization': 'Remaining amortization',
    'financingSummary.debtCoverageRatio': 'Debt coverage ratio',
    'financingSummary.ltvRatio': 'Loan-to-value ratio',
    'financingSummary.originationFee': 'Origination fee',
    'financingSummary.propertyValue': 'Property value',
    'financingSummary.purchasePrice': 'Purchase price',
    'financingSummary.borrowingValue': 'Borrowing value',
    'financingSummary.maxLoan': 'Maximum loan',
    'financingSummary.assumedLoan': 'Assumed loan',
    'financingSummary.loanBalance': 'Loan balance',
    'financingSummary.cmhcPremium': 'CMHC premium',
    'financingSummary.totalLoanWithPremium': 'Total loan including premium',
    'financingSummary.downPayment': 'Down payment',
    'financingSummary.trappedEquity': 'Trapped equity',
    'financingSummary.financingAndWorksFees': 'Financing and works fees',
    'financingSummary.financingFees': 'Financing fees',
    'financingSummary.acquisitionFees': 'Acquisition fees',
    'financingSummary.equityWithdrawal': 'Equity withdrawal',
    'financingSummary.totalInvestment': 'Total investment',
    'financingSummary.monthlyPayment': 'Monthly payment',
    'financingSummary.monthlyPaymentInterestOnly': 'Monthly payment (interest only)',
    'financingSummary.conventional': 'Conventional',
    'financingSummary.cmhc': 'CMHC Standard',
    'financingSummary.cmhc_aph': 'CMHC APH Select',
    'financingSummary.private': 'Private loan',
    'financingSummary.assumption': 'Mortgage assumption',
    'financingSummary.existing': 'In-place financing',
    'financingSummary.none': 'No mortgage',
    'financingSummary.existingLoan': 'Outstanding loan',
    'financingSummary.netEquity': 'Net equity',
    'financingSummary.assumptionRemainingMonths': 'Remaining amortization (months, estimated)',
    'financingSummary.assumptionTermEnd': 'Term end (noted)',
    'scenarioList.viewReport': 'View report',
    'scenarioList.edit': 'Edit',
    'scenarioList.duplicate': 'Duplicate',
    'scenarioList.delete': 'Delete',
    'basicInfo.title': 'Basic Information',
    'basicInfo.address': 'Address',
    'basicInfo.city': 'City',
    'basicInfo.province': 'Province',
    'basicInfo.postalCode': 'Postal Code',
    'basicInfo.askingPrice': 'Asking Price',
    'basicInfo.purchasePrice': 'Purchase Price',
    'basicInfo.estimatedMarketValue': 'Estimated market value',
    'basicInfo.municipalEvaluation': 'Municipal Assessment',
    'basicInfo.numberOfUnits': 'Number of units',
    'basicInfo.structureType': 'Structure type',
    'basicInfo.structureType.woodFrame': 'Wood',
    'basicInfo.structureType.concrete': 'Concrete',
    'basicInfo.schlBenchmarkVersion': 'CMHC expense benchmarks',
    'basicInfo.schlBenchmarkVersion.2023-2024': '2023–2024 (before Jun 8, 2026)',
    'basicInfo.schlBenchmarkVersion.2025-2026': '2025–2026 (from Jun 8, 2026)',
    'basicInfo.schlBenchmarkVersion.hint': 'Select the SCHL national benchmark cycle for underwriting expenses (includes 1% of EGI for other costs in 2025–2026).',
    'basicInfo.buildingPhotoLabel': 'Building photo (PDF report)',
    'basicInfo.buildingPhotoHint': 'Shown on the cover of generated PDFs. Save the building first, then upload.',
    'basicInfo.buildingPhotoRequiresSave': 'Save the building once before adding a photo.',
    'basicInfo.buildingPhotoError': 'Upload failed. Check your connection and try again.',
    'basicInfo.buildingPhotosTitle': 'Building photos',
    'basicInfo.buildingPhotosHint':
      'Add one or more photos. The first is used as the cover (PDF, pocket email). Order follows upload sequence.',
    'basicInfo.buildingPhotosChooseButton': 'Choose photos',
    'basicInfo.buildingPhotosChooseHint': 'Upload starts as soon as you select files (JPEG, PNG, WebP, GIF).',
    'basicInfo.buildingPhotosNoValidImages':
      'No image file was recognized (sometimes the browser omits the file type). Try another file or rename it with a .webp / .jpg extension.',
    'basicInfo.buildingPhotosMax': 'Maximum number of photos reached.',
    'basicInfo.buildingPhotoRemove': 'Remove photo',
    'basicInfo.buildingPhotoCover': 'Cover',
    'basicInfo.buildingPhotosUploadSuccess': '{count} photo(s) saved — they appear in the grid below.',
    'basicInfo.buildingPhotosWhereUsed':
      'These images are used as the PDF cover, in pocket listing previews, and in marketing emails (first photo = cover).',
    'basicInfo.buildingPhotosThumbError':
      'This image could not be displayed (blocked URL, storage rules, or network). Open the browser devtools (F12) → Network for details.',
    'basicInfo.buildingPhotosReorderUp': 'Move earlier (toward cover)',
    'basicInfo.buildingPhotosReorderDown': 'Move later',
    'revenue.title': 'Annual Revenue',
    'revenue.annualRent': 'Annual Rent',
    'revenue.otherRevenue': 'Other Revenue',
    'revenue.parkingRevenue': 'Parking Revenue',
    'revenue.internetRevenue': 'Internet Revenue',
    'revenue.storageRevenue': 'Storage Revenue',
    'revenue.vacancyRate': 'Vacancy / Bad debt (%)',
    'revenue.vacancyRateSchlHint': 'CMHC/SCHL allowance: 3% of gross potential income.',
    'revenue.effectiveGrossIncome': 'Effective gross income',
    'operatingExpenses.title': 'Operating Expenses',
    'operatingExpenses.insurance': 'Insurance',
    'operatingExpenses.municipalTaxes': 'Municipal Taxes',
    'operatingExpenses.schoolTaxes': 'School Taxes',
    'operatingExpenses.electricityHeating': 'Electricity/Heating',
    'operatingExpenses.maintenancePerUnit': 'Maintenance (per unit)',
    'operatingExpenses.conciergePerUnit': 'Concierge (per unit)',
    'operatingExpenses.managementRate': 'Management/Administration (%)',
    'operatingExpenses.otherExpenses': 'Other Expenses',
    'operatingExpenses.totalExpenses': 'Total Expenses',
    'operatingExpenses.heating': 'Heating',
    'operatingExpenses.electricity': 'Electricity',
    'operatingExpenses.landscaping': 'Landscaping',
    'operatingExpenses.snowRemoval': 'Snow Removal',
    'operatingExpenses.extermination': 'Extermination',
    'operatingExpenses.fireInspection': 'Fire Inspection',
    'operatingExpenses.advertising': 'Advertising',
    'operatingExpenses.legal': 'Legal Fees',
    'operatingExpenses.accounting': 'Accounting',
    'operatingExpenses.elevator': 'Elevator',
    'operatingExpenses.cableInternet': 'Cable/Internet',
    'operatingExpenses.garbage': 'Garbage',
    'operatingExpenses.hotWater': 'Hot Water',
    'operatingExpenses.numHeatPumps': 'Wall-mounted Heat Pumps (count)',
    'operatingExpenses.numFridges': 'Refrigerators (count)',
    'operatingExpenses.numStoves': 'Stoves (count)',
    'operatingExpenses.numDishwashers': 'Dishwashers (count)',
    'operatingExpenses.numWashers': 'Washers (count)',
    'operatingExpenses.numDryers': 'Dryers (count)',
    'operatingExpenses.numElevators': 'Elevators (count)',
    'operatingExpenses.replacementReserve': 'Replacement Reserve',
    'operatingExpenses.includedInOther': 'Included in Other Expenses',
    'operatingExpenses.appliances': 'Appliances',
    'operatingExpenses.washerDryer': 'Washer / dryer (common)',
    'pocketListing.badge': 'Pocket deal',
    'pocketListing.headline': 'Investment opportunity',
    'pocketListing.noPhoto': 'No building photo for this listing.',
    'pocketListing.about': 'About this property',
    'pocketListing.keyFigures': 'Key figures',
    'pocketListing.vacancyAllowance': 'Vacancy allowance (estimate)',
    'pocketListing.effectiveGross': 'Effective gross income',
    'pocketListing.tapForSources': 'Open for revenue sources',
    'pocketListing.tapForDetail': 'Open for expense detail',
    'pocketListing.expenseTotalNote':
      'The total matches the financial analysis. Only non-zero line items are listed.',
    'pocketListing.workspaceSectionTitle': 'Investment opportunity',
    'pocketListing.revenueExpenseSectionTitle': 'Revenue and expenses',
    'pocketListing.revenueExpenseSectionHint': 'Expand each card for a line-by-line breakdown.',
    'pocketListing.noBreakdownLines': 'No line items to show.',
    'pocketListing.photoPrev': 'Previous photo',
    'pocketListing.photoNext': 'Next photo',
    'fieldHelp.moreInfo': 'Show explanation',
    'financing.title': 'Financing',
    'financing.type': 'Financing Type',
    'financing.type.conventional': 'Conventional',
    'financing.type.cmhc': 'CMHC Standard',
    'financing.type.cmhc_aph': 'CMHC APH Select',
    'financing.type.private': 'Private Loan',
    'financing.type.assumption': 'Mortgage assumption',
    'financing.assumedLoanBalance': 'Remaining loan balance (assumed)',
    'financing.assumptionMonthlyPayment': 'Monthly payment',
    'financing.assumptionRateHint': 'Effective annual rate (same convention as the rest of the app).',
    'financing.assumptionTermEndOptional': 'Term end (optional)',
    'financing.assumptionTermEndHint': 'End of the current mortgage term (for your notes; not used in the model).',
    'financing.assumptionDerivedAmortMonths': 'Remaining amortization (months, estimated)',
    'financing.assumptionDerivedAmortMonthsHint': 'From balance, payment and rate (Canadian amortization, semi-annual compounding).',
    'financing.assumptionPaymentTooLow': 'Payment must exceed one month of interest on the balance.',
    'financing.aphPoints': 'APH Points',
    'financing.mortgageRate': 'Interest Rate (%)',
    'financing.contractualRate': 'Contractual rate (%)',
    'financing.ltvRatio': 'Loan-to-Value Ratio (LTV)',
    'financing.amortization': 'Amortization (years)',
    'financing.term': 'Term (years)',
    'financing.originationFee': 'Origination Fee',
    'financing.qualificationRate': 'Qualification Rate (%)',
    'financing.debtCoverageRatio': 'Debt coverage ratio',
    'financing.financingCapRate': 'Financing cap rate (%)',
    'financing.financingCapRateHint':
      'Optional. Market cap rate used by the lender; caps borrowing value to the lower of NOI ÷ rate and the revenue-based value.',
    'financing.typeHelp':
      'Loan structure for this scenario: conventional (bank, typically about 20% down), CMHC-insured (lower down payment), CMHC APH Select (maximum LTV and premium depend on points), private loan, or assuming an existing mortgage.',
    'financing.contractualRateHelp':
      'The actual interest rate of the loan. It is used to calculate the mortgage payment and cash flow, not the maximum loan amount.',
    'financing.qualificationRateHelp':
      'The lender’s qualifying (stress) rate. Together with the debt coverage ratio and amortization, it determines the maximum loan. It is often higher than the contractual rate.',
    'financing.amortizationHelp':
      'Number of years over which the loan is fully repaid. A longer amortization lowers the payment and can increase the maximum loan.',
    'financing.termHelp':
      'Length of the current mortgage contract before renewal. Used for projections over the term; it does not change the amortization schedule.',
    'financing.ltvRatioHelp':
      'Share of the purchase price the private lender will finance (loan-to-value).',
    'financing.originationFeeHelp':
      'Private lender origination fee, as a percentage of the loan or a fixed amount. It is added to acquisition costs.',
    'financing.debtCoverageRatioHelp':
      'Minimum net operating income (NOI) required relative to annual debt service. For example, 1.15 means NOI must cover 115% of payments. A higher ratio reduces the maximum loan.',
    'financing.financingCapRateHelp':
      'Optional. Market capitalization rate used by the lender. Borrowing value is the lower of NOI ÷ this rate and the revenue-based value.',
    'financing.aphPointsHelp':
      'CMHC APH Select points. At 70 points the maximum LTV rises to 90%, at 100 points to 95%. Premium rebates may also apply.',
    'financing.assumedLoanBalanceHelp':
      'Outstanding balance of the mortgage you are assuming.',
    'financing.assumptionMonthlyPaymentHelp':
      'Monthly payment of the assumed mortgage. Together with the rate and balance, it is used to estimate remaining amortization.',
    'financingSummary.financingCapRate': 'Financing cap rate',
    'financingFees.environmental1': 'Env. Phase 1',
    'financingFees.appraiser': 'Certified Appraiser',
    'financingFees.cmhcAnalysis': 'CMHC Analysis Fees',
    'financingFees.cmhcTax': 'Tax on CMHC Premium',
    'financingFees.otherFees': 'Other fees',
    'financingFees.notary': 'Notary',
    'financingFees.workCost': 'Work cost',
    'financingFees.sectionTitle': 'Financing fees',
    'financingFees.sectionTitleWithWork': 'Financing fees and optimization work',
    'financingFees.total': 'Total financing fees',
    'financingFees.totalWithWork': 'Total financing fees and work',
    'acquisitionCosts.title': 'Acquisition Costs',
    'acquisitionCosts.expertises': 'Expertise',
    'acquisitionCosts.expertisesInfo': 'Inspection, environmental studies, appraiser and other due-diligence tests grouped into one amount.',
    'acquisitionCosts.notary': 'Notary',
    'acquisitionCosts.notaryInfo': 'Notary and legal fees to close the purchase.',
    'acquisitionCosts.cmhcAnalysis': 'CMHC Analysis Fees',
    'acquisitionCosts.cmhcAnalysisInfo': 'CMHC application/analysis fees, calculated automatically from the number of units ($150 per unit).',
    'acquisitionCosts.cmhcTax': 'Tax on CMHC Premium',
    'acquisitionCosts.cmhcTaxInfo': 'Provincial tax on the CMHC insurance premium (9%). Calculated automatically.',
    'acquisitionCosts.welcomeTax': 'Welcome Tax',
    'acquisitionCosts.welcomeTaxInfo': 'Municipal land transfer tax (welcome tax), calculated automatically from the purchase price. You can override it if needed.',
    'acquisitionCosts.otherFees': 'Other Fees',
    'acquisitionCosts.otherFeesInfo': 'Any other closing costs not listed separately, such as work at acquisition when not using the detailed breakdown.',
    'acquisitionCosts.total': 'Total Acquisition Costs',
    'acquisitionCosts.totalInfo': 'Sum of all acquisition fees entered above (plus the private origination fee when applicable).',
    'acquisitionCosts.inspection': 'Inspection',
    'acquisitionCosts.inspectionInfo': 'Building inspection fees paid at acquisition.',
    'acquisitionCosts.environmental1': 'Env. Phase I',
    'acquisitionCosts.environmental1Info': 'Phase I environmental site assessment (historical review and visual inspection).',
    'acquisitionCosts.environmental2': 'Env. Phase II',
    'acquisitionCosts.environmental2Info': 'Phase II environmental assessment (sampling and lab analysis, if required).',
    'acquisitionCosts.environmental3': 'Env. Phase III',
    'acquisitionCosts.environmental3Info': 'Phase III environmental work (remediation or detailed characterization, if required).',
    'acquisitionCosts.appraiser': 'Certified Appraiser',
    'acquisitionCosts.appraiserInfo': 'Fees for a certified appraiser’s report, often required by the lender.',
    'acquisitionCosts.renovations': 'Renovations',
    'acquisitionCosts.renovationsInfo': 'Work or renovations planned at acquisition and paid from the buyer’s cash.',
    'currentFinancing.title': 'Current Financing',
    'currentFinancing.financingType': 'Financing Type',
    'currentFinancing.bank': 'Bank Institution',
    'currentFinancing.financedAmount': 'Financed Amount',
    'currentFinancing.interestRate': 'Interest Rate (%)',
    'currentFinancing.loanStartDate': 'Loan Start Date',
    'currentFinancing.amortization': 'Amortization (years)',
    'currentFinancing.termEndDate': 'Current Term End Date',
    'scenarioForm.financing.title': 'Financing Scenario',
    'scenarioForm.refinancing.title': 'Refinancing Scenario',
    'scenarioForm.optimization.title': 'Optimization Scenario',
    'scenarioForm.renewal.title': 'Renewal Scenario',
    'scenarioForm.genericTitle': 'Scenario',
    'scenarioForm.parameters': 'Scenario Parameters',
    'scenarioForm.titleLabel': 'Title',
    'scenarioForm.titlePlaceholder': 'Scenario',
    'scenarioForm.revenueGrowthPct': 'Revenue Growth (%)',
    'scenarioForm.expenseGrowthPct': 'Expense Growth (%)',
    'scenarioForm.valueAppreciationPct': 'Value Appreciation (%)',
    'scenarioForm.refinanceYears': 'Refinance in (years)',
    'scenarioForm.marketValue': 'New Market Value',
    'scenarioForm.notPossible': 'This refinancing scenario is not possible.',
    'scenarioForm.newInterestRate': 'New interest rate (%)',
    'scenarioForm.viewSummary': 'View results',
    'scenarioForm.editScenario': 'Edit scenario',
    'recommendations.title': 'Recommendations',
    'recommendations.capRate.veryLow':
      'Cap rate is very low. Consider negotiating the price or increasing revenues. If the building is very recent or strong optimization is possible, the cap rate may be acceptable.',
    'recommendations.capRate.low':
      'Cap rate is acceptable but could be improved. Consider negotiating the price or increasing rents. If the building is recent (less than 15 years) or optimization is possible, the cap rate may be acceptable.',
    'recommendations.capRate.good': 'Good cap rate above 5%.',
    'recommendations.cashFlow.negative':
      'Negative cash flow - the property does not finance itself. Review your strategy or plan additional funds.',
    'recommendations.cashFlow.low':
      'Positive but very low cash flow (less than 2% of net income). Plan a contingency reserve.',
    'recommendations.cashFlow.good':
      'Positive cash flow allowing good flexibility.',
    'executiveSummary.title': 'Executive Summary',
    'executiveSummary.p1.part1': 'This building of',
    'executiveSummary.units': 'units',
    'executiveSummary.p1.part2': 'located at',
    'executiveSummary.addressNotSpecified': 'address not specified',
    'executiveSummary.p1.part3': 'represents an investment of',
    'executiveSummary.p2.part1': 'With a net operating income (NOI) of',
    'executiveSummary.p2.part2': 'the project generates a capitalization rate of',
    'executiveSummary.p2.part3': 'The projected annual cash flow is',
    'executiveSummary.p2.part4': 'for a cash-on-cash return of',
    'executiveSummary.p3.part1': 'The total investment required (down payment + fees) amounts to',
    'executiveSummary.p3.part2': 'with a monthly mortgage payment of'
  },
  fr: {
    'network.online': 'En ligne',
    'network.offline': 'Hors ligne',
    'connectivity.online': 'Connecté',
    'connectivity.offline': 'Sans réseau',
    'availability.online': 'En ligne',
    'availability.away': 'Absent',
    'availability.offline': 'Ne pas déranger',
    'availability.menuTitle': 'Votre statut',
    'floatingChat.toggle': 'Messages privés',
    'floatingChat.panelTitle': 'Messages privés',
    'floatingChat.back': 'Retour',
    'floatingChat.sectionRecent': 'Récent',
    'floatingChat.sectionBrokers': 'Courtiers et clients',
    'floatingChat.sectionPrivateLenders': 'Prêteurs et Investisseurs',
    'floatingChat.sectionDealShares': 'Partages du deal',
    'floatingChat.dealSharesHint': 'Ouvrez un de vos immeubles pour voir les personnes qui ont ajouté ce deal via votre lien.',
    'floatingChat.noSharesForProperty': 'Aucun lien de partage pour cet immeuble.',
    'floatingChat.emptyBrokers': 'Aucun courtier ou client lié pour l’instant.',
    'floatingChat.emptyPrivateLenders': 'Aucun placement pour l’instant.',
    'floatingChat.emptyDealShares': 'Personne d’autre n’a encore ajouté ce deal.',
    'feedback.toggle': 'Feedback',
    'feedback.panelTitle': 'Envoyer un feedback',
    'feedback.categoryLabel': 'Catégorie',
    'feedback.messageLabel': 'Message',
    'feedback.messagePlaceholder': 'Qu’est-ce qui devrait être amélioré? Quel est le problème? Quelle nouvelle fonctionnalité souhaitez-vous?',
    'feedback.category.improvement': 'Amélioration',
    'feedback.category.bug': 'Bug / problème',
    'feedback.category.feature': 'Nouvelle fonctionnalité',
    'feedback.includeConsole': 'Inclure la console (200 derniers événements)',
    'feedback.capture': 'Capture',
    'feedback.capturing': 'Capture…',
    'feedback.captureOk': 'Capture réussie.',
    'feedback.captureError': 'Impossible de faire la capture. Vous pouvez envoyer sans image.',
    'feedback.screenshotAttached': 'Capture jointe',
    'feedback.screenshotAlt': 'Aperçu de la capture jointe',
    'feedback.remove': 'Retirer',
    'feedback.send': 'Envoyer',
    'feedback.sending': 'Envoi…',
    'feedback.sendError': 'Impossible d’envoyer le feedback. Réessayez.',
    'feedback.sent': 'Merci — feedback enregistré.',
    'feedbackAdmin.title': 'Feedback (admin)',
    'feedbackAdmin.exportJson': 'Exporter JSON',
    'feedbackAdmin.loaded': 'Chargé :',
    'feedbackAdmin.empty': 'Aucun feedback pour l’instant.',
    'feedbackAdmin.colWhen': 'Quand',
    'feedbackAdmin.colAuthor': 'Soumis par',
    'feedbackAdmin.colCategory': 'Catégorie',
    'feedbackAdmin.colMessage': 'Message',
    'feedbackAdmin.colScreenshot': 'Capture',
    'feedbackAdmin.colConsole': 'Console',
    'feedbackAdmin.colStatus': 'Statut',
    'feedbackAdmin.colActions': 'Actions',
    'feedbackAdmin.openPage': 'Ouvrir la page',
    'feedbackAdmin.openScreenshot': 'Ouvrir la capture',
    'feedbackAdmin.consoleEvents': 'événements',
    'feedbackAdmin.triage': 'Triage',
    'feedbackAdmin.markTriaged': 'Marquer comme trié',
    'feedbackAdmin.untriage': 'Dé-trier',
    'feedbackAdmin.markNew': 'Marquer comme nouveau',
    'feedbackAdmin.working': 'En cours…',
    'feedbackAdmin.actionError': 'Action échouée (permissions ou réseau).',
    'feedbackAdmin.copyCursorPrompt': 'Copier prompt Cursor',
    'feedbackAdmin.copied': 'Copié',
    'feedbackAdmin.copyFailed': 'Copie échouée',
    'feedbackAdmin.showConsole': 'Voir les logs console',
    'feedbackAdmin.filterAll': 'Tous',
    'feedbackAdmin.filterNew': 'Nouveaux',
    'feedbackAdmin.filterTriaged': 'Triés',
    'feedbackAdmin.filterArchived': 'Archivés',
    'feedbackAdmin.archive': 'Archiver',
    'feedbackAdmin.unarchive': 'Désarchiver',
    'feedbackAdmin.unknownAuthor': 'Utilisateur inconnu',
    'directChat.title': 'Message privé',
    'directChat.sensitiveHint':
      'Seuls vous et l’autre personne pouvez lire ce fil. À utiliser pour la négociation ou les sujets sensibles.',
    'directChat.empty': 'Aucun message privé pour l’instant.',
    'directChat.placeholder': 'Écrire un message privé…',
    'directChat.sendShortcutHint': 'Envoyer (Ctrl+Entrée ou ⌘+Entrée)',
    'directChat.modalTitle': 'Conversation privée',
    'directChat.ensureError': 'Impossible d’ouvrir cette conversation. Le lien entre les comptes est peut-être absent ou obsolète.',
    'directChat.noResolvableShareToken':
      'Aucun lien de partage actif ne vous relie à cette personne pour un message privé. Demandez-lui d’ouvrir de nouveau votre invitation.',
    'directChat.ensureErrorBrokerClient':
      'Ce fil utilise le lien d’invitation de votre courtier. Le lien enregistré est peut-être obsolète, ou le dossier client n’est pas encore lié à votre compte. Demandez à votre courtier de renvoyer l’invitation, ou connectez-vous avec la même adresse courriel que celle de l’invitation. Si c’est déjà fait, déconnectez-vous puis reconnectez-vous.',
    'directChat.recipientsSection': 'Messages privés avec les destinataires',
    'directChat.messageRecipient': 'Message privé',
    'directChat.messageOwner': 'Message au propriétaire du deal',
    'directChat.inboxTitle': 'Messages privés',
    'directChat.inboxEmpty': 'Aucune conversation pour l’instant.',
    'directChat.peerFallback': 'Contact',
    'directChat.goToDeal': 'Aller au deal',
    'directChat.goToSharedDeal': 'Aller au deal partagé',
    'directChat.loadError': 'Impossible de charger les messages. Vérifiez la connexion.',
    'directChat.you': 'Vous',
    'directChat.send': 'Envoyer',
    'directChat.loginToParticipate': 'Connectez-vous pour lire et envoyer des messages.',
    'header.privateMessages': 'Messages',
    'clients.messagePrivate': 'Message',
    'fundInvestors.messagePrivate': 'Message',
    'investorFunds.messageManager': 'Message au gestionnaire du fonds',
    'profile.notifyDirectMessageEmail': 'M’aviser par courriel lors d’un message privé',
    'profile.purchaseOffersModule': 'Gestion des offres d’achat (mandat de vente)',
    'profile.purchaseOffersModuleHint':
      'Activez pour cocher un mandat de vente sur les nouvelles analyses et pour partager des liens avec offres indicatives et PDF officiels. Vous pourrez désactiver ensuite ; les dossiers déjà engagés restent utilisables.',
    'auth.login.title': 'Connexion',
    'auth.login.error': 'Impossible de se connecter',
    'auth.login.invitedByBroker': 'Un courtier vous a invité. Connectez-vous pour lier votre compte au sien.',
    'auth.linkingBroker': 'Liaison à votre courtier en cours…',
    'auth.linkingInvestorAccount': 'Liaison de votre compte investisseur en cours…',
    'auth.linkingBrokerError': 'Impossible de vous lier à votre courtier. Vous pouvez réessayer plus tard ou aller à l\'accueil.',
    'auth.invitationWrongAccount':
      'Vous êtes connecté avec un autre compte que celui visé par cette invitation ({email}). Déconnectez-vous, puis rouvrez le lien, ou utilisez une fenêtre de navigation privée.',
    'auth.invitationWrongAccountHelp': 'Utilisez Déconnexion ci-dessous, puis connectez-vous avec le courriel invité ou rouvrez le lien en navigation privée.',
    'auth.invitationLinkPermissionHelp':
      'Impossible de terminer cette invitation (accès refusé). Si vous êtes connecté avec le mauvais compte, déconnectez-vous et rouvrez le lien avec le courriel invité ({email}).',
    'auth.invitationSignupEmailMismatch':
      'Cette invitation a été envoyée à {email}. Créez un compte ou connectez-vous avec cette adresse courriel exactement.',
    'auth.goToHome': 'Aller à l\'accueil',
    'auth.google.error': 'Impossible de se connecter avec Google',
    'auth.email': 'Email',
    'auth.password': 'Mot de passe',
    'auth.login.submit': 'Se connecter',
    'auth.google': 'Continuer avec Google',
    'auth.forgotPassword': 'Mot de passe oublié?',
    'auth.createAccount': 'Créer un compte',
    'auth.signup.title': 'Inscription',
    'auth.signup.error': 'Impossible de créer le compte',
    'auth.signup.invitedByBroker': 'Votre courtier ou prêteur vous a invité. Créez votre compte pour ajouter votre immeuble en tant qu\'investisseur.',
    'auth.signup.submit': "S'inscrire",
    'auth.error.emailAlreadyInUse': 'Un compte existe déjà pour ce courriel.',
    'auth.error.emailAlreadyInUseUseLogin': 'Ce courriel est déjà inscrit. Connectez-vous pour accepter l\'invitation de votre courtier.',
    'auth.signup.useLoginInstead': 'Se connecter',
    'signup.accountType.label': 'Type de compte',
    'signup.accountType.investisseur': 'Investisseur immobilier',
    'signup.accountType.required': 'Veuillez sélectionner un type de compte.',
    'signup.accountType.comingSoon': 'Bientôt disponible',
    'signup.accountType.comingSoonBadge': 'Bientôt',
    'signup.accountType.teaserHint':
      'Les accès courtier et prêteur privé arrivent bientôt. Pour l\'instant, inscrivez-vous en tant qu\'investisseur immobilier.',
    'signup.accountType.notAvailable': 'Ce type de compte n\'est pas encore disponible.',
    'signup.legal.acceptPrefix': "J'accepte les",
    'signup.legal.acceptAnd': 'et la',
    'signup.legal.acceptSuffix': '.',
    'signup.legal.required':
      "Vous devez accepter les Conditions d'utilisation et la Politique de confidentialité pour continuer.",
    'legal.terms': "Conditions d'utilisation",
    'legal.privacy': 'Politique de confidentialité',
    'legal.back': 'Retour',
    'signup.verification.intro':
      'Les comptes courtier et prêteur privé nécessitent une vérification. Un administrateur examinera votre demande avant de vous donner accès à la plateforme.',
    'signup.verification.companyName': 'Nom de l\'entreprise',
    'signup.verification.phone': 'Numéro de téléphone',
    'signup.verification.address': 'Adresse',
    'signup.verification.companyRequired': 'Le nom de l\'entreprise est requis.',
    'signup.verification.phoneRequired': 'Le numéro de téléphone est requis.',
    'signup.verification.addressRequired': 'Une adresse complète est requise.',
    'approval.pending.title': 'Compte en attente d\'approbation',
    'approval.pending.message':
      'Votre demande de compte a été soumise. Un administrateur examinera vos informations et approuvera l\'accès sous peu.',
    'approval.pending.requestedType': 'Type de compte demandé',
    'approval.rejected.title': 'Compte non approuvé',
    'approval.rejected.message':
      'Votre demande de compte n\'a pas été approuvée. Contactez admin@rentalyzer.ca pour toute question.',
    'admin.approvals.title': 'Approbations de comptes',
    'admin.approvals.empty': 'Aucune demande en attente.',
    'admin.approvals.approve': 'Approuver',
    'admin.approvals.reject': 'Refuser',
    'admin.approvals.company': 'Entreprise',
    'admin.approvals.phone': 'Téléphone',
    'admin.approvals.address': 'Adresse',
    'admin.approvals.email': 'Courriel',
    'admin.approvals.persona': 'Type de compte',
    'admin.approvals.rejectReasonPrompt': 'Motif du refus (optionnel)',
    'admin.approvals.approved': 'Compte approuvé.',
    'admin.approvals.rejected': 'Compte refusé.',
    'profile.companyName': 'Nom de l\'entreprise',
    'auth.error.weakPassword': 'Le mot de passe doit contenir au moins 6 caractères.',
    'auth.error.invalidEmail': 'Adresse courriel invalide.',
    'auth.error.operationNotAllowed': 'L\'inscription par courriel est désactivée. Contactez le support.',
    'auth.error.tooManyRequests': 'Trop de tentatives. Réessayez plus tard.',
    'auth.error.accountExistsWithDifferentCredential': 'Ce courriel est déjà utilisé avec une autre méthode de connexion. Connectez-vous par courriel/mot de passe ou avec la méthode déjà utilisée.',
    'auth.haveAccount': 'Déjà un compte?',
    'auth.login.link': 'Se connecter',
    'auth.reset.title': 'Réinitialiser le mot de passe',
    'auth.reset.success': 'Vérifiez vos courriels pour continuer.',
    'auth.reset.error': 'Impossible de réinitialiser le mot de passe',
    'auth.reset.submit': 'Réinitialiser',
    'auth.completeReset.title': 'Choisir un nouveau mot de passe',
    'auth.completeReset.subtitle': 'Pour {email}',
    'auth.completeReset.newPassword': 'Nouveau mot de passe',
    'auth.completeReset.confirmPassword': 'Confirmer le mot de passe',
    'auth.completeReset.submit': 'Enregistrer le mot de passe',
    'auth.completeReset.success': 'Votre mot de passe a été mis à jour. Redirection vers la connexion…',
    'auth.completeReset.error': 'Impossible de mettre à jour le mot de passe. Veuillez réessayer.',
    'auth.completeReset.mismatch': 'Les mots de passe ne correspondent pas.',
    'auth.completeReset.invalidLink': 'Ce lien de réinitialisation est invalide ou déjà utilisé.',
    'auth.completeReset.expiredLink': 'Ce lien de réinitialisation a expiré. Demandez-en un nouveau.',
    'auth.completeReset.weakPassword': 'Mot de passe trop faible (minimum 6 caractères).',
    'auth.backToLogin': 'Retour à la connexion',
    'auth.logout': 'Déconnexion',
    'onboarding.title': 'Bienvenue sur Rentalyzer',
    'onboarding.question': 'Comment utilisez-vous Rentalyzer?',
    'onboarding.continue': 'Continuer',
    'tour.progress': '{{current}} / {{total}}',
    'tour.actions.skip': 'Passer',
    'tour.actions.next': 'Suivant',
    'tour.actions.previous': 'Précédent',
    'tour.actions.done': 'Terminer',
    'tour.actions.resume': 'Reprendre le tutoriel',
    'tour.assistant.title': 'Guide d\'aide',
    'tour.assistant.subtitle': 'Tutoriels interactifs',
    'tour.assistant.pulseHint': 'Besoin d\'aide? Les tutoriels sont ici.',
    'tour.names.welcome': 'Premier démarrage',
    'tour.names.dashboard': 'Tableau de bord',
    'tour.names.createProject': 'Créer un projet',
    'tour.names.analysis': 'Scénario de financement',
    'tour.names.report': 'Rapport',
    'tour.names.settings': 'Paramètres',
    'tour.status.completed': 'Complété',
    'tour.status.skipped': 'Passé',
    'tour.status.pending': 'Non commencé',
    'tour.status.soon': 'Bientôt',
    'tour.welcome.intro.title': 'Bienvenue sur Rentalyzer',
    'tour.welcome.intro.description': 'Votre assistant intelligent pour réussir vos investissements immobiliers.',
    'tour.welcome.intro.description.investisseur': 'Ici, nous analyserons la rentabilité de vos deals immobiliers.',
    'tour.welcome.intro.description.courtier_immo': 'Gérez les deals clients, analyses et rapports depuis cet écran.',
    'tour.welcome.intro.description.courtier_hypo': 'Suivez les immeubles emprunteurs et les dossiers de financement.',
    'tour.welcome.intro.description.preteur_prive': 'Vue d\'ensemble de vos prêts, emprunteurs et immeubles financés.',
    'tour.welcome.create.title': 'Nouveau deal',
    'tour.welcome.create.description':
      'Cliquez ici pour ajouter un deal. Vous choisirez s’il s’agit d’un immeuble à analyser (achat) ou d’un immeuble que vous détenez déjà.',
    'tour.welcome.tabs.title': 'Vos sections',
    'tour.welcome.tabs.description': 'Basculez entre vos deals, placements et projets partagés.',
    'tour.welcome.profile.title': 'Profil et présence',
    'tour.welcome.profile.description': 'Votre photo, langue et préférences. Le statut En ligne/Absent est visible dans le clavardage.',
    'tour.welcome.profile.description.investisseur': 'Votre photo et vos préférences. Réglez En ligne, Absent ou Hors ligne — visible dans le clavardage.',
    'tour.welcome.nav.title': 'Outils professionnels',
    'tour.welcome.nav.description': 'Clients, checklists et fichiers sont accessibles en haut.',
    'tour.welcome.nav.preteur_prive.description': 'Emprunteurs, prêts et investisseurs de capitaux : tout est dans le menu.',
    'tour.welcome.nav.courtier_immo.description': 'Clients, checklists et campagnes pocket : accédez-y via le menu.',
    'tour.welcome.nav.courtier_hypo.description': 'Emprunteurs, checklists et deals partagés : le menu en haut.',
    'tour.createProject.intro.title': 'Nouvelle analyse',
    'tour.createProject.intro.description':
      'Sur cette page, vous ajoutez un immeuble à analyser. Nous vous demanderons plusieurs renseignements sur le bien. Soyez précis : la qualité de votre analyse de rentabilité en dépend.',
    'tour.createProject.header.title': 'Formulaire immeuble',
    'tour.createProject.header.description': 'C\'est ici que vous décrivez l\'immeuble à analyser.',
    'tour.createProject.basic.title': 'Informations de base',
    'tour.createProject.basic.description':
      'Saisissez les informations générales de l\'immeuble et choisissez les barèmes SCHL sur lesquels vous souhaitez baser votre analyse.',
    'tour.createProject.revenue.title': 'Saisir les revenus',
    'tour.createProject.revenue.description':
      'Indiquez les revenus de l\'immeuble. N\'oubliez pas d\'inclure tous les revenus annexes (garage, stationnement, internet, buanderie, etc.).',
    'tour.createProject.expenses.title': 'Saisir les dépenses d\'exploitation',
    'tour.createProject.expenses.description':
      'Saisissez vos dépenses d\'exploitation. Plus vos chiffres seront précis, plus le portrait du rendement de votre deal sera fidèle.',
    'tour.createProject.mode.title': 'Mode avancé (revenus – dépenses)',
    'tour.createProject.mode.description':
      'Activez ce mode pour détailler les revenus et les dépenses, afin d\'affiner votre analyse et d\'assurer une conformité adéquate aux normes SCHL.',
    'tour.createProject.save.title': 'Sauvegarder et ajouter images / description',
    'tour.createProject.save.description':
      'Une fois l\'immeuble enregistré, vous pourrez ajouter des photos et une description. L\'IA peut vous aider à rédiger cette description.',
    'tour.createProject.owned.intro.title': 'Immeuble que vous détenez',
    'tour.createProject.owned.intro.description':
      'Sur cette page, vous décrivez un immeuble déjà détenu. Saisissez la valeur estimée, les revenus, les dépenses et le financement en place (ou aucune hypothèque). Pas de prix d’achat ni de frais d’acquisition.',
    'tour.createProject.owned.basic.title': 'Immeuble et valeur estimée',
    'tour.createProject.owned.basic.description':
      'Indiquez l’adresse, le nombre d’unités et la valeur estimée actuelle. Cette valeur sert au TGA, au RPV et au rendement sur équité.',
    'tour.createProject.owned.financing.title': 'Financement actuel',
    'tour.createProject.owned.financing.description':
      'Choisissez un financement en place (solde et versement mensuel) ou aucune hypothèque. Ce n’est pas une assomption d’achat — c’est le prêt déjà sur l’immeuble.',
    'tour.createProject.owned.save.title': 'Sauvegarder et ouvrir le rapport',
    'tour.createProject.owned.save.description':
      'La sauvegarde crée le scénario « Situation actuelle » et ouvre le rapport de performance basé sur votre équité.',
    'tour.dashboard.overview.title': 'Deal analysé',
    'tour.dashboard.overview.description':
      'Retrouvez ici les indicateurs clés de l\'analyse de votre deal.',
    'tour.dashboard.intro.title': 'Tableau de bord',
    'tour.dashboard.intro.description': 'Vue d\'ensemble de l\'immeuble avant vos scénarios de financement.',
    'tour.dashboard.title.title': 'Votre immeuble',
    'tour.dashboard.title.description': 'L\'adresse et le contexte de cette analyse.',
    'tour.dashboard.kpis.title': 'Indicateurs clés',
    'tour.dashboard.kpis.description': 'Prix, revenus, taux de capitalisation et autres métriques.',
    'tour.dashboard.scenario.title': 'Créer vos scénarios',
    'tour.dashboard.scenario.description':
      'Cliquez ici pour créer vos scénarios de financement et modéliser la rentabilité de l\'immeuble.',
    'tour.dashboard.dataRoom.title': 'Documents pertinents',
    'tour.dashboard.dataRoom.description':
      'Utilisez la data room pour centraliser les fichiers associés à l\'immeuble et les classer par catégorie.',
    'tour.dashboard.rentRegister.title': 'Registre des loyers',
    'tour.dashboard.rentRegister.description':
      'Consignez le loyer, les inclusions et les services annexes pour chaque logement. Seul le propriétaire du deal peut modifier ce registre ; l\'accès partagé est en lecture seule.',
    'tour.dashboard.edit.title': 'Modifier les informations du deal',
    'tour.dashboard.edit.description':
      'Cliquez ici pour mettre à jour le prix, les revenus, les dépenses, les images et la description.',
    'tour.dashboard.owned.overview.title': 'Immeuble détenu',
    'tour.dashboard.owned.overview.description':
      'Indicateurs clés d’un immeuble que vous détenez : valeur estimée, équité nette, RPV et revenus.',
    'tour.dashboard.owned.rentRegister.description':
      'Suivez loyers et inclusions par logement pour cet immeuble détenu. Utile pour l’exploitation et les dossiers de refinancement.',
    'tour.dashboard.owned.scenario.title': 'Refinancement et optimisation',
    'tour.dashboard.owned.scenario.description':
      'Créez d’autres scénarios pour modéliser un refinancement, un renouvellement ou une optimisation. Votre financement actuel est déjà enregistré comme « Situation actuelle ».',
    'tour.dashboard.owned.edit.title': 'Modifier les informations',
    'tour.dashboard.owned.edit.description':
      'Mettez à jour la valeur de marché, les revenus, les dépenses et le financement en place de cet immeuble détenu.',
    'tour.analysis.intro.title': 'Création d\'un scénario de financement',
    'tour.analysis.intro.description':
      'Cette section vous permet de créer un scénario de financement afin d\'analyser la rentabilité de l\'immeuble.',
    'tour.analysis.title.title': 'Nommer le scénario',
    'tour.analysis.title.description':
      'Donnez un nom à votre scénario pour le retrouver facilement.',
    'tour.analysis.header.title': 'Configuration',
    'tour.analysis.header.description': 'Nommez votre scénario pour le retrouver facilement.',
    'tour.analysis.propertyData.title': 'Consultation des données',
    'tour.analysis.propertyData.description':
      'Consultez les données saisies pour ce deal. Pour les modifier, retournez au tableau de bord de l\'immeuble et mettez à jour les informations.',
    'tour.analysis.financing.title': 'Financement du deal',
    'tour.analysis.financing.description':
      'Saisissez les informations relatives au financement de ce deal immobilier.',
    'tour.analysis.acquisition.title': 'Frais d\'acquisition',
    'tour.analysis.acquisition.description':
      'Ajoutez vos frais d\'acquisition pour connaître le coût total d\'acquisition de l\'immeuble. Chaque frais compte pour obtenir un portrait fidèle du rendement de votre investissement.',
    'tour.analysis.save.title': 'Générer le rapport',
    'tour.analysis.save.description': 'Enregistrez pour ouvrir le rapport de rentabilité complet.',
    'tour.report.intro.title': 'Rapport d\'analyse de rentabilité',
    'tour.report.intro.description':
      'Voici le rapport d\'analyse de rentabilité de votre deal. Il regroupe l\'ensemble des indicateurs de performance de l\'immeuble, y compris leur évolution dans le temps.',
    'tour.report.enteredInfo.title': 'Informations saisies',
    'tour.report.enteredInfo.description':
      'Sommaire des données entrées pour le deal analysé : prix, revenus, dépenses et paramètres de financement.',
    'tour.report.edit.title': 'Modifier le scénario',
    'tour.report.edit.description':
      'Revenez au scénario de financement pour ajuster les paramètres et les frais d\'acquisition, puis actualisez votre analyse de rentabilité.',
    'tour.report.header.title': 'Vue du rapport',
    'tour.report.header.description': 'Résumé de l\'immeuble et du scénario affiché.',
    'tour.report.indicators.title': 'Indicateurs clés',
    'tour.report.indicators.description':
      'Principaux indicateurs de ce deal. Ils vous permettent de voir rapidement si le projet respecte vos critères de base.',
    'tour.report.financialSummary.title': 'Résumé financier',
    'tour.report.financialSummary.description':
      'Consultez les flux de trésorerie (cash flow) générés par le deal analysé.',
    'tour.report.financingSummary.title': 'Résumé du financement',
    'tour.report.financingSummary.description':
      'Structure du financement retenu : mise de fonds, prêt, taux, amortissement et coût total de l\'investissement.',
    'tour.report.futureReturns.title': 'Analyse du rendement',
    'tour.report.futureReturns.description':
      'Projetez les rendements en ajustant les hypothèses d\'augmentation des revenus, des dépenses et de la valeur de l\'immeuble.',
    'tour.report.amortization.title': 'Tableau d\'amortissement',
    'tour.report.amortization.description':
      'Tableau d\'amortissement de l\'hypothèque, combiné à l\'appréciation de la valeur selon l\'hypothèse définie ci-dessus.',
    'tour.report.subScenarios.title': 'Sous-scénarios',
    'tour.report.subScenarios.description':
      'Approfondissez votre analyse avec un sous-scénario d\'optimisation, de refinancement ou de renouvellement hypothécaire, puis intégrez-le aux modèles de rendement ci-dessus.',
    'tour.report.pdf.title': 'Générer un rapport',
    'tour.report.pdf.description':
      'Exportez un PDF pour présenter votre analyse ou valider vos hypothèses auprès de partenaires ou de prêteurs.',
    'tour.report.owned.intro.title': 'Rapport de performance — immeuble détenu',
    'tour.report.owned.intro.description':
      'Ce rapport mesure la performance sur votre équité : cashflow, rendement CoC, remboursement du capital et projections — sans frais d’acquisition.',
    'tour.report.owned.enteredInfo.description':
      'Sommaire de la valeur estimée, des revenus, des dépenses et du financement actuel (en place ou aucune hypothèque).',
    'tour.report.owned.indicators.description':
      'Mettez l’accent sur l’équité nette, le cashflow, le CoC sur équité, le RPV et le rendement total d’un immeuble détenu.',
    'tour.report.owned.financingSummary.description':
      'Affiche le solde du prêt, l’équité nette et le RPV. Les frais d’acquisition ne s’appliquent pas aux immeubles détenus.',
    'tour.report.owned.subScenarios.title': 'Refinancement et optimisation',
    'tour.report.owned.subScenarios.description':
      'Ajoutez des sous-scénarios pour modéliser un refinancement, un renouvellement ou des améliorations opérationnelles.',
    'tour.report.owned.futureReturns.description':
      'Projetez les rendements multi-années à partir de la valeur estimée actuelle et de votre équité.',
    'tour.settings.intro.title': 'Profil et préférences',
    'tour.settings.intro.description': 'Personnalisez Rentalyzer selon vos besoins.',
    'tour.settings.persona.title': 'Votre persona',
    'tour.settings.persona.description': 'Choisissez le rôle qui correspond à votre utilisation.',
    'tour.settings.language.title': 'Langue',
    'tour.settings.language.description': 'Basculez entre français et anglais à tout moment.',
    'tour.assistant.startError': 'Impossible de lancer ce tutoriel. Ouvrez l\'écran concerné, puis réessayez.',
    'tour.assistant.needsContext': 'Accédez à l\'écran concerné pour un meilleur résultat',
    'tour.assistant.recommended': 'Prochaine étape suggérée',
    'tour.assistant.suggestionsTitle': 'Conseils pour vous',
    'tour.assistant.allTours': 'Tous les tutoriels',
    'tour.assistant.aiComingSoon': 'Assistant IA bientôt disponible — conseils contextuels pour l\'instant.',
    'tour.assistant.greeting': 'Besoin d\'aide? Choisissez un tutoriel ou suivez la suggestion.',
    'tour.assistant.greeting.investisseur': 'Analysez vos deals plus vite — parcours adapté à l\'investisseur.',
    'tour.assistant.greeting.courtier_immo': 'Guide courtier immo : clients, deals et rapports.',
    'tour.assistant.greeting.courtier_hypo': 'Guide courtier hypo : dossiers emprunteurs et partages.',
    'tour.assistant.greeting.preteur_prive': 'Guide prêteur privé : prêts, emprunteurs et capitaux.',
    'tour.assistant.suggest.welcome': 'Commencer par l\'accueil',
    'tour.assistant.suggest.createProject': 'Apprendre à créer un deal (analyse ou détenu)',
    'tour.assistant.suggest.dashboard': 'Explorer le tableau de bord',
    'tour.assistant.suggest.analysis': 'Configurer un scénario de financement',
    'tour.assistant.suggest.report': 'Lire et exporter le rapport',
    'tour.assistant.suggest.settings': 'Ajuster profil et préférences',
    'tour.assistant.tip.firstDeal':
      'Créez votre premier deal — immeuble à analyser (achat) ou déjà détenu — pour débloquer le parcours complet.',
    'tour.assistant.tip.firstDealBroker':
      'Ajoutez un immeuble client (achat ou détenu) ou un deal perso pour commencer.',
    'tour.assistant.tip.lenderHome': 'Onglets Gestion des prêts et Emprunteurs pour votre activité.',
    'tour.assistant.tip.brokerPdf': 'Exportez un PDF pour présenter le deal à votre client.',
    'tour.checklist.title': 'Premiers pas',
    'tour.checklist.createDeal': 'Créer un deal (à analyser ou déjà détenu)',
    'tour.checklist.runScenario': 'Enregistrer un scénario de financement',
    'tour.checklist.exportPdf': 'Générer un rapport PDF',
    'tour.welcome.create.description.investisseur':
      'Cliquez ici pour ajouter un deal. Choisissez un immeuble à analyser (achat) ou un immeuble que vous détenez déjà.',
    'tour.welcome.create.description.courtier_immo':
      'Lancez un deal pour vous ou un client. Vous pouvez analyser un achat ou documenter un immeuble déjà détenu.',
    'tour.welcome.create.description.courtier_hypo':
      'Démarrez l’analyse d’un immeuble emprunteur — souscription d’achat ou immeuble déjà détenu.',
    'tour.welcome.create.description.preteur_prive':
      'Modélisez un immeuble avant de structurer un prêt, y compris un bien déjà détenu par l’emprunteur.',
    'tour.welcome.hero.description.investisseur': 'Votre hub : deals, partages et outils portfolio.',
    'tour.welcome.hero.description.courtier_immo': 'Centralisez deals clients, campagnes pocket et partages.',
    'tour.welcome.hero.description.courtier_hypo': 'Suivez les immeubles emprunteurs et dossiers.',
    'tour.welcome.hero.description.preteur_prive': 'Accédez aux prêts, emprunteurs et immeubles financés.',
    'tour.createProject.intro.description.investisseur':
      'Sur cette page, vous ajoutez un immeuble à analyser. Soyez précis sur les revenus et les dépenses : vos métriques de rentabilité en dépendent.',
    'tour.createProject.intro.description.courtier_immo':
      'Sur cette page, vous ajoutez un immeuble à analyser. Saisissez des données précises pour produire une analyse fiable à partager avec votre client.',
    'tour.createProject.intro.description.courtier_hypo':
      'Sur cette page, vous documentez l\'immeuble avant la soumission aux prêteurs. La précision des données est essentielle.',
    'tour.createProject.intro.description.preteur_prive':
      'Sur cette page, vous enregistrez l\'actif à évaluer. Des chiffres précis facilitent la structuration du financement.',
    'tour.createProject.save.description.investisseur':
      'Une fois enregistré, ajoutez photos et description, puis passez au scénario de financement.',
    'tour.createProject.save.description.courtier_immo':
      'Une fois enregistré, ajoutez photos et description pour enrichir le dossier partagé avec votre client.',
    'tour.createProject.save.description.courtier_hypo':
      'Une fois enregistré, ajoutez photos et description pour compléter le dossier emprunteur.',
    'tour.createProject.save.description.preteur_prive':
      'Une fois enregistré, ajoutez photos et description avant de poursuivre l\'évaluation du prêt.',
    'tour.dashboard.scenario.description.investisseur':
      'Modélisez ici les termes hypothécaires pour analyser flux de trésorerie et rendements.',
    'tour.dashboard.scenario.description.courtier_immo':
      'Créez ici des scénarios pour comparer les options de financement avec votre client.',
    'tour.dashboard.scenario.description.courtier_hypo':
      'Créez ici des scénarios pour préparer les hypothèses du dossier de financement.',
    'tour.dashboard.scenario.description.preteur_prive':
      'Définissez ici les termes du prêt avant de générer votre rapport de rentabilité.',
    'tour.analysis.intro.description.investisseur':
      'Créez ici un scénario de financement pour modéliser flux de trésorerie et rendements.',
    'tour.analysis.intro.description.courtier_immo':
      'Créez ici un scénario de financement pour produire une analyse de rentabilité client.',
    'tour.analysis.intro.description.courtier_hypo':
      'Configurez ici un scénario de financement aligné avec les critères des prêteurs.',
    'tour.analysis.intro.description.preteur_prive':
      'Définissez ici un scénario de financement avec les termes du prêteur privé et les coûts d\'acquisition.',
    'tour.report.pdf.description.investisseur': 'Exportez un PDF pour vos partenaires ou votre dossier.',
    'tour.report.pdf.description.courtier_immo': 'PDF soigné pour la présentation client.',
    'tour.report.pdf.description.courtier_hypo': 'Partagez un résumé PDF avec l\'emprunteur ou le prêteur.',
    'tour.report.pdf.description.preteur_prive': 'Téléchargez le rapport pour votre comité ou investisseur.',
    'persona.courtier_immo': 'Courtier immobilier',
    'persona.investisseur': 'Investisseur',
    'persona.courtier_hypo': 'Courtier hypothécaire',
    'persona.preteur_prive': 'Prêteur privé',
    'persona.autre': 'Autre',
    'profile.title': 'Mon profil',
    'profile.menuItem': 'Mon profil',
    'pwa.install': 'Installer l\'application',
    'profile.displayName': 'Nom affiché',
    'profile.displayNamePlaceholder': 'Votre nom ou entreprise',
    'profile.avatarUrl': 'Avatar (URL d’image)',
    'profile.persona': 'Type de profil',
    'profile.language': 'Langue',
    'profile.save': 'Enregistrer',
    'profile.tabPersonal': 'Infos personnelles',
    'profile.tabCompanies': 'Mes sociétés',
    'profile.personalAddressSection': 'Adresse personnelle',
    'profile.personalAddressHint': 'Il s’agit de votre domicile ou adresse postale à titre personnel, distincte des adresses de vos sociétés. Commencez à taper la rue pour des suggestions d’adresse Google.',
    'profile.phone': 'Téléphone',
    'profile.addressStreet': 'Rue et numéro',
    'profile.addressCity': 'Ville',
    'profile.addressProvince': 'Province / État',
    'profile.addressPostalCode': 'Code postal',
    'profile.addressCountry': 'Pays',
    'profile.bio': 'À propos de vous',
    'profile.bioPlaceholder': 'Quelques mots sur vous (optionnel)',
    'profile.emailReadOnly': 'Courriel (connexion)',
    'profile.saveError': 'Erreur lors de l’enregistrement du profil',
    'profile.saveSuccessVerse': 'Un souffle, un clic — votre profil s’en va tout doux se blottir dans le nuage, là-haut.',
    'profile.unknown': 'Utilisateur',
    'profile.deleteAccount': 'Supprimer mon compte',
    'profile.deleteAccountWarning': 'Votre compte et toutes vos données seront supprimés définitivement : profil, immeubles et deals. Cette action est irréversible.',
    'profile.deleteAccountConfirm': 'Oui, supprimer mon compte et toutes mes données',
    'profile.deleteAccountError': 'Impossible de supprimer le compte. Si vous êtes connecté depuis longtemps, déconnectez-vous, reconnectez-vous, puis réessayez.',
    'profile.deleteAccountBrokerHasClients': 'Vous ne pouvez pas supprimer votre compte tant que vous avez des clients actifs. Supprimez ou transférez vos clients dans Mes Clients d\'abord.',
    'profile.deleting': 'Suppression…',
    'profile.deleteAccountPasswordHint': 'Entrez votre mot de passe pour confirmer votre identité',
    'profile.deleteAccountReauthGoogle': 'Vous devrez vous reconnecter avec Google pour confirmer votre identité.',
    'about.back': 'Retour',
    'about.tagline': 'Actuaire de formation, entrepreneur et investisseur immobilier',
    'about.intro': "Passionné par l'analyse financière et l'immobilier, j'ai développé Rentalyzer pour offrir aux investisseurs un outil simple et fiable afin de valider les rendements réels de leurs projets.",
    'about.mvvTitle': "Mission, vision et valeurs de l'application",
    'about.mvv1': "Rentalyzer est né du désir de démocratiser l'analyse de rentabilité immobilière et d'aider les investisseurs à prendre des décisions éclairées.",
    'about.mvv2': "À long terme, l'objectif est d'accompagner les investisseurs pour comprendre leurs placements et valider les rendements réels qu'ils génèrent.",
    'about.valuesLabel': 'Valeurs',
    'about.valuesList': 'Simplicité, Fiabilité, Éthique',
    'about.backgroundTitle': 'Parcours professionnel',
    'about.education': "Détenteur d'un baccalauréat en actuariat",
    'about.entrepreneur': "Entrepreneur depuis l'âge de 20 ans",
    'about.investor': "Investisseur immobilier d'expérience",
    'about.lender': 'Prêteur privé (fonds de prêt hypothécaire ou équivalent)',
    'about.contactTitle': 'Contact',
    'about.email': 'Courriel',
    'about.phone': 'Téléphone',
    'about.linkedin': 'LinkedIn',
    'about.photoAlt': 'Photo du développeur',
    'loading': 'Chargement...',
    'errorBoundary.message': 'Une erreur est survenue. Essayez de rafraîchir la page.',
    'errorBoundary.refresh': 'Rafraîchir la page',
    'property.import.success': 'Immeuble importé avec succès',
    'share.link.copied': 'Lien de partage copié dans le presse-papiers',
    'share.error': "Erreur lors du partage",
    'home.title': "Outil d'Analyse de Rentabilité Multi Logements",
    'home.subtitle': 'Rentalyzer, votre assistant intelligent pour réussir vos investissements immobiliers',
    'home.empty.title': '🚀 Commencez votre premier deal',
    'home.step1.title': '1. Saisissez vos données',
    'home.step1.text': 'Prix, revenus, dépenses - nous vous guidons à chaque étape',
    'home.step2.title': "2. Obtenez l'analyse",
    'home.step2.text': 'Rentabilité, cash flow, recommandations personnalisées',
    'home.step3.title': '3. Générez le rapport',
    'home.step3.text': 'Rapport professionnel prêt à présenter',
    'home.new.button': 'Analyser mon premier immeuble',
    'home.existing.title': 'Mes Deals',
    'home.existing.new': 'Nouveau deal',
    'home.sectionMyAnalyses': 'Mes Deals',
    'home.mandatesFilter': 'Mandats seulement',
    'home.mandatesFilterHint': 'Afficher uniquement les immeubles avec mandat de vente déclaré.',
    'home.mandatesFilterEmpty': 'Aucun immeuble ne correspond à ce filtre.',
    'home.sectionPocketCampaigns': 'Campagnes pocket',
    'home.pocketCampaignsTitle': 'Campagnes pocket récentes',
    'home.pocketCampaignsIntro':
      'Indicateurs par campagne (envois, ouvertures, clics, inscriptions, demandes d’intérêt). Renvoyer ouvre directement l’onglet liste VIP / pocket pour un nouvel envoi après correction ou mise à jour du deal.',
    'home.pocketCampaignsEmpty':
      'Aucune campagne pocket pour le moment. Envoyez un pocket deal depuis Partager → onglet VIP / pocket d’un immeuble.',
    'home.pocketCampaignProperty': 'Immeuble',
    'home.pocketCampaignPropertyMissing': 'Immeuble inconnu ou supprimé',
    'home.pocketCampaignActions': 'Actions',
    'home.pocketCampaignResubmit': 'Renvoyer',
    'home.sectionClientProperties': 'Immeubles Clients',
    'home.clientPropertiesCount': '{{count}} immeuble(s)',
    'home.clientPropertiesCount_one': '1 immeuble',
    'home.clientPropertiesCount_other': '{{count}} immeubles',
    'home.noClientProperties': 'Aucun immeuble partagé par un client pour le moment.',
    'home.clientDeleted': 'Client supprimé',
    'home.unknownClient': 'Client inconnu',
    'home.noMyAnalyses': 'Aucun deal pour le moment. Créez-en un avec le bouton ci-dessus.',
    'home.share': 'Partager',
    'home.newBadge': 'Nouveau',
    'home.delete': 'Supprimer',
    'home.address.unset': 'Adresse non spécifiée',
    'home.units': 'unités',
    'home.pricePerDoor': '$/porte',
    'home.perYear': '$/an',
    'home.about': 'À PROPOS',
    'home.clients': 'Mes clients',
    'home.borrowers': 'Mes emprunteurs',
    'home.checklists': 'Mes checklists',
    'home.marketParams': 'Paramètres marché',
    'header.goToHome': 'Accueil',
    'header.menu': 'Menu',
    'clients.title': 'Mes clients',
    'clients.add': 'Ajouter un client',
    'clients.name': 'Nom',
    'clients.address': 'Adresse',
    'clients.email': 'Courriel',
    'clients.noClients': 'Aucun client pour l\'instant.',
    'clients.propertiesLinked': 'Immeubles liés',
    'clients.inviteLinkCreated': 'Invitation créée. Un courriel a été envoyé au client (ou copiez le lien ci-dessous).',
    'clients.inviteLinkCopy': 'Envoyez ce lien à votre client pour qu\'il s\'inscrive en tant qu\'investisseur et ajoute son immeuble.',
    'clients.inviteQueueError': 'Impossible d\'enregistrer l\'invitation pour envoi :',
    'clients.copyLink': 'Copier le lien',
    'clients.sending': 'Envoi…',
    'clients.resendInvitation': 'Renvoyer l\'invitation par courriel',
    'clients.bulkInviteAllSummary':
      '{n} client(s) n’ont pas encore relié leur compte — vous pouvez mettre tous les courriels d’invitation en file d’attente.',
    'clients.bulkInviteAllSummaryBorrower':
      '{n} emprunteur(s) n’ont pas encore relié leur compte — vous pouvez mettre tous les courriels d’invitation en file d’attente.',
    'clients.bulkInviteAllButton': 'Inviter tout le monde en attente (courriel)',
    'clients.bulkInviteAllConfirm':
      'Envoyer les courriels d’invitation à tous ceux qui n’ont pas encore relié leur compte ? Le même type de lien qu’un renvoi individuel sera utilisé.',
    'clients.bulkInviteAllSending': 'Envoi…',
    'clients.bulkInviteAllResultOk': '{n} courriel(s) d’invitation mis en file d’attente.',
    'clients.bulkInviteAllResultPartial':
      '{ok} invitation(s) mise(s) en file, {failed} échec(s) — vérifiez la console ou réessayez pour ces lignes.',
    'clients.invitationResent': 'Envoyé!',
    'clients.invitationResentConfirm': 'L\'invitation a été renvoyée à',
    'clients.edit': 'Modifier',
    'clients.delete': 'Supprimer',
    'clients.titleBorrowers': 'Mes emprunteurs',
    'clients.addBorrower': 'Ajouter un emprunteur',
    'clients.noBorrowers': 'Aucun emprunteur pour l\'instant.',
    'clients.inviteLinkCopyBorrower':
      'Envoyez ce lien pour que l\'emprunteur crée son compte. Les immeubles brouillon que vous avez ajoutés apparaîtront sur son tableau de bord après l\'inscription.',
    'clients.propertiesLinkedBorrower': 'Immeubles liés',
    'clients.stagedSectionTitle': 'Immeubles brouillon (avant inscription)',
    'clients.stagedSectionHelp':
      'Seule l\'adresse est obligatoire. L\'analyse pourra être complétée plus tard. Les offres et prêts dans l\'app requièrent un immeuble rattaché au compte de l\'emprunteur.',
    'clients.stagedBuildingsCount': 'Brouillons',
    'clients.stagedAddress': 'Adresse (rue)',
    'clients.stagedCity': 'Ville',
    'clients.stagedProvince': 'Province',
    'clients.stagedPostal': 'Code postal',
    'clients.stagedNotes': 'Notes (optionnel)',
    'clients.stagedAddButton': 'Enregistrer le brouillon',
    'clients.stagedDelete': 'Retirer',
    'clients.stagedAfterSignupNote':
      'Cet emprunteur est lié. Les brouillons ont été convertis en immeubles à l\'inscription.',
    'clients.stagedAddressAutocompleteHint':
      'Tapez l’adresse — choisissez une suggestion pour remplir ville, province et code postal (Canada).',
    'clients.stagedExpandAddButton': 'Ajouter un immeuble brouillon',
    'clients.stagedFormOpenLabel': 'Nouvel immeuble brouillon',
    'clients.stagedDoneButton': 'Terminé',
    'clients.stagedLoanTranchesTitle': 'Tranches de prêt (optionnel)',
    'clients.stagedLoanTranchesHelp':
      'Ajoutez au moins une tranche avec capital et date de début pour migrer les prêts lorsque l’emprunteur lie son compte.',
    'clients.stagedTranchePrincipal': 'Capital ($)',
    'clients.stagedTrancheRate': 'Taux % annuel',
    'clients.stagedTrancheNote': 'Note',
    'clients.stagedTrancheAdd': 'Ajouter une tranche',
    'clients.stagedTrancheRemove': 'Retirer la tranche',
    'clients.stagedTrancheMaturityAfterStart':
      'Lorsqu’une date d’échéance est indiquée, elle doit être postérieure à la date de début (la date de début est alors obligatoire).',
    'clients.stagedTranchesSummary': '{count} tranche(s) · {total} de capital',
    'clients.importBorrowersButton': 'Importer depuis un fichier',
    'clients.importBorrowersTitle': 'Importer des emprunteurs depuis un fichier',
    'clients.importBorrowersHint':
      'Téléversez un fichier CSV ou Excel (.csv, .xlsx, .xls) issu de vos dossiers. L’assistant lit le tableau et propose des lignes à valider avant toute écriture.',
    'clients.importBorrowersTemplateRecommended':
      'Le téléchargement du fichier exemple Excel (.xlsx) est recommandé (facultatif). Votre propre export peut suffire si le tableau est lisible.',
    'clients.importBorrowersDownloadTemplate': 'Télécharger un exemple (Excel .xlsx)',
    'clients.importBorrowersExpectedTitle':
      'Le fichier est plus facile à interpréter s’il contient (colonnes ou tableau) :',
    'clients.importBorrowersExpectedBorrower': 'Emprunteur — nom de la personne ou de la société',
    'clients.importBorrowersExpectedEmail':
      'Courriel — sert à regrouper immeubles et tranches sous un même emprunteur',
    'clients.importBorrowersExpectedAddress':
      'Adresse de l’immeuble — adresse civique et/ou nom de projet (dossier) pour chaque bien',
    'clients.importBorrowersExpectedTranches':
      'Tranches de prêt liées à chaque immeuble — date de début et date de fin (échéance) de chaque tranche',
    'clients.importBorrowersExpectedAmount': 'Montant prêté — capital de la tranche',
    'clients.importBorrowersExpectedRate': 'Taux du prêt — pourcentage annuel pour chaque tranche',
    'clients.importChooseFile': 'Choisir un fichier',
    'clients.importAnalyzing': 'Analyse en cours…',
    'clients.importPreviewHelp':
      'Le courriel relie tout : plusieurs lignes avec le même courriel = un seul emprunteur et un immeuble brouillon par ligne (si « Immeuble brouillon » est coché). Décochez pour ignorer. Projet seul → colonne « Projet » ; adresse + projet → les deux vont dans les notes.',
    'clients.importConfirm': 'Créer les emprunteurs sélectionnés',
    'clients.importName': 'Nom',
    'clients.importEmail': 'Courriel',
    'clients.importProjectTitle': 'Projet',
    'clients.importStreet': 'Adresse (rue)',
    'clients.importTranchesColumn': 'Tranches de prêt',
    'clients.importTrancheAt': 'à',
    'clients.importTrancheDateLine': 'Du {start} Au {end}',
    'clients.importRowSubtotal': 'Sous-total',
    'clients.importPreviewTotalPrincipal': 'Total principal (toutes les lignes)',
    'clients.importPreviewTotalSelected': 'Total principal (lignes cochées seulement)',
    'clients.importIncludeBuilding': 'Immeuble brouillon',
    'clients.importDuplicateEmail': 'Ce courriel est déjà un emprunteur.',
    'clients.importExistingBorrowerBuildingsOnly':
      'Déjà emprunteur : cette ligne ajoute seulement un immeuble brouillon (pas de nouveau compte).',
    'clients.importSameEmailGroupedHint':
      'Même courriel qu’une ligne ci-dessus : un seul emprunteur ; cette ligne ajoute un brouillon si coché.',
    'clients.importNoEmail': 'Courriel valide requis.',
    'clients.importSuccess': '{n} emprunteur(s) créé(s).',
    'clients.importSuccessBuildings': '{n} immeuble(s) brouillon ajouté(s).',
    'clients.importSuccessBorrowersAndBuildings': '{b} emprunteur(s) créé(s), {d} immeuble(s) brouillon ajouté(s).',
    'clients.importNothingCreated': 'Rien n’a été enregistré. Vérifiez les courriels, la sélection et les lignes brouillon.',
    'clients.importPartialSummary':
      '{br} emprunteur(s) créé(s), {bd} immeuble(s) brouillon ; {bad} ligne(s) en erreur (validation ou enregistrement).',
    'clients.importNoSelection': 'Aucune ligne sélectionnée.',
    'clients.importNoteProjectPrefix': 'Projet : ',
    'checklists.title': 'Mes checklists',
    'checklists.add': 'Nouvelle checklist',
    'checklists.name': 'Nom',
    'checklists.description': 'Description',
    'checklists.items': 'Éléments',
    'checklists.itemLabel': 'Élément',
    'checklists.requireFile': 'Exiger un fichier',
    'checklists.setDefault': 'Définir par défaut',
    'checklists.isDefault': 'Par défaut',
    'checklists.noChecklists': 'Aucune checklist pour l\'instant.',
    'checklists.deleteConfirm': 'Supprimer cette checklist?',
    'financementDossier.request': 'Envoyer la checklist de financement',
    'financementDossier.sendChecklist': 'Envoyer la checklist de financement',
    'financementDossier.requestDescription': 'Demandez à ce client de compléter un dossier de financement avec une de vos checklists.',
    'financementDossier.selectChecklist': 'Choisir une checklist',
    'financementDossier.requested': 'Dossier demandé',
    'financementDossier.sectionTitle': 'Dossiers de financement',
    'financementDossier.open': 'Ouvrir',
    'financementDossier.status_not_started': 'Non commencé',
    'financementDossier.status_in_progress': 'En cours',
    'financementDossier.status_submitted': 'Soumis',
    'financementDossier.noDossiersYet': 'Aucun dossier pour l\'instant. Utilisez « Dossier de financement » ci-dessus lorsqu\'un courtier en a demandé un.',
    'financementDossier.chooseFromProject': 'Choisir dans la data room',
    'financementDossier.markSubmitted': 'Marquer comme soumis',
    'financementDossier.loadError': 'Impossible de charger le dossier.',
    'financementDossier.notFound': 'Dossier introuvable.',
    'financementDossier.propertyUnavailable': 'Propriété non disponible.',
    'financementDossier.updateChecklist': 'Mettre à jour la checklist',
    'financementDossier.cancelRequest': 'Annuler la demande',
    'financementDossier.activeRequestDescription': 'Vous avez déjà une demande de dossier de financement active pour cet immeuble. Mettez à jour sa checklist ou annulez-la.',
    'financementDossier.existingRequestsForProperty': 'Demandes en cours pour cet immeuble',
    'financementDossier.manageExistingHint': 'Vous pouvez ouvrir, mettre à jour la checklist ou annuler chaque demande ci-dessous.',
    'financementDossier.cancelRequestConfirm': 'Voulez-vous vraiment annuler cette demande? Le client ne la verra plus.',
    'financementDossier.selectChecklistWithItems': 'Choisissez une checklist avec au moins un élément.',
    'privateLenderOffer.makeOffer': 'Faire une offre de financement',
    'privateLenderOffer.comment': 'Commentaire (optionnel)',
    'privateLenderOffer.commentPlaceholder': 'Résumez votre offre ou ajoutez toute information utile pour l\'investisseur.',
    'privateLenderOffer.principalAmount': 'Montant du prêt',
    'privateLenderOffer.interestRate': 'Taux d\'intérêt (%)',
    'privateLenderOffer.termMonths': 'Terme (mois)',
    'privateLenderOffer.loanStartDate': 'Date de début du prêt',
    'privateLenderOffer.loanMaturityDate': 'Date d\'échéance du prêt (optionnelle)',
    'privateLenderOffer.paymentFrequency': 'Fréquence de paiement des intérêts',
    'privateLenderOffer.paymentFrequency_monthly': 'Mensuelle',
    'privateLenderOffer.paymentFrequency_quarterly': 'Trimestrielle',
    'privateLenderOffer.interestPaymentDue': 'Paiement des intérêts dû',
    'privateLenderOffer.interestPaymentDue_end': 'En fin de mois',
    'privateLenderOffer.interestPaymentDue_beginning': 'En début de mois',
    'privateLenderOffer.interestCalculationMode': 'Calcul des intérêts',
    'privateLenderOffer.interestCalculationMode_daily': 'Quotidien (prorata, base 365 jours)',
    'privateLenderOffer.interestCalculationMode_monthly': 'Mensuel (montant fixe par mois complet)',
    'privateLenderOffer.interestCalculationMode_dailyHint':
      'Les intérêts s’accumulent au jour le jour avec le taux nominal annuel ÷ 365 sur le capital en cours, y compris pour le premier et le dernier mois partiels.',
    'privateLenderOffer.interestCalculationMode_monthlyHint':
      'Chaque mois civil complet utilise le même montant : capital × (taux annuel ÷ 100) ÷ 12. Le premier et le dernier mois sont calculés au prorata des jours si le prêt ne commence pas le 1er ou ne se termine pas le dernier jour du mois. Un ajout de capital en cours de mois utilise le prorata quotidien pour ce mois.',
    'privateLenderOffer.interestPaymentDueStandardNote': 'Les paiements d’intérêts sont dus en fin de mois.',
    'privateLenderOffer.contractFile': 'Contrat de prêt (PDF)',
    'privateLenderOffer.chooseContract': 'Choisir le fichier du contrat',
    'privateLenderOffer.submit': 'Soumettre l\'offre',
    'privateLenderOffer.submitting': 'Envoi en cours…',
    'privateLenderOffer.contractRequired': 'Veuillez joindre le contrat de prêt (PDF).',
    'privateLenderOffer.offersForProperty': 'Vos offres de financement pour cet immeuble',
    'financementDossier.checklistsSentToClient': 'Checklists de financement envoyées à ce client',
    'privateLenderOffer.sectionTitle': 'Offres de financement',
    'privateLenderOffer.status_pending': 'En attente',
    'privateLenderOffer.status_accepted': 'Acceptée',
    'privateLenderOffer.status_rejected': 'Refusée',
    'privateLenderOffer.status_cancelled': 'Annulée',
    'privateLenderOffer.status_deal_cancelled': 'Annulée',
    'privateLenderOffer.cancelContract': 'Annuler le contrat',
    'privateLenderOffer.cancelOffer': 'Annuler l\'offre',
    'privateLenderOffer.cancelOfferConfirm': 'Annuler cette offre de financement? L\'investisseur la verra comme annulée.',
    'privateLenderOffer.accept': 'Accepter',
    'privateLenderOffer.reject': 'Refuser',
    'privateLenderOffer.uploadSignedContract': 'Téléverser le contrat signé',
    'privateLenderOffer.downloadSignedContract': 'Télécharger le contrat signé',
    'privateLenderOffer.chooseSignedContract': 'Choisir le fichier du contrat signé',
    'privateLenderOffer.summaryDisclaimer': 'Ce résumé est fourni à titre informatif. Il est important de lire et de bien comprendre le contrat. Ce résumé ne constitue pas un avis juridique ni financier.',
    'privateLenderOffer.summarizeContract': 'Résumer le contrat',
    'privateLenderOffer.summarizeWithAI': 'Résumer le contrat avec l\'IA',
    'privateLenderOffer.summaryTitle': 'Résumé du contrat',
    'privateLenderOffer.summarizing': 'Résumé en cours…',
    'privateLenderOffer.summaryError': 'Impossible de générer le résumé.',
    'privateLenderOffer.downloadContract': 'Télécharger le contrat',
    'privateLenderOffer.fillOrVerifyWithAI': 'Remplir / Vérifier avec l\'IA',
    'privateLenderOffer.extracting': 'Extraction en cours…',
    'privateLenderOffer.documentDoesNotCorroborate': 'Le document ne corrobore pas cette information.',
    'privateLenderOffer.conformitySuccess': 'Tout est conforme au contrat.',
    'privateLenderLoans.title': 'Gestion des prêts',
    'privateLenderLoans.stagingDraftSummaryTitle': 'Immeubles brouillon (emprunteurs)',
    'privateLenderLoans.stagingDraftSummaryBody':
      '{buildings} immeuble(s) brouillon avec tranches de prêt — capital saisi : {principal}. Gérez-les sous Emprunteurs.',
    'privateLenderLoans.activeLoans': 'Prêts actifs',
    'privateLenderLoans.totalCapital': 'Capital total',
    'privateLenderLoans.awaitingDisbursement': 'En attente de décaissement',
    'privateLenderLoans.awaitingDisbursementBlock': 'En attente de décaissement',
    'privateLenderLoans.markDisbursed': 'Marquer fonds versés',
    'privateLenderLoans.maturitySoon': 'Échéance sous 90 jours',
    'privateLenderLoans.days': 'jours',
    'privateLenderLoans.colProperty': 'Propriété',
    'privateLenderLoans.colAmount': 'Montant',
    'privateLenderLoans.colRate': 'Taux',
    'privateLenderLoans.colStart': 'Début',
    'privateLenderLoans.colMaturity': 'Échéance',
    'privateLenderLoans.colDisbursed': 'Fonds versés',
    'privateLenderLoans.colActions': 'Actions',
    'privateLenderLoans.noLoans': 'Aucun prêt actif.',
    'privateLenderLoans.detail': 'Détail',
    'privateLenderLoans.detailTitle': 'Détail du prêt',
    'privateLenderLoans.yes': 'Oui',
    'privateLenderLoans.no': 'Non',
    'privateLenderLoans.tranches': 'Tranches',
    'privateLenderLoans.addTranche': 'Ajouter une tranche de capital',
    'privateLenderLoans.noTranches': 'Aucune tranche.',
    'privateLenderLoans.trancheInitial': 'Initiale',
    'privateLenderLoans.trancheAdditional': 'Additionnelle',
    'privateLenderLoans.addTrancheTitle': 'Ajouter une tranche de capital',
    'privateLenderLoans.trancheAmount': 'Montant',
    'privateLenderLoans.trancheStartDate': 'Date de début',
    'privateLenderLoans.trancheRate': 'Taux d\'intérêt (%)',
    'privateLenderLoans.trancheNote': 'Note',
    'privateLenderLoans.addTrancheSubmit': 'Ajouter la tranche',
    'privateLenderLoans.trancheAmountDateRequired': 'Le montant et la date de début sont requis.',
    'privateLenderLoans.trancheType': 'Type',
    'privateLenderLoans.trancheEffectiveDate': 'Date de prise d\'effet',
    'privateLenderLoans.deleteTranche': 'Supprimer',
    'privateLenderLoans.deleteTrancheConfirm': 'Supprimer cette tranche ? Elle ne peut être supprimée qu\'avant sa date de prise d\'effet.',
    'privateLenderLoans.cancelDeal': 'Annuler le prêt',
    'privateLenderLoans.cancelDealConfirm': 'Annuler ce contrat de prêt ? Les fonds n\'ont pas encore été versés. Cette action est irréversible.',
    'privateLenderLoans.interestPaymentSection': 'Paiement d\'intérêts',
    'privateLenderLoans.interestExpected': 'Attendu',
    'privateLenderLoans.expectedBy': 'Attendu le',
    'privateLenderLoans.interestStatus_pending': 'En attente',
    'privateLenderLoans.interestStatus_paid': 'Reçu',
    'privateLenderLoans.interestStatus_partial': 'Partiel',
    'privateLenderLoans.interestStatus_overpaid': 'Surapporté',
    'privateLenderLoans.markPaymentReceived': 'Marquer comme reçu',
    'privateLenderLoans.paidAmount': 'Montant reçu',
    'privateLenderLoans.paidAt': 'Date de réception',
    'privateLenderLoans.discrepancyNote': 'Note (si différent du montant attendu)',
    'privateLenderLoans.savePayment': 'Enregistrer',
    'privateLenderLoans.status': 'Statut',
    'privateLenderLoans.scheduleExpected': '$ Attendu',
    'privateLenderLoans.scheduleExpectedBy': 'Attendu le',
    'privateLenderLoans.scheduleReceived': '$ Reçu',
    'privateLenderLoans.noSchedule': 'Aucune échéance.',
    'privateLenderLoans.schedulePeriod': 'Période',
    'interestReport.title': 'Rapport des intérêts reçus',
    'interestReport.back': 'Retour aux prêts',
    'interestReport.year': 'Année',
    'interestReport.loading': 'Chargement du rapport…',
    'interestReport.empty': 'Aucune donnée pour l\'année sélectionnée.',
    'interestReport.errorPermission': 'Vous n\'avez pas la permission de charger ce rapport. Vérifiez que vous êtes connecté en tant que prêteur.',
    'interestReport.errorPermissionTranches': 'Permission refusée lors du chargement des tranches de prêt. Vérifiez Firestore : les documents financedDeals doivent avoir lenderUid et ownerUid ; les financedDealTranches doivent avoir dealId pointant vers ce prêt.',
    'interestReport.errorPermissionPayments': 'Permission refusée lors du chargement des paiements d\'intérêts. Vérifiez Firestore : les documents interestPayments doivent avoir lenderUid égal à l\'id utilisateur du prêteur.',
    'interestReport.errorLoad': 'Impossible de charger le rapport. Veuillez réessayer.',
    'interestReport.colProperty': 'Propriété / Emprunteur',
    'interestReport.colCapital': 'Capital',
    'interestReport.colMonth': 'Mois',
    'interestReport.colExpected': 'Intérêt attendu',
    'interestReport.colReceived': 'Intérêt reçu',
    'interestReport.colStatus': 'Statut',
    'interestReport.colNote': 'Note disparité',
    'interestReport.status_pending': 'En attente',
    'interestReport.status_paid': 'Reçu',
    'interestReport.status_partial': 'Partiel',
    'interestReport.status_overpaid': 'Surapporté',
    'interestReport.showReport': 'Rapport des intérêts reçus',
    'lenderSettings.title': 'Paramètres de paiement',
    'lenderSettings.help': 'Optionnel. Utilisé pour la référence Interac (REF-XXX-YYYYYY) et l\'adresse de paiement.',
    'lenderSettings.fundCode': 'Code fonds',
    'lenderSettings.fundCodeHelp': 'Code court (ex. FND1, ABC). Utilisé dans la référence Interac pour chaque prêt.',
    'lenderSettings.paymentEmailPrefix': 'Courriel de paiement (préfixe)',
    'lenderSettings.paymentEmailHelp': 'Votre adresse de notifications de paiement sera prefix@rentalyzer.ca',
    'lenderSettings.paymentEmailPrefixTaken': 'Ce préfixe est déjà utilisé par quelqu\'un d\'autre.',
    'lenderSettings.autoMarkPayments': 'Marquer automatiquement les paiements reçus par courriel Interac (quand disponible)',
    'lenderSettings.moreInfo': 'Plus d\'information',
    'lenderSettings.autoMarkInfoModalTitle': 'Fonctionnement du marquage automatique des paiements Interac',
    'lenderSettings.autoMarkInfoModalIntro': 'Lorsque cette option est activée, Rentalyzer peut marquer les paiements d\'intérêts comme reçus automatiquement lorsqu\'un courriel de notification de virement Interac est reçu à votre adresse de paiement (préfixe@rentalyzer.ca).',
    'lenderSettings.autoMarkInfoModalStep1': 'Transmettez à chaque emprunteur la référence affichée dans le détail du prêt (REF-XXX-YYYYYY). L\'emprunteur doit inscrire cette référence exactement dans le message ou la note de son virement Interac.',
    'lenderSettings.autoMarkInfoModalStep2': 'Lorsque le courriel de notification arrive à votre adresse de paiement, notre système recherche la référence et le montant, identifie le prêt, puis crée ou met à jour le paiement d\'intérêts pour la période en cours.',
    'lenderSettings.autoMarkInfoModalStep3': 'Vous pouvez toujours marquer les paiements manuellement. Le marquage automatique est une commodité optionnelle.',
    'lenderSettings.save': 'Enregistrer',
    'lenderSettings.saving': 'Enregistrement…',
    'lenderSettings.saved': 'Paramètres enregistrés.',
    'dealPayment.directDepositTitle': 'Référence pour le dépôt direct des intérêts',
    'dealPayment.refInterac': 'Référence Interac',
    'dealPayment.refInteracHelp': 'Inscrivez cette référence exactement dans le message ou la note de votre virement Interac pour que le prêteur puisse associer le paiement.',
    'dealPayment.refInteracHelpLender': 'Transmettez cette référence à l\'emprunteur pour qu\'il l\'inscrive dans le message de son virement Interac.',
    'investorFunds.title': 'Mon placement',
    'investorFunds.empty': 'Aucun placement pour le moment.',
    'investorFunds.colPlacement': 'Date de début',
    'investorFunds.colInvested': 'Investi',
    'investorFunds.colTotalRepaid': 'Total remboursé',
    'investorFunds.colBalance': 'Solde',
    'investorFunds.colFundManager': 'Gestionnaire du fonds',
    'investorFunds.colAmountInvested': 'Montant investi',
    'investorFunds.colAverageYield': 'Rendement moyen (12 mo.)',
    'investorFunds.yieldNotAvailable': '—',
    'investorFunds.managePlacement': 'Gérer ce placement',
    'investorFunds.capitalInvestorOnlyMessage': 'Vous êtes connecté en tant qu\'investisseur de capitaux. Vos placements sont affichés ci-dessous.',
    'investorFunds.loading': 'Chargement…',
    'investorFunds.errorLoad': 'Impossible de charger les détails des placements.',
    'investorFunds.totalPlacements': 'Total des placements',
    'investorFunds.investingEntity': 'Investissement à titre',
    'investorFunds.investingIndividual': 'Personnel',
    'investorFunds.investingCompany': 'Société',
    'investorFunds.selectCompany': 'Choisir une société',
    'investorFunds.saveDefaultHolder': 'Enregistrer comme titulaire par défaut pour ce placement',
    'investorFunds.defaultHolderSaved': 'Titulaire par défaut enregistré.',
    'investorFunds.defaultHolderSavedReattest': 'Titulaire enregistré. Comme le titulaire a changé, veuillez refaire l’attestation d’investisseur qualifié.',
    'investorFunds.defaultHolderSavedLockedReminder':
      'Le choix « Investissement à titre » est maintenant figé pour ce placement de votre côté. Seul le gestionnaire du fonds peut le corriger si nécessaire.',
    'investorFunds.holderSaveIrreversibleWarning':
      'Après l’enregistrement, vous ne pourrez plus modifier vous-même « Investissement à titre ». Vérifiez qu’il correspond bien à la détention réelle avant de continuer.',
    'investorFunds.holderSaveConfirmCheckbox':
      'Je comprends qu’après l’enregistrement, je ne pourrai plus modifier ce choix moi-même pour ce placement.',
    'investorFunds.holderLockedReadOnlyHint':
      'Le titulaire de ce placement est enregistré et verrouillé de votre côté. Pour une correction, communiquez avec le gestionnaire du fonds.',
    'investorFunds.holderCommitAckRequired': 'Veuillez confirmer que vous comprenez que ce choix ne pourra pas être modifié après l’enregistrement.',
    'investorFunds.attestRequiresHolderSave':
      'Enregistrez d’abord « Investissement à titre » pour ce placement (avec confirmation), puis vous pourrez compléter l’attestation d’investisseur qualifié.',
    'investorFunds.qualifiedViaActivePlacementBody':
      'Ce placement est déjà actif dans le fonds (capital reçu). Vous n’avez pas à compléter l’autodéclaration d’investisseur qualifié dans l’application pour cette étape. Si le gestionnaire demande des pièces justificatives, elles apparaîtront aux étapes suivantes.',
    'investorFunds.noCompaniesHint': 'Ajoutez des sociétés dans Profil → Mes sociétés.',
    'investorFunds.openProfileCompaniesLink': 'Ouvrir le profil — Mes sociétés',
    'investorFunds.openProfileCompaniesHelp':
      'Votre placement reste ouvert derrière cette fenêtre. Quand vous avez terminé, fermez le profil et choisissez la société dans la liste ci-dessus.',
    'investorFunds.allocatedInterestTitle': 'Intérêts alloués (24 derniers mois)',
    'investorFunds.allocatedInterestEmpty': 'Aucun rendement calculé pour l’instant. Demandez au gestionnaire du fonds de cliquer sur « Mettre à jour rendements (mois) ».',
    'investorFunds.allocatedMonthCol': 'Mois',
    'investorFunds.allocatedInterestCol': 'Intérêts',
    'investorFunds.allocatedShareCol': 'Part',
    'investorFunds.openPeriodReport': 'Rapport mensuel du portefeuille',
    'investorFunds.reportNotPublished': 'Rapport non disponible pour l’instant',
    'capitalInvestors.recomputePeriodLabel': 'Période à traiter (mois civil)',
    'capitalInvestors.recomputeButton': 'Mettre à jour rendements (mois)',
    'capitalInvestors.recomputeProgress': 'Calcul en cours…',
    'capitalInvestors.recomputePeriodHelp':
      'En pratique le mois précédent après réception des paiements des emprunteurs (ex. en avril, choisir mars). Vous pouvez relancer le même mois si des paiements arrivent en retard.',
    'capitalInvestors.publishReportTitle': 'Rapport visible par les investisseurs',
    'capitalInvestors.publishReportHelp':
      'Après « Mettre à jour rendements », publiez lorsque vous souhaitez que les investisseurs de capitaux puissent ouvrir le rapport détaillé pour cette période.',
    'capitalInvestors.managerCommentLabel': 'Message aux investisseurs (optionnel)',
    'capitalInvestors.publishButton': 'Publier aux investisseurs',
    'capitalInvestors.unpublishButton': 'Retirer la publication',
    'capitalInvestors.reportPublished': 'Publié',
    'capitalInvestors.reportDraft': 'Non publié',
    'capitalInvestors.summaryMissing': 'Aucune donnée pour cette période — exécutez d’abord la mise à jour des rendements.',
    'fundPeriodReport.modalTitle': 'Rapport mensuel des revenus',
    'fundPeriodReport.close': 'Fermer',
    'fundPeriodReport.period': 'Période',
    'fundPeriodReport.loading': 'Chargement…',
    'fundPeriodReport.tabLoans': 'Portefeuille de prêts',
    'fundPeriodReport.tabOverview': 'Vue globale du fonds',
    'fundPeriodReport.legendRenewal': 'Renouvellement (nouvelle échéance)',
    'fundPeriodReport.legendMatured': 'Échéance dans la période — renouvellement non détecté',
    'fundPeriodReport.colBorrower': 'Emprunteur',
    'fundPeriodReport.colProperty': 'Propriété',
    'fundPeriodReport.colLoanDate': 'Date du prêt',
    'fundPeriodReport.colMaturity': 'Maturité',
    'fundPeriodReport.colPrincipal': 'Montant prêté',
    'fundPeriodReport.colRate': 'Taux',
    'fundPeriodReport.colDividends': 'Dividendes',
    'fundPeriodReport.colInterest': 'Intérêts reçus',
    'fundPeriodReport.colQuota': 'Quote-part gestionnaire',
    'fundPeriodReport.colPaidInvestors': 'Payé aux investisseurs',
    'fundPeriodReport.totals': 'Totaux',
    'fundPeriodReport.emptyLoans': 'Aucun prêt déboursé ne chevauche ce mois.',
    'fundPeriodReport.quotaNote': 'La quote-part gestionnaire est de {pct} % des intérêts reçus (réglable dans les paramètres de paiement des prêts).',
    'fundPeriodReport.outstandingTitle': 'Total des prêts en cours (fin de période)',
    'fundPeriodReport.outstandingHint': 'Somme des capitaux pour les prêts encore actifs le dernier jour du mois.',
    'fundPeriodReport.fundMovementsTitle': 'Mouvements du capital (commanditaires)',
    'fundPeriodReport.capitalPrev': 'Capital en fin de période précédente',
    'fundPeriodReport.capitalAdded': 'Capital additionnel pendant la période',
    'fundPeriodReport.capitalAddedTooltip':
      'Les rendements sur les entrées de capital en cours de mois sont répartis au prorata du nombre de jours dans le fonds (même logique que le calcul des parts).',
    'fundPeriodReport.prorataHint': 'au prorata des jours',
    'fundPeriodReport.capitalRepaid': 'Remboursements de fonds aux commanditaires',
    'fundPeriodReport.capitalEnd': 'Capital total en fin de période',
    'fundPeriodReport.upcomingTitle': 'À venir (indicatif)',
    'fundPeriodReport.upcomingAdditions': 'Capital additionnel à recevoir (propositions)',
    'fundPeriodReport.upcomingWithdrawals': 'Demandes de rappel de fonds en attente',
    'fundPeriodReport.upcomingFootnote':
      'Les retraits ne sont pas encore liés à une période cible dans l’application ; les montants correspondent à toutes les demandes en attente. Une mécanique dédiée pourra affiner cela.',
    'fundPeriodReport.managerComment': 'Message du gestionnaire de fonds',
    'lenderSettings.fundManagerQuotaPct': 'Quote-part gestionnaire sur les intérêts (%)',
    'lenderSettings.fundManagerQuotaPctHelp':
      'Appliquée aux intérêts reçus dans le rapport mensuel investisseurs (ex. 20 %). Défaut 20 %.',
    'lenderSettings.interestPeriodAnchorDay': 'Les périodes d’intérêts commencent le jour du mois',
    'lenderSettings.interestPeriodAnchorDayHelp':
      '1 = mois civil. Ex. 15 : du 15 au 14 du mois suivant (ancre mi-mois). Maximum 28.',
    'lenderSettings.fundInConfiguration': 'Fonds en configuration (pas d’invitations automatiques)',
    'lenderSettings.fundInConfigurationHelp':
      'Si activé, l’ajout d’un emprunteur ou d’un investisseur ne met pas de courriel d’invitation en file. Vous pouvez toujours renvoyer manuellement.',
    'investorFunds.modalTabOverview': 'Aperçu',
    'investorFunds.modalTabProfile': 'Titulaire & banque',
    'investorFunds.holderBankingStepAttestation': 'Attestation d’investisseur qualifié',
    'investorFunds.holderBankingEditHolder': 'Modifier',
    'investorFunds.holderBankingContractUnlockHint':
      'Ouvrez le PDF du contrat ou générez un résumé IA pour débloquer le téléversement du spécimen de chèque ci-dessous.',
    'investorFunds.modalTabRequests': 'Demandes',
    'investorFunds.requestsNeedAttestation':
      'Complétez d’abord l’attestation d’investisseur qualifié dans l’onglet « Titulaire et banque » avant de demander des retraits ou des ajouts de capital.',
    'investorFunds.contractSectionTitle': 'Contrat de placement',
    'investorFunds.contractNoFile': 'Aucun document de contrat n’est disponible pour ce placement.',
    'investorFunds.contractSummaryEmptyHint':
      'Générez un résumé IA du PDF du contrat (une fois). Il sera conservé pour vos prochaines visites.',
    'fundInvestors.investingHolder': 'Titulaire',
    'fundInvestors.investingPersonal': 'Personnel',
    'companies.title': 'Mes sociétés',
    'companies.hint': 'Ajoutez les personnes morales par lesquelles vous investissez. Vous pourrez choisir le personnel ou une société pour chaque placement et transaction.',
    'companies.legalName': 'Raison sociale',
    'companies.address': 'Adresse de l’entreprise',
    'companies.addressAutocompleteHint': 'Commencez à taper — les suggestions s’affichent tout de suite.',
    'companies.addressFirstHint':
      'Choisissez une adresse dans les suggestions Google ci-dessous ; les champs d’enregistrement n’apparaissent qu’après une sélection.',
    'companies.regNumbersIntro': 'Choisissez la province ou le territoire pour afficher le bon libellé du numéro provincial. Les deux champs sont facultatifs.',
    'companies.province': 'Province ou territoire',
    'companies.provincePlaceholder': 'Choisir…',
    'companies.federalReg': 'Numéro d’entreprise fédéral (NE / BN — ARC)',
    'companies.provincialReg.default': 'Numéro d’enregistrement provincial ou territorial',
    'companies.provincialReg.AB': 'Numéro d’accès aux sociétés (Alberta)',
    'companies.provincialReg.BC': 'Numéro d’entreprise ou d’incorporation (C.-B.)',
    'companies.provincialReg.MB': 'Numéro d’enregistrement d’entreprise (Manitoba)',
    'companies.provincialReg.NB': 'Numéro d’entreprise (N.-B.)',
    'companies.provincialReg.NL': 'Numéro du registre (T.-N.-L.)',
    'companies.provincialReg.NS': 'Numéro du registre des sociétés (N.-É.)',
    'companies.provincialReg.NT': 'Registre des entreprises (T. N.-O.)',
    'companies.provincialReg.NU': 'Numéro du registre des entreprises (Nunavut)',
    'companies.provincialReg.ON': 'Numéro d’identification d’entreprise de l’Ontario (BIN)',
    'companies.provincialReg.PE': 'Numéro d’enregistrement d’entreprise (Î.-P.-É.)',
    'companies.provincialReg.QC': 'NEQ — Numéro d’entreprise du Québec',
    'companies.provincialReg.SK': 'Numéro du registre des sociétés (Saskatchewan)',
    'companies.provincialReg.YT': 'Numéro du registre des entreprises (Yukon)',
    'companies.listFederal': 'Fédéral',
    'companies.listProvincial': 'Provincial',
    'companies.region.AB': 'Alberta',
    'companies.region.BC': 'Colombie-Britannique',
    'companies.region.MB': 'Manitoba',
    'companies.region.NB': 'Nouveau-Brunswick',
    'companies.region.NL': 'Terre-Neuve-et-Labrador',
    'companies.region.NS': 'Nouvelle-Écosse',
    'companies.region.NT': 'Territoires du Nord-Ouest',
    'companies.region.NU': 'Nunavut',
    'companies.region.ON': 'Ontario',
    'companies.region.PE': 'Île-du-Prince-Édouard',
    'companies.region.QC': 'Québec',
    'companies.region.SK': 'Saskatchewan',
    'companies.region.YT': 'Yukon',
    'companies.add': 'Ajouter la société',
    'companies.save': 'Enregistrer',
    'companies.edit': 'Modifier',
    'companies.delete': 'Supprimer',
    'companies.deleteConfirm': 'Supprimer cette société ?',
    'companies.saveError': 'Enregistrement impossible.',
    'companies.deleteError': 'Suppression impossible.',
    'fundInvestors.empty': 'Aucun investisseur de capitaux pour le moment. Invitez-en un pour commencer.',
    'fundInvestors.invite.button': 'Inviter un investisseur',
    'fundInvestors.invite.title': 'Inviter un investisseur de capitaux',
    'fundInvestors.invite.email': 'Courriel',
    'fundInvestors.invite.displayName': 'Nom affiché',
    'fundInvestors.invite.displayNamePlaceholder': 'Nom de l\'investisseur',
    'fundInvestors.invite.investedAmount': 'Montant investi ($)',
    'fundInvestors.invite.interestStartDate': 'Date de début des intérêts',
    'fundInvestors.invite.contractPdf': 'Contrat (PDF, optionnel)',
    'fundInvestors.invite.submit': 'Créer l\'invitation',
    'fundInvestors.invite.sending': 'Création…',
    'fundInvestors.invite.successMessage': 'Invitation créée. L\'envoi par courriel sera disponible prochainement.',
    'fundInvestors.invite.skippedEmailFundConfig':
      'Fiche investisseur créée. Aucun courriel d’invitation (fonds en configuration).',
    'fundInvestors.fundInConfigurationBanner':
      'Fonds en configuration : les courriels d’invitation ne partent pas automatiquement à l’ajout d’investisseurs ou d’emprunteurs. Vous pouvez toujours renvoyer manuellement lorsque l’option est disponible.',
    'fundInvestors.invite.errorEmail': 'Veuillez entrer une adresse courriel valide.',
    'fundInvestors.invite.errorAmount': 'Veuillez entrer un montant valide.',
    'fundInvestors.invite.errorGeneric': 'Une erreur s\'est produite. Veuillez réessayer.',
    'fundInvestors.legacyImport.button': 'Enregistrer un placement actif',
    'fundInvestors.legacyImport.title': 'Ajouter un investisseur historique',
    'fundInvestors.legacyImport.disclaimer':
      'Pour un investisseur déjà en place dans votre fonds. L\'onboarding est marqué comme complété; les périodes d\'intérêts passées ne sont pas reconstituées dans Rentalyzer. Les allocations portent sur les données saisies à partir d\'ici.',
    'fundInvestors.legacyImport.tranchesTitle': 'Tranches de capital',
    'fundInvestors.legacyImport.addTranche': 'Ajouter une tranche',
    'fundInvestors.legacyImport.trancheAmount': 'Montant ($)',
    'fundInvestors.legacyImport.trancheNote': 'Note',
    'fundInvestors.legacyImport.sendInvitation': 'Envoyer le courriel d\'invitation (optionnel)',
    'fundInvestors.legacyImport.sendInvitationDisabledFundConfig': 'désactivé en mode configuration',
    'fundInvestors.legacyImport.submit': 'Créer le placement',
    'fundInvestors.legacyImport.successMessage': 'Placement historique créé.',
    'fundInvestors.legacyImport.errorTranche': 'Chaque ligne remplie nécessite un montant positif et une date de début.',
    'fundInvestors.legacyImport.errorNeedTranche': 'Ajoutez au moins une tranche avec montant et date.',
    'fundInvestors.legacyImport.badge': 'Placement actif',
    'fundInvestors.legacyImport.badgeHint':
      'Importé pour le suivi; l\'historique d\'intérêts dans Rentalyzer peut être incomplet.',
    'fundInvestors.legacyImport.modalNote':
      'Ce placement a été importé comme déjà actif. Les mouvements ci-dessous peuvent omettre d\'anciennes tranches; les poids de capital pour les allocations utilisent les enregistrements de tranches.',
    'fundInvestors.legacyImport.listStatusNote':
      'Dates de contrat et de dépôt des fonds : non suivies dans l’app pour ce placement (hors parcours d’invitation).',
    'fundInvestors.fileImport.button': 'Importer depuis un fichier',
    'fundInvestors.fileImport.title': 'Importer des investisseurs de capitaux depuis un fichier',
    'fundInvestors.fileImport.hint':
      'Téléversez un fichier CSV ou Excel (.csv, .xlsx, .xls). Nous extrayons les noms, les courriels (s’ils figurent), chaque tranche de capital et ses dates. Vérifiez l’aperçu avant de créer les placements.',
    'fundInvestors.fileImport.templateRecommended':
      'Le téléchargement du fichier exemple Excel (.xlsx) est recommandé (facultatif). Votre propre export peut suffire si le tableau est lisible.',
    'fundInvestors.fileImport.downloadTemplate': 'Télécharger un exemple (Excel .xlsx)',
    'fundInvestors.fileImport.expectedTitle': 'Données recherchées',
    'fundInvestors.fileImport.expectedName': 'Nom ou libellé de l’investisseur',
    'fundInvestors.fileImport.expectedEmail':
      'Courriel (recommandé — obligatoire pour créer la fiche dans l’application)',
    'fundInvestors.fileImport.expectedIdentifier':
      'Identifiant interne ou numéro de compte si aucun courriel dans le fichier (vous pouvez saisir le courriel dans l’aperçu)',
    'fundInvestors.fileImport.expectedTranches': 'Chaque apport de capital sur une ligne avec montant',
    'fundInvestors.fileImport.expectedDates': 'Date de début ou d’effet par tranche (AAAA-MM-JJ)',
    'fundInvestors.fileImport.expectedKind':
      'Indication « souscription initiale » vs « apport ultérieur » lorsque le document le précise (sinon les tranches sont conservées par date ; la plus ancienne devient la tranche initiale)',
    'fundInvestors.fileImport.analyzing': 'Analyse en cours…',
    'fundInvestors.fileImport.chooseFile': 'Choisir un fichier',
    'fundInvestors.fileImport.previewHelp':
      'Décochez les lignes à exclure. Corrigez les courriels manquants avant de valider. Les lignes dont le courriel existe déjà dans le fonds sont ignorées.',
    'fundInvestors.fileImport.previewTotalAll': 'Total des fonds (toutes les lignes)',
    'fundInvestors.fileImport.previewTotalSelected': 'Total des fonds (lignes cochées seulement)',
    'fundInvestors.fileImport.colDisplayName': 'Nom affiché',
    'fundInvestors.fileImport.colTotal': 'Total',
    'fundInvestors.fileImport.colTranches': 'Tranches',
    'fundInvestors.fileImport.colStatus': 'Statut',
    'fundInvestors.fileImport.emailPlaceholder': 'nom@exemple.com',
    'fundInvestors.fileImport.duplicateEmail': 'Déjà dans le fonds',
    'fundInvestors.fileImport.badEmail': 'Courriel invalide',
    'fundInvestors.fileImport.noTranches': 'Aucune tranche',
    'fundInvestors.fileImport.trancheInitial': 'initiale',
    'fundInvestors.fileImport.trancheAddition': 'ajout',
    'fundInvestors.fileImport.trancheUnspecified': 'non précisé',
    'fundInvestors.fileImport.back': 'Retour',
    'fundInvestors.fileImport.commit': 'Créer les placements sélectionnés',
    'fundInvestors.fileImport.noRowsAfterMerge': 'Aucun investisseur avec des tranches valides.',
    'fundInvestors.fileImport.partialSummary':
      '{ok} placement(s) créé(s). {skip} doublon(s) ignoré(s). {bad} ligne(s) en échec.',
    'fundInvestors.fileImport.successCount': '{n} placement(s) créé(s).',
    'fundInvestors.fileImport.onlyDuplicates': 'Aucun nouveau placement — tous les courriels sélectionnés existent déjà.',
    'fundInvestors.fileImport.nothingCreated': 'Création impossible. Vérifiez les courriels et les tranches.',
    'fundInvestors.fileImport.noSelection': 'Aucune sélection à importer.',
    'fundInvestors.statusInvited': 'Invité',
    'fundInvestors.statusInvitationAccepted': 'Invitation acceptée',
    'fundInvestors.statusSigned': 'Contrat signé',
    'fundInvestors.statusFundsReceived': 'Fonds reçus',
    'fundInvestors.table.email': 'Investisseur',
    'fundInvestors.table.amount': 'Montant',
    'fundInvestors.table.startDate': 'Date de début',
    'fundInvestors.table.status': 'Statut',
    'fundInvestors.table.actions': 'Actions',
    'fundInvestors.actions.markSigned': 'Marquer contrat signé',
    'fundInvestors.actions.markSignedExplainer':
      'Confirme l’étape contrat dans l’app (p. ex. contrat papier ou avant le téléversement du PDF final). Si vous téléversez le PDF entièrement signé dans Gérer l’investisseur, le statut est mis à jour automatiquement.',
    'fundInvestors.actions.markSignedRedundantHint':
      'Un PDF de contrat entièrement signé est déjà en place — le statut devrait passer à « Contrat signé » automatiquement après actualisation.',
    'fundInvestors.actions.markSignedSyncStuck':
      'Finaliser : le dossier est encore à « Invitation acceptée » alors que le PDF entièrement signé est en place — cliquez pour passer le statut à Contrat signé.',
    'fundInvestors.onboarding.stepAttestation': 'titulaire et attestation investisseur qualifié',
    'fundInvestors.onboarding.stepQualDocsInvestor': 'justificatifs (en attente de l’investisseur)',
    'fundInvestors.onboarding.stepQualDocsReview': 'justificatifs (examen de votre part)',
    'fundInvestors.onboarding.stepContractInvestorSigned': 'contrat — PDF signé par l’investisseur',
    'fundInvestors.onboarding.stepContractFullyExecuted': 'contrat — téléverser le PDF entièrement signé',
    'fundInvestors.onboarding.stepCheque': 'spécimen de chèque / coordonnées bancaires',
    'fundInvestors.onboarding.stepConfirmContractStatus':
      'Étape suivante : cliquez sur « Marquer contrat signé » lorsque l’étape du contrat de placement est complétée (p. ex. aucun fichier dans l’app, ou suivi des fonds en dehors de ces téléversements).',
    'fundInvestors.actions.markFundsReceived': 'Marquer fonds reçus',
    'fundInvestors.signedAt': 'Signé le',
    'fundInvestors.fundsReceivedAt': 'Fonds reçus le',
    'fundInvestors.resendInvitation': 'Renvoyer l\'invitation',
    'fundInvestors.resendSending': 'Envoi…',
    'fundInvestors.resendSuccess': 'Envoyé!',
    'fundInvestors.bulkInviteAllSummary':
      '{n} investisseur(s) n’ont pas encore relié leur compte Rentalyzer (y compris placements importés) — vous pouvez mettre tous les courriels d’invitation en file d’attente.',
    'fundInvestors.bulkInviteAllButton': 'Inviter tout le monde en attente (courriel)',
    'fundInvestors.bulkInviteAllConfirm':
      'Envoyer les courriels d’invitation à tous les investisseurs qui n’ont pas encore relié leur compte ? Chacun recevra un nouveau lien d’inscription.',
    'fundInvestors.bulkInviteAllSending': 'Envoi…',
    'fundInvestors.bulkInviteAllResultOk': '{n} courriel(s) d’invitation mis en file d’attente.',
    'fundInvestors.bulkInviteAllResultPartial':
      '{ok} invitation(s) mise(s) en file, {failed} échec(s) — vérifiez la console ou réessayez pour ces lignes.',
    'fundInvestors.manageRepayments': 'Gérer les remboursements',
    'fundInvestors.manageCapitalInvestor': 'Gérer cet investisseur',
    'fundInvestors.lenderModalTabOverview': 'Aperçu',
    'fundInvestors.lenderModalTabDocuments': 'Documents & banque',
    'fundInvestors.lenderModalTabRequests': 'Demandes',
    'fundInvestors.lenderModalInvestorInfo': 'Fiche investisseur',
    'fundInvestors.lenderModalContract': 'Contrat de placement',
    'fundInvestors.statusWithdrawalRequest': 'Demande de retrait',
    'fundInvestors.statusCapitalAdditionRequest': 'Demande d\'ajout de capital',
    'fundInvestors.transaction.title': 'Information sur la transaction',
    'fundInvestors.transaction.date': 'Date',
    'fundInvestors.transaction.amount': 'Montant',
    'fundInvestors.transaction.submit': 'Confirmer',
    'fundInvestors.transaction.submitting': 'Enregistrement…',
    'fundInvestors.transaction.noFutureDate': 'La date ne peut pas être dans le futur.',
    'fundInvestors.transaction.errorDate': 'Veuillez entrer une date.',
    'fundInvestors.transaction.confirmReceipt': 'Confirmer la réception des fonds',
    'fundInvestors.transaction.receivedOn': 'Reçu le',
    'fundInvestors.fundMovements.title': 'Mouvements de fonds',
    'fundInvestors.fundMovements.empty': 'Aucun mouvement pour le moment.',
    'fundInvestors.fundMovements.date': 'Date',
    'fundInvestors.fundMovements.amount': 'Montant',
    'fundInvestors.fundMovements.type': 'Type',
    'fundInvestors.fundMovements.typeWithdrawal': 'Retrait',
    'fundInvestors.fundMovements.typeCapital': 'Ajout de capital',
    'fundInvestors.fundMovements.typeInitialSubscription': 'Souscription initiale',
    'fundInvestors.chequeSpecimen.title': 'Spécimen de chèque (annulé)',
    'fundInvestors.chequeSpecimen.hint': 'Téléversez un chèque annulé ou un spécimen bancaire (PDF ou image) pour que le gestionnaire du fonds puisse déposer les intérêts sur votre compte.',
    'fundInvestors.chequeSpecimen.upload': 'Téléverser / remplacer',
    'fundInvestors.chequeSpecimen.uploading': 'Téléversement…',
    'fundInvestors.chequeSpecimen.currentFile': 'Fichier actuel',
    'fundInvestors.chequeSpecimen.open': 'Ouvrir',
    'fundInvestors.chequeSpecimen.updatedAt': 'Dernière mise à jour',
    'fundInvestors.chequeSpecimen.none': 'Aucun spécimen enregistré pour le moment.',
    'fundInvestors.chequeSpecimen.uploadSuccess': 'Spécimen enregistré. Le gestionnaire du fonds en sera informé.',
    'fundInvestors.chequeSpecimen.uploadError': 'Échec du téléversement. Veuillez réessayer.',
    'fundInvestors.chequeSpecimen.pendingBadge': 'Spécimen chèque mis à jour',
    'fundInvestors.chequeSpecimen.lenderSection': 'Spécimen de chèque (investisseur)',
    'fundInvestors.chequeSpecimen.acknowledge': 'Marquer comme consulté',
    'fundInvestors.chequeSpecimen.acknowledging': 'Enregistrement…',
    'fundInvestors.contractSigned.investorWorkflowHint':
      'Téléchargez le gabarit du contrat, signez-le, puis téléversez votre PDF signé ici. Une fois le gestionnaire du fonds contre-signé et le PDF final téléversé, chaque partie pourra ouvrir la version définitive.',
    'fundInvestors.contractSigned.fullyExecutedAvailable': 'Contrat entièrement signé disponible',
    'fundInvestors.contractSigned.downloadFullyExecuted': 'Ouvrir le contrat entièrement signé (PDF)',
    'fundInvestors.contractSigned.templateLabel': 'Gabarit du contrat (non signé)',
    'fundInvestors.contractSigned.uploadSignedLabel': 'Téléverser le PDF signé (votre signature)',
    'fundInvestors.contractSigned.replaceSignedUpload': 'Remplacer le PDF signé',
    'fundInvestors.contractSigned.openMySigned': 'Ouvrir ma copie signée',
    'fundInvestors.contractSigned.uploading': 'Téléversement…',
    'fundInvestors.contractSigned.waitForManager':
      'Après votre téléversement, le gestionnaire du fonds examinera, signera et téléversera le contrat finalisé. Vous pourrez ensuite ouvrir le PDF entièrement signé.',
    'fundInvestors.contractSigned.pdfOnly': 'Veuillez choisir un fichier PDF.',
    'fundInvestors.contractSigned.uploadSuccess': 'Contrat signé téléversé. Le gestionnaire du fonds en a été informé.',
    'fundInvestors.contractSigned.uploadError': 'Échec du téléversement. Veuillez réessayer.',
    'fundInvestors.contractSigned.fileTooLarge': 'Le fichier est trop volumineux (max 20 Mo).',
    'fundInvestors.contractSigned.lenderWorkflowHint':
      'Ouvrez le PDF signé par l’investisseur, complétez votre signature si requis, puis téléversez le PDF entièrement signé afin que les deux parties y aient accès dans le dossier du placement.',
    'fundInvestors.contractSigned.pendingInvestorSignedBadge': 'Contrat signé (investisseur)',
    'fundInvestors.contractSigned.pendingInvestorSignedNotice':
      'L’investisseur a téléversé une copie signée — ouvrez-la, contre-signez si applicable, puis téléversez le PDF entièrement signé.',
    'fundInvestors.contractSigned.lenderBlockMarkSignedNeedInvestorSigned':
      'Attendez que l’investisseur téléverse son contrat signé (PDF) avant de marquer le contrat comme signé.',
    'fundInvestors.contractSigned.investorSignedBlockTitle': 'Copie signée par l’investisseur',
    'fundInvestors.contractSigned.noInvestorSignedYet':
      'Aucun PDF signé par l’investisseur pour le moment. L’investisseur doit téléverser sa copie signée avant la finalisation.',
    'fundInvestors.contractSigned.openInvestorSigned': 'Ouvrir le PDF signé par l’investisseur',
    'fundInvestors.contractSigned.investorSignedUploadedAt': 'Téléversé par l’investisseur le',
    'fundInvestors.contractSigned.fullyExecutedBlockTitle': 'Contrat entièrement signé (toutes les signatures)',
    'fundInvestors.contractSigned.fullyExecutedHint':
      'Téléversez le PDF final une fois toutes les parties signataires. Cela remplace tout fichier entièrement signé précédent.',
    'fundInvestors.contractSigned.fullyExecutedUploadedAt': 'Téléversé le',
    'fundInvestors.contractSigned.openFullyExecuted': 'Ouvrir le PDF entièrement signé',
    'fundInvestors.contractSigned.uploadFullyExecutedLabel': 'Téléverser le PDF entièrement signé',
    'fundInvestors.contractSigned.replaceFullyExecutedUpload': 'Remplacer le PDF entièrement signé',
    'fundInvestors.contractSigned.lenderUploadFullyExecutedSuccess':
      'Contrat entièrement signé enregistré. L’investisseur peut maintenant l’ouvrir depuis son placement.',
    'fundInvestors.qualified.title': 'Attestation d’investisseur qualifié requise',
    'fundInvestors.qualified.intro':
      'Avant d’utiliser vos placements, confirmez comment vous vous qualifiez aux termes des règles canadiennes sur les placements privés (p. ex. NI 45-106). Il s’agit d’une autodéclaration dans l’application — cela ne remplace pas un avis juridique ni le processus de conformité de votre gestionnaire.',
    'fundInvestors.qualified.placementsLabel': 'Placements en attente d’attestation',
    'fundInvestors.qualified.categoryLabel': 'Je me qualifie comme (catégorie déclarée)',
    'fundInvestors.qualified.categoryAccredited': 'Investisseur accrédité',
    'fundInvestors.qualified.categoryEligible': 'Investisseur admissible',
    'fundInvestors.qualified.categoryInstitution':
      'Personne non physique / institution (selon les exemptions applicables)',
    'fundInvestors.qualified.ni45106Title': 'NI 45-106 (exemption de prospectus) — liens vers l’information officielle',
    'fundInvestors.qualified.ni45106Intro':
      'Consultez le texte officiel ci-dessous avant de soumettre votre attestation. Il s’agit d’information générale et non d’un avis juridique.',
    'fundInvestors.qualified.ni45106Fr': 'Lire la NI 45-106 (français, officiel)',
    'fundInvestors.qualified.ni45106En': 'Lire la NI 45-106 (anglais, officiel)',
    'fundInvestors.qualified.categoryExplanationTitle': 'Quelle est la différence entre ces catégories ?',
    'fundInvestors.qualified.categoryExplanationAccredited':
      'Investisseur accrédité : vous confirmez que vous répondez à des critères généralement fondés sur le revenu/la valeur nette, ce qui peut permettre de participer en vertu d’exemptions de prospectus applicables.',
    'fundInvestors.qualified.categoryExplanationEligible':
      'Investisseur admissible : vous confirmez que vous répondez aux critères spécifiques « investisseur admissible » prévus dans les règles canadiennes sur les placements privés (p. ex. comment l’exemption s’applique).',
    'fundInvestors.qualified.categoryExplanationInstitution':
      'Personne non physique / institution : vous confirmez que l’investisseur est une société ou une institution pouvant se qualifier selon les exemptions applicables (et non comme personne physique).',
    'fundInvestors.qualificationDocs.title': 'Checklist de pièces de qualification (NI 45-106)',
    'fundInvestors.qualificationDocs.intro':
      'Si votre prêteur privé a besoin de preuves pour valider votre catégorie d’investisseur qualifié, il peut vous demander des documents justificatifs ici. Téléversez les pièces ci-dessous, puis soumettez-les lorsque prêt.',
    'fundInvestors.qualificationDocs.requestedCategory': 'Catégorie demandée',
    'fundInvestors.qualificationDocs.status_not_requested': 'Aucune demande de documents.',
    'fundInvestors.qualificationDocs.status_requested': 'Demande envoyée. Veuillez téléverser les documents ci-dessous.',
    'fundInvestors.qualificationDocs.status_submitted': 'Pièces soumises. Votre prêteur les examinera.',
    'fundInvestors.qualificationDocs.status_reviewed': 'Pièces revues.',
    'fundInvestors.qualificationDocs.requestButton': 'Demander des pièces justificatives',
    'fundInvestors.qualificationDocs.waitForAttestation': 'Complétez d’abord l’attestation d’investisseur qualifié.',
    'fundInvestors.qualificationDocs.open': 'Ouvrir',
    'fundInvestors.qualificationDocs.notUploaded': 'Non fourni pour le moment.',
    'fundInvestors.qualificationDocs.uploadLabel': 'Téléverser un document',
    'fundInvestors.qualificationDocs.replaceLabel': 'Remplacer le document',
    'fundInvestors.qualificationDocs.uploading': 'Téléversement…',
    'fundInvestors.qualificationDocs.fileOnFile': 'Fichier enregistré',
    'fundInvestors.qualificationDocs.filesOnRecord': 'Fichiers enregistrés',
    'fundInvestors.qualificationDocs.uploadSuccess': 'Document téléversé.',
    'fundInvestors.qualificationDocs.uploadError': 'Échec du téléversement. Veuillez réessayer.',
    'fundInvestors.qualificationDocs.submitButton': 'Soumettre la checklist',
    'fundInvestors.qualificationDocs.submitMissing': 'Veuillez téléverser tous les documents requis avant de soumettre.',
    'fundInvestors.qualificationDocs.markReviewed': 'Marquer comme revu',
    'fundInvestors.qualificationDocs.requestError': 'Impossible de demander la checklist. Veuillez réessayer.',
    'fundInvestors.qualificationDocs.reviewError': 'Impossible de marquer comme revu. Veuillez réessayer.',
    'fundInvestors.qualificationDocs.noCategoryError': 'Aucune catégorie trouvée pour cet investisseur.',
    'fundInvestors.qualificationDocs.reviewedBadge': 'Revu',
    'fundInvestors.qualificationDocs.itemProofIncome': 'Preuve de revenu (p. ex. déclarations / relevés)',
    'fundInvestors.qualificationDocs.itemProofNetWorth': 'Preuve de valeur nette (p. ex. état de la valeur nette)',
    'fundInvestors.qualificationDocs.itemProofEligible': 'Preuve d’admissibilité (p. ex. calculs / pièces justificatives)',
    'fundInvestors.qualificationDocs.itemInstitutionDocs': 'Documents de l’institution',
    'fundInvestors.qualificationDocs.itemAuthorizationDocs': 'Résolution / autorisation',
    'fundInvestors.qualified.checkboxQualification':
      'Je confirme satisfaire aux critères de la catégorie choisie ci-dessus (au sens des lois canadiennes sur les valeurs mobilières) relativement à cet investissement.',
    'fundInvestors.qualified.checkboxNotAdvice':
      'Je comprends que Rentalyzer ne fournit pas d’avis juridique, réglementaire ou en placement.',
    'fundInvestors.qualified.disclaimer':
      'En cas de doute, consultez un professionnel compétent avant d’investir. Votre gestionnaire peut demander des pièces justificatives.',
    'fundInvestors.qualified.submit': 'Soumettre l’attestation pour tous les placements listés',
    'fundInvestors.qualified.submitSinglePlacement': 'Soumettre l’attestation pour ce placement',
    'fundInvestors.qualified.submitting': 'Envoi…',
    'fundInvestors.qualified.errorConfirm': 'Cochez toutes les cases pour continuer.',
    'fundInvestors.qualified.errorGeneric': 'Impossible d’enregistrer l’attestation. Réessayez.',
    'fundInvestors.qualified.errorEmailMismatch':
      'Le courriel de votre compte doit correspondre à celui de l’invitation. Connectez-vous avec l’adresse invitée ou contactez votre gestionnaire.',
    'fundInvestors.qualified.completeFirst': 'Complétez d’abord l’attestation d’investisseur qualifié dans l’onglet « Titulaire et banque ».',
    'fundInvestors.qualified.attestationInModalHint':
      'Pour chaque placement, ouvrez Gérer → Titulaire et banque : indiquez qui détient l’investissement (particulier ou société), enregistrez, puis complétez l’attestation d’investisseur qualifié avant d’utiliser les demandes de retrait ou de capital.',
    'fundInvestors.qualified.holderLockedHint':
      'Le détenteur de l’investissement ne peut plus être modifié après votre attestation (déclarations fiscales). Pour utiliser une autre entité, le gestionnaire doit clôturer ce placement et en créer un nouveau.',
    'fundInvestors.qualified.holderChangeResetsAttestationHint':
      'Vous pouvez modifier le titulaire de cet investissement. Si vous enregistrez un titulaire différent, vous devrez refaire l’attestation d’investisseur qualifié.',
    'fundInvestors.qualified.saveCompanyBeforeAttest':
      'Enregistrez la société comme détenteur pour ce placement avant d’attester. Si l’écran ne correspond pas à ce qui est enregistré, cliquez sur Enregistrer.',
    'fundInvestors.qualified.syncHolderBeforeAttest':
      'Vos choix ne correspondent pas au détenteur enregistré. Utilisez le bouton d’enregistrement du titulaire pour ce placement avant d’attester.',
    'fundInvestors.qualified.attestedSummaryTitle': 'Attestation investisseur qualifié',
    'fundInvestors.qualified.attestedOn': 'Autodéclaration le',
    'fundInvestors.qualified.errorHolderCompanyRequired':
      'Ce placement est configuré en société mais aucune société n’est enregistrée. Choisissez une société et enregistrez avant d’attester.',
    'fundInvestors.qualified.lenderPendingBadge': 'Qualification en attente',
    'fundInvestors.qualified.lenderFieldLabel': 'Attestation investisseur qualifié',
    'fundInvestors.qualified.lenderPending': 'Pas encore complétée par l’investisseur.',
    'fundInvestors.qualified.lenderAttested': 'Autodéclaration le',
    'fundInvestors.qualified.lenderSatisfiedViaFundsReceived':
      'Exigence satisfaite — placement marqué « fonds reçus » (aucune autodéclaration dans l’application).',
    'fundInvestors.qualified.lenderCategoryAccredited': 'Accrédité',
    'fundInvestors.qualified.lenderCategoryEligible': 'Admissible',
    'fundInvestors.qualified.lenderCategoryInstitution': 'Institution',
    'fundInvestors.qualified.lenderBlockMarkSignedNeedLink':
      'L’investisseur doit accepter l’invitation et lier son compte avant que vous puissiez marquer le contrat comme signé.',
    'fundInvestors.qualified.lenderBlockMarkSignedNeedAttestation':
      'L’investisseur doit compléter l’attestation d’investisseur qualifié avant que vous puissiez marquer le contrat comme signé.',
    'fundInvestors.repayments.title': 'Remboursements de capital',
    'fundInvestors.repayments.invested': 'Investi',
    'fundInvestors.repayments.investedInitially': 'Invest. Init.',
    'fundInvestors.repayments.capitalAdded': 'Cap. Ajouté',
    'fundInvestors.repayments.totalRepaid': 'Cap Remboursé',
    'fundInvestors.repayments.balance': 'Solde',
    'fundInvestors.repayments.listTitle': 'Historique des remboursements',
    'fundInvestors.repayments.empty': 'Aucun remboursement pour le moment.',
    'fundInvestors.repayments.date': 'Date',
    'fundInvestors.repayments.amount': 'Montant',
    'fundInvestors.repayments.type': 'Type',
    'fundInvestors.repayments.typePartial': 'Partiel',
    'fundInvestors.repayments.typeFull': 'Total',
    'fundInvestors.repayments.note': 'Note',
    'fundInvestors.repayments.notePlaceholder': 'Note optionnelle',
    'fundInvestors.repayments.addTitle': 'Ajouter un remboursement',
    'fundInvestors.repayments.add': 'Ajouter le remboursement',
    'fundInvestors.repayments.adding': 'Ajout…',
    'fundInvestors.repayments.errorAmount': 'Veuillez entrer un montant valide.',
    'withdrawalRequests.title': 'Demandes de rappel de fonds',
    'withdrawalRequests.empty': 'Aucune demande de rappel.',
    'withdrawalRequests.requestButton': 'Demander le rappel de mes fonds',
    'withdrawalRequests.amountOptional': 'Montant (optionnel)',
    'withdrawalRequests.submit': 'Envoyer la demande',
    'withdrawalRequests.submitting': 'Envoi…',
    'withdrawalRequests.statusPending': 'En attente',
    'withdrawalRequests.statusCompleted': 'Complété',
    'withdrawalRequests.statusCancelled': 'Annulé',
    'withdrawalRequests.markCompleted': 'Marquer comme complété',
    'withdrawalRequests.colInvestor': 'Investisseur',
    'withdrawalRequests.colDate': 'Date',
    'withdrawalRequests.colAmount': 'Montant',
    'withdrawalRequests.colStatus': 'Statut',
    'withdrawalRequests.commentOptional': 'Commentaire (optionnel)',
    'withdrawalRequests.commentPlaceholder': 'Ajouter un commentaire pour le gestionnaire…',
    'withdrawalRequests.investorComment': 'Commentaire de l\'investisseur',
    'withdrawalRequests.repaidOn': 'Remboursé le',
    'capitalAddition.title': 'Propositions de fonds additionnels',
    'capitalAddition.empty': 'Aucune proposition.',
    'capitalAddition.proposeButton': 'Proposer des fonds additionnels',
    'capitalAddition.proposedAmount': 'Montant ($)',
    'capitalAddition.availableFromDate': 'Disponible à partir du',
    'capitalAddition.submit': 'Envoyer la proposition',
    'capitalAddition.submitting': 'Envoi…',
    'capitalAddition.statusPending': 'En attente',
    'capitalAddition.statusAccepted': 'Accepté',
    'capitalAddition.statusRejected': 'Refusé',
    'capitalAddition.accept': 'Accepter',
    'capitalAddition.reject': 'Refuser',
    'capitalAddition.colInvestor': 'Investisseur',
    'capitalAddition.colAmount': 'Montant',
    'capitalAddition.colAvailableFrom': 'Disponible à partir du',
    'capitalAddition.colStatus': 'Statut',
    'confirm': 'Confirmer',
    'deleteProperty.title': 'Supprimer l\'immeuble',
    'deleteProperty.confirmMessage': 'Êtes-vous sûr de vouloir supprimer cet immeuble? Cette action est irréversible.',
    'deleteProperty.warningIntro': 'Cet immeuble comporte:',
    'deleteProperty.warningBrokers': 'Partagé avec {count} courtier(s)',
    'deleteProperty.warningDossiers': '{count} dossier(s) de financement',
    'deleteProperty.warningProjectDocuments': 'Data room',
    'deleteProperty.warningOutro': 'La suppression retirera l\'accès aux courtiers. Les dossiers et documents ne seront plus accessibles.',
    'deleteProperty.deleteButton': 'Supprimer l\'immeuble',
    'deleteProperty.deleteAnyway': 'Supprimer quand même',
    'duplicateProperty.title': 'Dupliquer l\'immeuble',
    'duplicateProperty.description': 'Choisissez ce qu\'inclure dans la copie :',
    'duplicateProperty.noSharingHint': 'Les partages, accès courtiers, dossiers de financement et data room ne seront pas copiés.',
    'duplicateProperty.withoutScenarios': 'Immeuble seulement',
    'duplicateProperty.withoutScenariosHint': 'Copier les données de l\'immeuble sans les scénarios de financement.',
    'duplicateProperty.withScenarios': 'Immeuble et scénarios',
    'duplicateProperty.withScenariosHint': 'Copier aussi tous les scénarios de financement (marqués comme copies).',
    'home.duplicate': 'Dupliquer',
    'clients.deleteConfirm': 'Supprimer ce client?',
    'shareModal.title': 'Partager cet immeuble',
    'shareModal.recipientEmail': 'Adresse courriel du destinataire',
    'shareModal.send': 'Envoyer',
    'shareModal.emailRequired': 'Veuillez entrer une adresse courriel.',
    'shareModal.sentToAccount': "L'immeuble a été ajouté au tableau de bord de {email}.",
    'shareModal.sentByEmail': 'Un lien a été envoyé par courriel à {email}.',
    'shareModal.access': 'Accès',
    'shareModal.accessRead': 'Lecture seule',
    'shareModal.accessWrite': 'Lecture et écriture',
    'shareModal.ownedReadOnlyNote':
      'Les immeubles détenus sont partagés en lecture seule. Les destinataires voient vos dernières données sauvegardées.',
    'shareModal.ownedHint':
      'Utile pour un financement ou un co-investisseur. La cession de deal et le mandat de vente ne s’appliquent pas aux immeubles détenus.',
    'shareModal.includeNdaHintOwned':
      'Si cette option est cochée, vous devez joindre ou choisir un NDA. Les destinataires en auront besoin avant l’accès à la data room.',
    'shareModal.allowSubScenarios': 'Permettre de modifier les sous-scénarios (refinancement, optimisation)',
    'shareModal.createLink': 'Créer le lien',
    'shareModal.linkCopied': 'Lien copié dans le presse-papiers.',
    'shareModal.shareOnFacebook': 'Partager sur Facebook',
    'shareModal.facebookSuggestedMessage': 'Voici un immeuble que je vous partage via la plateforme Rentalyzer.',
    'shareModal.copyMessageForPost': 'Copier ce message pour votre publication',
    'shareModal.orShareByLink': 'Ou partager par lien',
    'shareModal.recipientCount': '{count} personne l\'a dans son tableau de bord',
    'shareModal.recipientCount_plural': '{count} personnes l\'ont dans leur tableau de bord',
    'shareModal.shareLinkLabel': 'Lien de partage',
    'shareModal.tabEmail': 'Par courriel',
    'shareModal.tabLink': 'Par lien',
    'shareModal.sharedOptionsIntro': 'Les réglages d\'accès et de scénarios ci-dessus s\'appliquent au partage par courriel et par lien.',
    'shareModal.linkTabHint': 'Créez un lien à copier, à envoyer par message ou à publier sur les réseaux sociaux.',
    'shareModal.shareForCession': 'Partager pour céder ce deal',
    'shareModal.shareForCessionHint':
      'Les destinataires pourront demander plus d\'informations et l\'accès à la data room en vue d\'une cession de promesse d\'achat.',
    'shareModal.brokerShareOptionsTitle': 'Autres options',
    'shareModal.includeNda': 'Inclure un NDA',
    'shareModal.includeNdaHint':
      'Si cette option est cochée, vous devez joindre ou choisir un fichier NDA. Les destinataires en auront besoin avant la data room lorsque le partage pour cession est activé.',
    'shareModal.ndaRequired': 'Veuillez joindre ou sélectionner un fichier NDA.',
    'shareModal.anonymizeAddress': 'Anonymiser l\'adresse (afficher **** *************, Ville (QC), *** ***)',
    'shareModal.attachNda': 'Joindre un NDA (optionnel)',
    'shareModal.chooseNdaFile': 'Choisir un fichier',
    'shareModal.removeNda': 'Retirer',
    'shareModal.tabVip': 'Liste VIP (pocket deals)',
    'shareModal.vipRequiresCession':
      'Activez « Partager pour céder ce deal » ou « Partager sous mandat de vente » ci-dessus pour que les destinataires VIP puissent demander des informations.',
    'shareModal.shareForMandatedSale': 'Partager sous mandat de vente (offres)',
    'shareModal.shareForMandatedSaleHint':
      'Les destinataires peuvent demander des informations, utiliser la data room si vous l’autorisez, puis déposer une offre indicative et suivre les étapes PDF officielles. Incompatible avec « Partager pour céder ».',
    'shareModal.mandatedSaleRequiresModule': 'Activez « Gestion des offres d’achat » dans votre profil pour utiliser un mandat de vente sur les partages.',
    'shareModal.mandateFromPropertyNote':
      'Cet immeuble est marqué avec un mandat de vente à l’analyse. Les nouveaux partages utiliseront automatiquement le parcours mandat (lorsque vous partagez en tant que courtier).',
    'shareModal.vipNoRecipients': 'Aucun contact avec « Pocket deals » activé. Ajoutez ou modifiez des clients dans Mes clients.',
    'shareModal.vipQueued': '{n} courriel(s) pocket deal mis en file. Envoi sous peu.',
    'shareModal.vipDefaultSubject': 'Pocket deal — partagé avec vous (Rentalyzer)',
    'shareModal.vipSubject': 'Objet du courriel',
    'shareModal.vipPreheader': 'Pré-en-tête (aperçu)',
    'shareModal.vipIntro': 'Texte d\'introduction (optionnel)',
    'shareModal.vipIncludeDescription': 'Inclure la description marketing',
    'shareModal.vipIncludeImage': 'Inclure la photo de l\'immeuble',
    'shareModal.vipIncludeMetrics': 'Inclure le tableau des chiffres clés',
    'shareModal.vipIncludeScenario': 'Inclure le résumé d\'un scénario',
    'shareModal.vipEmailLanguages': 'Langue(s) du courriel',
    'shareModal.vipEmailLangFr': 'Français',
    'shareModal.vipEmailLangEn': 'Anglais',
    'shareModal.vipEmailLanguagesHint':
      'Si les deux sont cochés, un seul courriel contient les sections français et anglais. Si la description marketing n’est pas encore en anglais, elle sera traduite une fois puis enregistrée pour les prochains envois.',
    'shareModal.vipEmailLangRequired': 'Sélectionnez au moins une langue pour le courriel.',
    'shareModal.vipScenarioLabel': 'Scénario',
    'shareModal.vipSendButton': 'Envoyer à la liste VIP',
    'shareModal.vipRecipientsCount': '{count} destinataire(s) avec pocket deals activé',
    'shareModal.vipHelp':
      'Chaque contact reçoit un lien personnel. Si vous activez la cession ou un mandat de vente, les mêmes options s’appliquent aux courriels VIP. Sinon le pocket reste un simple contact.',
    'shareModal.vipMetricsTitle': 'Campagnes pocket récentes',
    'shareModal.vipMetricsNote':
      'Les ouvertures utilisent un pixel de suivi; beaucoup de boîtes bloquent les images, donc le taux d’ouverture est souvent sous-estimé. Les clics sont l’indicateur principal.',
    'shareModal.vipMetricsEmpty': 'Aucune campagne pour l’instant. Après un envoi, les compteurs s’affichent ici.',
    'shareModal.vipMetricsCampaign': 'Campagne',
    'shareModal.vipMetricsUpdated': 'Mise à jour',
    'shareModal.vipMetricsSent': 'Envoyés',
    'shareModal.vipMetricsOpens': 'Ouvertures',
    'shareModal.vipMetricsClicks': 'Clics',
    'shareModal.vipMetricsSignups': 'Inscriptions',
    'shareModal.vipMetricsInterests': 'Demandes d’info',
    'clients.sendPocketDeals': 'Envoyer les pocket deals',
    'clients.skipAutoInvite': 'Ne pas envoyer l\'invitation Rentalyzer pour l\'instant',
    'clients.addressOptionalHint': 'Optionnel — pourra servir à personnaliser plus tard.',
    'clients.importVipButton': 'Importer des contacts VIP',
    'clients.importVipTitle': 'Importer des contacts VIP (IA)',
    'clients.importVipAiHint':
      'Téléversez un fichier : Rentalyzer utilise l’IA pour repérer noms, courriels et adresses optionnelles. Vérifiez l’aperçu avant d’ajouter (opt-in pocket deals, traçabilité CASL).',
    'clients.importVipFormatsTitle': 'Fichiers acceptés',
    'clients.importVipFormatExcel': 'Excel (.xlsx, .xls)',
    'clients.importVipFormatSheets': 'Google Sheets — télécharger ou exporter en Excel ou CSV d’abord',
    'clients.importVipFormatWord': 'Word (.docx seulement ; enregistrer en .docx si vous avez un ancien .doc)',
    'clients.importVipFormatCsv': 'CSV ou texte (.csv, .txt)',
    'clients.importVipAddressNote':
      'Le courriel suffit pour les envois pocket deals. L’adresse postale est optionnelle (personnalisation ou segmentation ultérieure).',
    'clients.importVipDownloadTemplate': 'Télécharger le modèle Excel',
    'clients.importVipAnalyzing': 'Analyse du fichier…',
    'clients.importVipPreviewHelp': 'Décochez les lignes à ignorer. Corrigez les courriels si besoin — seules les adresses valides peuvent être ajoutées.',
    'clients.importVipColName': 'Nom',
    'clients.importVipColEmail': 'Courriel',
    'clients.importVipColAddress': 'Adresse (optionnel)',
    'clients.importVipColNotes': 'Notes',
    'clients.importVipColStatus': 'Statut',
    'clients.importVipAddressPlaceholder': 'Optionnel',
    'clients.importVipBadEmail': 'Courriel invalide',
    'clients.importVipBack': 'Retour',
    'clients.importPickFile': 'Choisir un fichier',
    'clients.importVipEmpty': 'Aucun contact avec un courriel valide. Essayez un autre fichier ou une autre mise en page.',
    'clients.importVipExists': 'Déjà dans votre liste',
    'clients.importVipCommit': 'Ajouter les contacts sélectionnés',
    'clients.vipOnRentalyzer': 'Sur Rentalyzer',
    'clients.vipPocketOnly': 'Liste pocket seulement',
    'basicInfo.marketingTitle': 'Description marketing (inscription)',
    'basicInfo.marketingHint':
      'Utilisée dans les courriels pocket deals et rapports. Utilisez la barre d’outils pour la mise en forme. « Générer avec l’IA » envoie le texte actuel (et les données d’immeuble) au modèle comme consignes ; le résultat remplace l’éditeur.',
    'basicInfo.marketingEditorPlaceholder':
      'Notes, ton ou brouillon : angles à mettre en avant, public visé, etc. Barre d’outils : gras, titres, listes, liens. Puis Générer avec l’IA pour obtenir une description complète (remplace ce contenu).',
    'basicInfo.marketingAiGenerate': 'Générer avec l\'IA',
    'basicInfo.marketingAiTranslateEn': 'Traduire en anglais (aperçu)',
    'basicInfo.marketingAiTranslateFr': 'Traduire en français (aperçu)',
    'basicInfo.marketingAiApply': 'Remplacer par la traduction',
    'basicInfo.marketingAiDisclaimer': 'Contenu généré par IA — à réviser avant envoi.',
    'basicInfo.marketingAiLoading': 'Génération…',
    'basicInfo.marketingTranslationPreview': 'Aperçu de la traduction',
    'basicInfo.marketingToolbarBold': 'Gras',
    'basicInfo.marketingToolbarItalic': 'Italique',
    'basicInfo.marketingToolbarStrike': 'Barré',
    'basicInfo.marketingToolbarH2': 'Titre 2',
    'basicInfo.marketingToolbarH3': 'Titre 3',
    'basicInfo.marketingToolbarBulletList': 'Liste à puces',
    'basicInfo.marketingToolbarOrderedList': 'Liste numérotée',
    'basicInfo.marketingToolbarQuote': 'Citation',
    'basicInfo.marketingToolbarHr': 'Ligne horizontale',
    'basicInfo.marketingToolbarUndo': 'Annuler',
    'basicInfo.marketingToolbarRedo': 'Refaire',
    'basicInfo.marketingToolbarLink': 'Lien',
    'basicInfo.marketingToolbarUnsetLink': 'Retirer le lien',
    'basicInfo.marketingLinkPrompt': 'URL du lien (vide pour retirer)',
    'myFiles.title': 'Mes Fichiers',
    'myFiles.hint': 'Conservez ici vos documents (ex. NDA) pour les réutiliser lors d\'un partage.',
    'myFiles.upload': 'Téléverser',
    'myFiles.uploading': 'Téléversement…',
    'myFiles.noFiles': 'Aucun fichier.',
    'myFiles.fileTooBig': 'Le fichier doit faire moins de 10 Mo',
    'myFiles.download': 'Télécharger',
    'myFiles.delete': 'Supprimer',
    'myFiles.deleteConfirm': 'Supprimer ce fichier?',
    'myFiles.chooseFromList': 'Choisir dans Mes Fichiers',
    'listingOffer.indicativeTitle': 'Offre indicative (sans valeur juridique)',
    'listingOffer.price': 'Prix offert',
    'listingOffer.conditions': 'Conditions',
    'listingOffer.delays': 'Délais',
    'listingOffer.comments': 'Commentaires',
    'listingOffer.representedByBroker': 'Je suis représenté par un courtier',
    'listingOffer.brokerEmail': 'Courriel du courtier',
    'listingOffer.brokerEmailInvalid': 'Entrez un courriel de courtier valide.',
    'listingOffer.submitIndicative': 'Soumettre l’offre indicative',
    'listingOffer.versionsTitle': 'Versions d’offre',
    'listingOffer.kindIndicative': 'Indicative',
    'listingOffer.kindFormal': 'PDF officiel',
    'listingOffer.acceptOffer': 'Accepter cette offre officielle',
    'listingOffer.acceptConfirm':
      'Accepter cette offre? Les autres personnes intéressées perdront l’accès à la data room pour cette annonce.',
    'listingOffer.addFormalPdf': 'Tour d’offre PDF officielle',
    'listingOffer.uploadFormal': 'Créer une version et joindre le premier PDF',
    'listingOffer.pdfRequired': 'Choisissez un fichier PDF.',
    'listingOffer.appendPdf': 'Ajouter un PDF à une version',
    'listingOffer.chooseVersion': 'Choisir la version',
    'listingOffer.uploadAttachment': 'Téléverser un PDF',
    'listingOffer.submitError': 'Une erreur s’est produite. Réessayez.',
    'listingOffer.cancelIndicativeForm': 'Annuler',
    'listingOffer.indicativeFormHiddenHint':
      'Le formulaire d’offre indicative est masqué. Vous pourrez l’afficher de nouveau quand vous serez prêt.',
    'listingOffer.showIndicativeFormAgain': 'Afficher le formulaire d’offre indicative',
    'listingMandateStatus.active': 'Mandat de vente : actif',
    'listingMandateStatus.negotiating': 'Mandat de vente : en négociation',
    'listingMandateStatus.offer_accepted': 'Mandat de vente : offre acceptée',
    'listingMandateStatus.sold': 'Mandat de vente : vendu',
    'shareInterest.sectionTitle': 'Demander plus d\'information',
    'shareInterest.listingAccessClosed':
      'L’accès à la data room pour cette annonce est fermé — une autre offre a été acceptée. Merci de votre intérêt.',
    'shareInterest.requestMoreInfo': 'Demander plus d\'information au cédant',
    'shareInterest.requestSentNoNda': 'Votre demande a été envoyée. Le cédant pourra vous donner accès à la data room.',
    'shareInterest.ndaRequired': 'Le cédant exige un NDA signé. Téléversez votre NDA signé ci-dessous.',
    'shareInterest.chooseSignedNda': 'Choisir le fichier NDA signé',
    'shareInterest.submitNda': 'Soumettre le NDA',
    'shareInterest.ndaPendingApproval': 'Votre NDA a été soumis et est en attente d\'approbation par le cédant.',
    'shareInterest.waitingDocumentAccess': 'NDA approuvé. En attente de l\'accès à la data room par le cédant.',
    'shareInterest.documentAccessRefused': 'Votre demande a été refusée. Si vous avez besoin d\'accès, le cédant devra l\'initier.',
    'shareInterest.confirmInterest': 'Confirmer mon intérêt',
    'shareInterest.confirmReadyForOffers': 'Soumettre une offre indicative',
    'shareInterest.pocketConfirmContactRequest': 'Confirmer ma demande auprès du courtier',
    'shareInterest.chooseCessionContract': 'Choisir le contrat de cession',
    'shareInterest.submitCessionContract': 'Soumettre le contrat de cession',
    'shareInterest.cessionSubmittedWaiting': 'Contrat de cession déposé. En attente de la finalisation par le cédant.',
    'shareInterest.ownedDiligenceReady':
      'Vous pouvez consulter la data room. Ce partage d’immeuble détenu n’utilise pas la cession de deal.',
    'shareInterest.sectionTitleBroker': 'Demander plus d\'information',
    'shareInterest.requestMoreInfoBroker': 'Demander plus d\'information au courtier',
    'shareInterest.requestSentNoNdaBroker':
      'Votre demande a été envoyée. Le courtier pourra vous donner accès à la data room.',
    'shareInterest.ndaRequiredBroker': 'Le courtier exige un NDA signé. Téléversez votre NDA signé ci-dessous.',
    'shareInterest.ndaPendingApprovalBroker': 'Votre NDA a été soumis et est en attente d\'approbation par le courtier.',
    'shareInterest.waitingDocumentAccessBroker':
      'NDA approuvé. En attente de l\'accès à la data room par le courtier.',
    'shareInterest.documentAccessRefusedBroker':
      'Votre demande a été refusée. Si vous avez besoin d\'accès, le courtier devra l\'initier.',
    'shareInterest.phoneLabel': 'Numéro de téléphone',
    'shareInterest.phonePlaceholder': 'ex. (514) 555-1234',
    'shareInterest.phoneRequired': 'Entrez un numéro de téléphone valide (au moins 10 chiffres).',
    'shareInterest.pocketDealNoCessionUpload':
      'Ce pocket deal ne prévoit pas le dépôt de contrat de cession ici. Contactez le courtier pour la suite.',
    'shareInterest.pocketRequestSentBroker':
      'Votre demande a été envoyée. Le courtier pourra vous contacter avec les renseignements fournis.',
    'shareInterest.pocketContactConsentProfilePhone':
      'En poursuivant, vous acceptez que nous communiquions votre nom, votre courriel et le numéro de téléphone de votre profil Rentalyzer au courtier pour qu’il puisse vous contacter.',
    'shareInterest.pocketContactConsentWithPhone':
      'En poursuivant, vous acceptez que nous communiquions votre nom, votre courriel et le numéro ci-dessous au courtier pour qu’il puisse vous contacter. Ce numéro sera enregistré dans votre profil.',
    'shareInterest.pocketNdaSubmittedBroker': 'Le courtier traite votre demande.',
    'shareInterest.pocketNdaPendingBroker': 'En attente du courtier.',
    'shareInterest.pocketWaitingFollowUpBroker': 'Suivi en cours avec le courtier.',
    'shareInterest.pocketAccessRefusedBroker': 'Le courtier a refusé cette demande.',
    'shareInterest.pocketConfirmInterest': 'Confirmer ma demande auprès du courtier',
    'share.brokerPocketInterestsTitle': 'Demandes de contact (partages)',
    'share.brokerPocketInterestPhone': 'Téléphone',
    'share.brokerPocketInterestShareHint':
      'Investisseurs qui ont demandé à être rejoints pour un deal que vous avez partagé sur cet immeuble.',
    'pocketInterest.status_requested': 'Demande de contact',
    'pocketInterest.status_nda_submitted': 'En cours',
    'pocketInterest.status_nda_approved': 'En cours',
    'pocketInterest.status_document_access_granted': 'Suivi en cours',
    'pocketInterest.status_document_access_refused': 'Refusé',
    'pocketInterest.status_interest_confirmed': 'Intérêt confirmé',
    'pocketInterest.status_cession_submitted': 'Étape complétée',
    'cession.cedentSectionTitle': 'Demandes et accès aux documents',
    'cession.interestsForShare': 'Demandes pour ce partage',
    'cession.approveNda': 'Approuver le NDA',
    'cession.grantDocumentAccess': 'Donner accès aux documents',
    'cession.grantAccessNoNdaWarning': 'Ce partage n\'inclut pas de NDA. Donner l\'accès permet au destinataire de consulter la data room sans accord de confidentialité. N\'accordez l\'accès que si vous acceptez ce risque.',
    'cession.grantAccessNoNdaConfirm': 'Accorder l\'accès aux documents sans NDA? Le destinataire pourra consulter toute la data room.',
    'cession.revokeDocumentAccess': 'Révoquer l\'accès aux documents',
    'cession.refuseDocumentAccess': 'Refuser la demande d\'accès aux documents',
    'cession.status_requested': 'Demande reçue',
    'cession.status_nda_submitted': 'NDA soumis',
    'cession.status_nda_approved': 'NDA approuvé',
    'cession.status_document_access_granted': 'Accès documents accordé',
    'cession.status_document_access_refused': 'Accès documents refusé',
    'cession.status_interest_confirmed': 'Intérêt confirmé',
    'cession.status_cession_submitted': 'Contrat de cession déposé',
    'cession.status_listing_access_revoked': 'Accès annonce fermé (autre offre acceptée)',
    'cession.peopleWithDocumentAccess': 'Personnes ayant accès aux documents',
    'cession.noInterests': 'Aucune demande pour l\'instant.',
    'cession.finalizeCession': 'Finaliser la cession et attribuer à cet investisseur',
    'cession.finalizeConfirm': 'Attribuer cet immeuble à cet investisseur (cessionnaire)? Il apparaîtra dans Mes Deals et pourra partager avec son courtier.',
    'home.sharedWithYou': 'Partagé avec vous',
    'home.cessionRevoked': 'Cession conclue – accès révoqué',
    'home.tabSharedWithMe': 'Partagés avec moi',
    'home.sectionFinancedProjects': 'Projets financés',
    'home.sectionFinancedProperties': 'Immeubles financés',
    'home.sectionLoansManagement': 'Gestion des prêts',
    'home.privateLenderEmptyHubHelp':
      'Vous n’avez pas encore de deal immobilier à votre nom. Utilisez Gestion des prêts pour ajouter des emprunteurs et des brouillons d’immeubles, et Investisseurs de capitaux pour l’intégration du fonds. Les invitations peuvent attendre que vous soyez prêt.',
    'home.sectionCapitalInvestors': 'Investisseurs de capitaux',
    'home.noFinancedProperties': 'Aucun immeuble financé pour le moment.',
    'home.noSharedWithMe': 'Aucun immeuble partagé avec vous pour le moment.',
    'home.removeFromDashboard': 'Retirer de mon tableau de bord',
    'home.new': 'Nouveau',
    'home.sharedWithCount': 'Partagé avec {count} personne',
    'home.sharedWithCount_plural': 'Partagé avec {count} personnes',
    'home.sharedWithBroker': 'Partagé avec 1 courtier',
    'home.sharedWithBrokers': 'Partagé avec {count} courtiers',
    'home.sharedWithCountAndBroker': 'Partagé avec {count} personne(s) et 1 courtier',
    'home.sharedShort': 'Partagé',
    'home.shareAddedBanner': 'Immeuble ajouté dans vos immeubles partagés.',
    'home.pocketDealLinkEmailMismatchBanner':
      'Le courriel de votre compte Rentalyzer ne correspond pas à l’adresse à laquelle ce pocket deal a été envoyé. Vous pouvez consulter le deal, mais le lien avec le courtier ne s’activera pas tant que vous n’utilisez pas la même adresse, ou tant que le courtier ne renvoie pas le deal à votre courriel actuel.',
    'shareMethodChoice.title': 'Comment voulez-vous partager?',
    'shareMethodChoice.description': 'Partagez cet immeuble avec votre courtier, ou envoyez un lien par courriel ou sur les médias sociaux.',
    'shareMethodChoice.withBroker': 'Partager avec mon courtier',
    'shareMethodChoice.withBrokerHint': 'Votre courtier le verra dans sa liste de clients.',
    'shareMethodChoice.byEmail': 'Partager par courriel et médias sociaux',
    'shareMethodChoice.byEmailHint': 'Envoyer un lien par courriel ou le coller où vous voulez (réseaux sociaux, messagerie, etc.).',
    'shareWithBroker.title': 'Partager avec votre courtier',
    'shareWithBroker.description': 'Votre courtier pourra voir cette propriété et ajouter des scénarios de financement.',
    'shareWithBroker.includeScenarios': 'Partager aussi mes scénarios de financement',
    'shareWithBroker.confirm': 'Partager avec le courtier',
    'shareWithBroker.invitationLinked': 'Vous êtes maintenant lié à votre courtier. Vous pouvez partager des propriétés depuis l\'accueil.',
    'shareWithBroker.pickBroker': 'Partager avec quel courtier?',
    'shareWithBroker.pickBrokerHint': 'Choisissez le courtier avec qui partager cet immeuble.',
    'shareWithBroker.brokerLabel': 'Courtier {index}',
    'shareWithBroker.alreadyShared': 'Déjà partagé',
    'shareWithBroker.alreadySharedWithThisBroker': 'Cet immeuble est déjà partagé avec ce courtier.',
    'sharedView.badge': 'Immeuble partagé (lecture seule)',
    'sharedView.badgeWrite': 'Immeuble partagé (vous pouvez modifier)',
    'sharedView.loginToEdit': 'Immeuble partagé. Connectez-vous pour modifier.',
    'sharedView.writtenBy': 'Partagé par le courtier. Vous ne pouvez pas modifier.',
    'sharedView.addToDashboard': 'Ajoutez cet immeuble à votre tableau de bord Rentalyzer :',
    'sharedView.removeProperty': "Retirer l'immeuble",
    'marketParams.title': 'Paramètres marché',
    'marketParams.newVersion': 'Nouvelle version',
    'marketParams.versionLabel': 'Libellé de la version',
    'marketParams.updatedAt': 'Mise à jour le',
    'marketParams.useInPdf': 'Utiliser dans le PDF',
    'marketParams.defaultRates': 'Taux par défaut (référence uniquement)',
    'home.cmb.title': 'Taux CMB',
    'home.cmb.error': 'Impossible de charger les taux',
    'home.cmb.empty': 'Taux CMB non disponible pour le moment',
    'property.brokerSaleMandate': 'J’ai le mandat de vendre cet immeuble en tant que courtier immobilier',
    'property.brokerSaleMandateHint':
      'Affiché seulement lorsque la gestion des offres est activée. Les nouveaux partages pour cet immeuble suivront le parcours mandat sans reposer la question dans la fenêtre de partage.',
    'property.form.title': "Nouvelle Analyse d'Immeuble",
    'property.form.titleOwned': 'Mon immeuble',
    'property.form.simpleMode': 'Mode simplifié',
    'property.form.advancedMode': 'Mode avancé',
    'property.alert.fillAddress': "Veuillez remplir l'adresse complète (adresse, ville, province, code postal).",
    'property.alert.ownedValueMissing': 'La valeur estimée est vide. Continuer quand même ?',
    'dealPurpose.modal.title': 'Quel type de deal ?',
    'dealPurpose.modal.subtitle': 'Choisissez une analyse d’achat, un immeuble déjà détenu, ou l’import depuis une fiche.',
    'dealPurpose.acquisition.title': 'Immeuble à analyser',
    'dealPurpose.acquisition.description': 'Analyser un immeuble à acheter — prix d’achat, frais d’acquisition et scénarios de financement.',
    'dealPurpose.owned.title': 'Immeuble que je détiens',
    'dealPurpose.owned.description': 'Saisir les infos d’un immeuble déjà détenu — valeur de marché, revenus, dépenses et financement en place (ou aucune hypothèque).',
    'dealPurpose.importListing.title': 'Importer depuis une fiche',
    'dealPurpose.importListing.description':
      'Collez une URL de courtier, le texte de la fiche, ou un PDF (mémoire de présentation). Le formulaire est prérempli ; vous révisez avant de sauvegarder.',
    'listingImport.title': 'Importer depuis une fiche',
    'listingImport.subtitle':
      'Essayez l’URL publique si le site le permet. S’il bloque la lecture, collez le texte ou joignez le PDF — puis vérifiez chaque chiffre.',
    'listingImport.urlLabel': 'URL de la fiche (facultatif)',
    'listingImport.urlPlaceholder': 'https://…',
    'listingImport.urlHint':
      'Plusieurs sites de courtiers (dont PMML) bloquent la lecture automatique. Gardez l’URL, et collez les chiffres ci-dessous.',
    'listingImport.textLabel': 'Texte de la fiche (recommandé)',
    'listingImport.textPlaceholder':
      'Collez le prix, le nombre d’unités, les revenus, taxes et dépenses depuis la fiche ou le mémoire.',
    'listingImport.fileLabel': 'PDF ou document (facultatif)',
    'listingImport.fileHint': 'PDF, Word (.docx) ou texte — 8 Mo max',
    'listingImport.extract': 'Extraire les champs',
    'listingImport.extracting': 'Extraction…',
    'listingImport.extractAgain': 'Extraire à nouveau',
    'listingImport.apply': 'Ouvrir dans le formulaire',
    'listingImport.completeTitle': 'Analyse terminée',
    'listingImport.completeHint':
      'Les chiffres extraits sont affichés ci-dessous. Vérifiez-les, puis ouvrez le formulaire.',
    'listingImport.showSource': 'Modifier la source',
    'listingImport.hideSource': 'Masquer la source',
    'listingImport.sourceTextUsed': 'Texte collé',
    'listingImport.previewTitle': 'Champs extraits',
    'listingImport.previewHint': 'Rien n’est enregistré pour l’instant. Vérifiez ces valeurs, puis ouvrez le formulaire pour le financement et le reste.',
    'listingImport.previewEmpty': 'Pas de résumé — ouvrez le formulaire pour voir l’extrait complet.',
    'listingImport.fetchOk': 'La page de la fiche a été lue.',
    'listingImport.fetchBlocked':
      'Le site a bloqué la lecture automatique (anti-bot). L’extraction a utilisé le texte ou le fichier fourni.',
    'listingImport.fetchFailed': 'Impossible de charger l’URL. L’extraction a utilisé le texte ou le fichier fourni.',
    'listingImport.needsMoreContent':
      'Pas assez de contenu lisible. Collez la description et les chiffres de la page, ou joignez le PDF.',
    'listingImport.errorNeedSource': 'Ajoutez une URL, du texte, ou un fichier.',
    'listingImport.errorFileTooLarge': 'Fichier trop volumineux (8 Mo max).',
    'listingImport.errorNoFields': 'Aucun champ utilisable. Ajoutez plus de texte et réessayez.',
    'listingImport.errorQuota': 'Limite quotidienne d’import de fiches atteinte. Réessayez demain.',
    'listingImport.errorAuth': 'Connectez-vous pour importer une fiche.',
    'listingImport.errorNetwork':
      'Impossible de joindre le service d’import depuis ce site (souvent un blocage CORS ou réseau). Réessayez après redéploiement de la Cloud Function, ou collez le texte de la fiche si la page elle-même est bloquée.',
    'listingImport.formBannerTitle': 'Préremplir depuis une fiche',
    'listingImport.formBannerBody': 'Collez une URL, le texte de la fiche ou un PDF pour remplir prix, unités, revenus et dépenses. Révisez avant de sauvegarder.',
    'listingImport.formBannerButton': 'Importer une fiche',
    'listingImport.formWarningsTitle': 'Importé depuis une fiche — à réviser avant de sauvegarder',
    'dealPurpose.badge.owned': 'Détenu',
    'ownedFinancing.title': 'Financement actuel',
    'ownedFinancing.type': 'Situation hypothécaire',
    'ownedFinancing.type.existing': 'Financement en place',
    'ownedFinancing.type.none': 'Aucune hypothèque',
    'ownedFinancing.loanBalance': 'Solde du prêt',
    'ownedFinancing.monthlyPayment': 'Versement hypothécaire mensuel',
    'ownedFinancing.noneHint': 'Cet immeuble est modélisé sans dette. Le cashflow correspond au revenu net d’exploitation.',
    'ownedFinancing.currentSituationTitle': 'Situation actuelle',
    'property.marketingMediaLockedTitle': 'Présentation & photos',
    'property.marketingMediaLockedBody':
      'Enregistrez l’immeuble une première fois avec le bouton ci-dessous. Vous pourrez ensuite ajouter plusieurs photos et la description marketing.',
    'back': 'Retour',
    'cancel': 'Annuler',
    'close': 'Fermer',
    'save': 'Sauvegarder',
    'edit': 'Modifier',
    'building.dashboard.title': "Tableau de bord de l'immeuble",
    'building.purchasePrice': "Prix d'Achat",
    'building.estimatedMarketValue': 'Valeur estimée actuelle',
    'building.netEquity': 'Équité nette',
    'building.ltv': 'RPV',
    'building.numberOfUnits': "Nombre d'unités",
    'building.pricePerDoor': 'Prix par porte',
    'building.grossIncome': 'Revenu brut',
    'building.totalExpenses': 'Dépenses totales',
    'building.netIncome': 'Revenu net (NOI)',
    'building.grm': 'MRB',
    'building.nim': 'MRN',
    'building.capRate': 'TGA',
    'building.createScenario': 'Créer Scénario de Financement',
    'building.createScenarioOwned': 'Créer un scénario de refinancement / optimisation',
    'building.scenarios': 'Scénarios',
    'building.clientSubScenarios': 'Sous-scénarios des clients',
    'building.subScenariosFromShares': 'Sous-scénarios des partages',
    'building.createdBy': 'Créé par',
    'building.createdViaShare': 'Créé via partage par',
    'building.propertyOfClient': "Immeuble de",
    'building.projectDocuments': 'Data room',
    'building.rentRegister': 'Registre des loyers',
    'rentRegister.title': 'Registre des loyers',
    'rentRegister.subtitle': 'Une ligne par logement — saisissez le loyer mensuel et les services annexes pour chaque unité.',
    'rentRegister.subtitleReadOnly': 'Registre des loyers par logement (lecture seule).',
    'rentRegister.unit': 'Logement',
    'rentRegister.unitNumber': 'Logement {n}',
    'rentRegister.rentPrice': 'Prix du loyer',
    'rentRegister.inclusions': 'Inclusions',
    'rentRegister.inclusionsPlaceholder': 'Chauffage, eau chaude, stationnement…',
    'rentRegister.ancillaryRevenue': 'Revenus de services annexes',
    'rentRegister.ancillaryDetails': 'Détail des services annexes',
    'rentRegister.ancillaryDetailsPlaceholder': 'Entreposage, stationnement, internet…',
    'rentRegister.total': 'Total',
    'rentRegister.grandTotal': 'Total général',
    'rentRegister.noUnitsHint': 'Indiquez d\'abord le nombre de logements dans les informations de l\'immeuble pour remplir le registre.',
    'rentRegister.noUnitsError': 'Le nombre de logements doit être supérieur à zéro.',
    'rentRegister.saveError': 'Impossible d\'enregistrer le registre des loyers.',
    'rentRegister.saving': 'Enregistrement…',
    'building.dealRecipientsSection': 'Deal partagé avec les destinataires suivants',
    'building.dealRecipientsHint':
      'Révoquer l’accès par lien retire le deal du « Partagé avec moi ». Retirer l’accès professionnel empêche un courtier ou un prêteur privé de voir cet immeuble dans ses dossiers. Vous pouvez rétablir l’accès par lien à tout moment, et l’accès professionnel depuis la même liste après l’avoir retiré. Vous ne pouvez pas retirer l’accès tableau de bord d’un courtier hypothécaire ou immobilier tant que vous êtes son client dans un dossier actif (les prêteurs privés font exception).',
    'building.dealRecipientsEmpty': 'Aucun destinataire pour le moment.',
    'building.recipientBadgeLinkShare': 'Partage par lien',
    'building.recipientBadgeLinkShareCourtierHypo': 'Partagé par lien — courtier hypothécaire',
    'building.recipientBadgeLinkShareCourtierImmo': 'Partagé par lien — courtier immobilier',
    'building.recipientBadgeLinkSharePrivateLender': 'Partagé par lien — prêteur privé',
    'building.recipientBadgePrivateLender': 'Prêteur privé',
    'building.recipientBadgeMortgageBroker': 'Courtier hypothécaire',
    'building.recipientBadgeRealEstateBroker': 'Courtier immobilier',
    'building.recipientBadgeProfessional': 'Accès professionnel (tableau de bord)',
    'building.revokeBrokerAccess': 'Retirer l’accès professionnel',
    'building.restoreBrokerAccess': 'Rétablir l’accès professionnel',
    'building.revokeBrokerBlockedClientRelation':
      'Impossible de retirer l’accès tableau de bord tant que vous êtes client de ce courtier dans un dossier actif. Votre courtier peut ajuster le partage depuis son compte si besoin.',
    'building.revokeBrokerAccessConfirm':
      'Retirer l’accès de ce professionnel à cet immeuble sur son tableau de bord ? Il ne verra plus cette propriété dans ses dossiers clients.',
    'building.recipientBadgeProfessionalRevoked': 'Accès professionnel (révoqué)',
    'building.revokeRecipientAccess': 'Révoquer l’accès à ce deal',
    'building.restoreRecipientAccess': 'Rétablir l’accès à ce deal',
    'building.openRecipientChat': 'Ouvrir le message privé',
    'building.recipientChatLinkOnly': 'Le message privé concerne les personnes ayant accès via un lien de partage.',
    'building.recipientChatDisabled': 'Le message privé n’est pas disponible tant que l’accès est révoqué.',
    'building.recipientActionError': 'Impossible de mettre à jour l’accès. Réessayez.',
    'building.recipientsBroadcastPanelTitle': 'Destinataires et envoi groupé',
    'building.ownerBroadcastTitle': 'Message à tous les destinataires',
    'building.ownerBroadcastHint':
      'Envoie le même texte dans la conversation privée de chaque destinataire actif avec vous (bulle de chat en bas à droite). Ce n’est pas un fil de groupe. Les personnes dont l’accès a été retiré ne reçoivent pas le message.',
    'building.ownerBroadcastPlaceholder': 'Écrire un message à toutes les personnes ayant accès via vos liens…',
    'building.ownerBroadcastSend': 'Envoyer à tous',
    'building.ownerBroadcastSending': 'Envoi…',
    'building.ownerBroadcastNoRecipients': 'Aucun destinataire actif à qui écrire.',
    'building.ownerBroadcastSuccess': 'Message envoyé à {count} destinataire(s). Ils le verront dans les messages privés.',
    'building.ownerBroadcastPartial': 'Envoyé à {sent}; impossible d’atteindre {fail} destinataire(s).',
    'building.ownerBroadcastError': 'Envoi impossible. Réessayez.',
    'building.interestPaymentReceived': 'Le prêteur a indiqué avoir reçu votre paiement d\'intérêts pour {month}.',
    'building.nextInterestPayment': 'Prochain paiement d\'intérêts',
    'building.nextInterestPaymentDueBy': 'à payer le',
    'building.manageLoan': 'Gérer le prêt',
    'building.loanManagementTitle': 'Gestion du prêt',
    'building.interestPaymentsTracking': 'Paiements d\'intérêts',
    'building.period': 'Période',
    'building.dueDate': 'Date d\'échéance',
    'building.lender': 'Prêteur',
    'building.paidOn': 'Payé le {date}',
    'building.tranches': 'Tranches',
    'building.trancheInitial': 'Tranche initiale',
    'building.trancheAdditional': 'Tranche additionnelle',
    'projectDocuments.title': 'Data room',
    'projectDocuments.addSection': 'Ajouter',
    'projectDocuments.addSectionModalTitle': 'Description du projet',
    'projectDocuments.sectionTitlePlaceholder': 'Titre de la section',
    'projectDocuments.sectionHint': 'Ex. : Promesse d\'achat, déclaration du vendeur, baux en vigueur, inspections, évaluation, etc.',
    'projectDocuments.createSection': 'Créer',
    'projectDocuments.deleteSection': 'Supprimer la section',
    'projectDocuments.deleteSectionConfirm': 'Supprimer cette section et tous ses fichiers?',
    'projectDocuments.upload': 'Téléverser',
    'projectDocuments.download': 'Télécharger',
    'projectDocuments.delete': 'Supprimer',
    'projectDocuments.noFiles': 'Aucun fichier',
    'projectDocuments.uploading': 'Téléversement…',
    'projectDocuments.fileTooBig': 'Le fichier doit faire moins de 10 Mo',
    'projectDocuments.readOnlyDisclaimer': 'Consultation uniquement. Ne pas imprimer ni redistribuer ces documents.',
    'view': 'Voir',
    'propertyReport.title': "Rapport d'Analyse de Rentabilité",
    'propertyReport.titleOwned': 'Rapport de performance — immeuble détenu',
    'propertyReport.analyzingProperty': 'Propriété à analyser',
    'propertyReport.askingPrice': 'Prix demandé',
    'propertyReport.purchasePrice': "Prix d'achat",
    'propertyReport.estimatedMarketValue': 'Valeur estimée actuelle',
    'propertyReport.netEquity': 'Équité nette',
    'propertyReport.numberOfUnits': "Nombre d'unités",
    'propertyReport.pricePerDoor': 'Prix par porte',
    'propertyReport.valuePerDoor': 'Valeur par porte',
    'propertyReport.totalRevenue': 'Revenus totaux',
    'propertyReport.totalExpenses': 'Dépenses totales',
    'propertyReport.debtServiceYear1': 'Service de la dette an 1',
    'propertyReport.cashFlow': 'Cash Flow',
    'propertyReport.financing': 'Financement',
    'propertyReport.financingType.conventional': 'Conventionnel',
    'propertyReport.financingType.cmhc': 'SCHL Standard',
    'propertyReport.financingType.cmhc_aph': 'SCHL APH Select',
    'propertyReport.financingType.private': 'Prêt privé',
    'propertyReport.financingType.assumption': 'Assumation hypothécaire',
    'propertyReport.financingType.existing': 'Financement en place',
    'propertyReport.financingType.none': 'Aucune hypothèque',
    'propertyReport.maxLoan': 'Prêt maximal',
    'propertyReport.outstandingLoan': 'Solde du prêt',
    'propertyReport.monthlyPayment': 'Versement mensuel',
    'propertyReport.remainingAmortMonths': 'Amortissement restant (mois)',
    'propertyReport.termEnd': 'Fin de terme',
    'propertyReport.loanGranted': 'Prêt octroyé',
    'propertyReport.ltv': 'Ratio prêt-valeur',
    'propertyReport.downPayment': 'Mise de fonds',
    'propertyReport.totalInvestment': 'Investissement total',
    'propertyReport.averageRentPerDoor': 'Loyer moyen par porte',
    'propertyReport.futureReturns.title': 'Rendements futurs',
    'propertyReport.futureReturns.intro':
      'Indiquez comment vos revenus, dépenses et la valeur de l\'immeuble pourraient évoluer. Rentalyzer calcule automatiquement vos rendements sur la période choisie.',
    'propertyReport.futureReturns.horizonLabel': 'Horizon de projection (ans)',
    'propertyReport.futureReturns.presetsLabel': 'Hypothèses rapides',
    'propertyReport.futureReturns.presetsHint': 'Vous pourrez ajuster chaque hypothèse ci-dessous.',
    'propertyReport.futureReturns.preset.conservative': 'Conservateur',
    'propertyReport.futureReturns.preset.realistic': 'Réaliste',
    'propertyReport.futureReturns.preset.optimistic': 'Optimiste',
    'propertyReport.futureReturns.resultsTitle': 'Résultat sur {years} ans',
    'propertyReport.futureReturns.projectedAtYear': 'Dans {years} ans',
    'propertyReport.futureReturns.heroNetCaption': 'Revenu net projeté',
    'propertyReport.futureReturns.totalReturnInfo':
      'Gain total sur votre investissement sur toute la période, incluant les flux de trésorerie et l\'appréciation de l\'immeuble.',
    'propertyReport.futureReturns.annualizedInfo':
      'Rendement annuel moyen équivalent, lissé sur l\'horizon sélectionné.',
    'propertyReport.futureReturns.irrInfo':
      'Le TRI (taux de rentabilité interne) tient compte du calendrier des entrées et sorties d\'argent. Utile pour comparer des projets.',
    'propertyReport.futureReturns.modeSimpleDesc': 'Ajustez trois hypothèses de croissance en pourcentage annuel.',
    'propertyReport.futureReturns.modeAdvancedDesc':
      'Personnalisez revenus, dépenses et valeur année par année dans un tableau détaillé.',
    'propertyReport.futureReturns.advancedOptions': 'Options avancées',
    'propertyReport.futureReturns.annualIncrease': 'Augmentation annuelle',
    'propertyReport.futureReturns.showAltMethod': 'Autre méthode ($, TGA…)',
    'propertyReport.futureReturns.hideAltMethod': 'Utiliser l\'augmentation annuelle (%)',
    'propertyReport.futureReturns.revenueHelp': 'Croissance annuelle estimée des loyers et revenus annexes.',
    'propertyReport.futureReturns.expenseHelp': 'Taxes, entretien, gestion et autres dépenses d\'exploitation.',
    'propertyReport.futureReturns.valueHelp': 'Appréciation annuelle estimée de la valeur marchande de l\'immeuble.',
    'propertyReport.futureReturns.desc1': 'Ajustez les hypothèses pour estimer les rendements après',
    'propertyReport.futureReturns.desc2': 'ans.',
    'propertyReport.futureReturns.revenue': 'Revenus',
    'propertyReport.futureReturns.expense': 'Dépenses d\'exploitation',
    'propertyReport.futureReturns.propertyValue': 'Valeur de l\'immeuble',
    'propertyReport.futureReturns.modeCapRate': 'TGA',
    'propertyReport.futureReturns.annualizedLabel': 'annualisé',
    'propertyReport.futureReturns.hintGross': 'Brut',
    'propertyReport.futureReturns.hintEffective': 'Effectif',
    'propertyReport.futureReturns.hintNet': 'Revenu net',
    'propertyReport.futureReturns.hintExpenses': 'dépenses',
    'propertyReport.futureReturns.netIncomeCaption': 'Revenu net projeté',
    'propertyReport.futureReturns.disabledSubScenario':
      'Les hypothèses proviennent du sous-scénario sélectionné ; la saisie manuelle est désactivée.',
    'propertyReport.futureReturns.savedScenario': 'Scénario de rendement sauvegardé',
    'propertyReport.futureReturns.selectSavedScenario': 'Sélectionner un scénario sauvegardé',
    'propertyReport.futureReturns.saveCurrentScenario': 'Enregistrer scénario',
    'propertyReport.futureReturns.noSavedScenario': '— Hypothèses personnalisées —',
    'propertyReport.futureReturns.modeSimple': 'Simple',
    'propertyReport.futureReturns.modeAdvanced': 'Avancé',
    'propertyReport.futureReturns.subScenarioFinancingNote':
      'Le sous-scénario sélectionné ajuste le financement seulement (dette, refinancement). Les revenus, dépenses et la valeur projetée proviennent des hypothèses ci-dessous.',
    'propertyReport.futureReturns.advanced.configureGrid': 'Configurer année par année',
    'propertyReport.futureReturns.advanced.modalTitle': 'Hypothèses année par année',
    'propertyReport.futureReturns.advanced.modalDesc':
      'L\'année 0 est figée selon l\'analyse actuelle. Saisissez les années 1 et suivantes ($ = montant annuel, % = variation vs année précédente). Cellule vide = reprise de l\'année précédente.',
    'propertyReport.futureReturns.advanced.applyColumn': 'Appliquer à toutes les années',
    'propertyReport.futureReturns.advanced.prefillSimple': 'Préremplir depuis le mode simple',
    'propertyReport.futureReturns.advanced.reset': 'Effacer toutes les saisies',
    'propertyReport.futureReturns.advanced.saveGrid': 'Appliquer',
    'propertyReport.futureReturns.advanced.yearCol': 'Année',
    'propertyReport.futureReturns.advanced.year0': 'Année 0',
    'propertyReport.futureReturns.advanced.clearCell': 'Effacer',
    'propertyReport.futureReturns.advanced.negativeNetWarning':
      'Une ou plusieurs années ont un revenu net projeté négatif.',
    'propertyReport.futureReturns.save': 'Enregistrer',
    'propertyReport.futureReturns.delete': 'Supprimer',
    'propertyReport.futureReturns.deleteConfirm': 'Supprimer ce scénario de rendement sauvegardé ?',
    'propertyReport.futureReturns.saveModalTitle': 'Enregistrer un scénario de rendement',
    'propertyReport.futureReturns.saveTitleLabel': 'Titre',
    'propertyReport.futureReturns.saveTitlePlaceholder': 'ex. Conservateur 5 ans',
    'propertyReport.futureReturns.saveOutlookLabel': 'Perspective',
    'propertyReport.futureReturns.saveTitleRequired': 'Le titre est obligatoire.',
    'propertyReport.futureReturns.saveFailed': 'Impossible d\'enregistrer le scénario.',
    'propertyReport.futureReturns.saving': 'Enregistrement…',
    'propertyReport.futureReturns.saveConfirm': 'Enregistrer',
    'propertyReport.futureReturns.outlook.pessimistic': 'Pessimiste',
    'propertyReport.futureReturns.outlook.realistic': 'Réaliste',
    'propertyReport.futureReturns.outlook.optimistic': 'Optimiste',
    'propertyReport.futureReturns.pdfAssumptions': 'Hypothèses de rendement',
    'propertyReport.futureReturns.pdfRevenue': 'Revenus',
    'propertyReport.futureReturns.pdfExpense': 'Dépenses',
    'propertyReport.futureReturns.pdfValue': 'Valeur immeuble',
    'propertyReport.futureReturns.pdfSavedScenario': 'Scénario sauvegardé',
    'propertyReport.subScenario': 'Sous-scénario',
    'propertyReport.none': 'Aucun',
    'propertyReport.untitled': 'Sans titre',
    'propertyReport.years': 'Années',
    'propertyReport.incomeGrowth': 'Croissance des revenus (%)',
    'propertyReport.expenseGrowth': 'Croissance des dépenses (%)',
    'propertyReport.valueAppreciation': 'Appréciation de la valeur (%)',
    'propertyReport.globalReturn.prefix': 'Rendement global sur',
    'propertyReport.globalReturn.suffix': 'an(s)',
    'propertyReport.annualizedReturn': 'Rendement annualisé',
    'propertyReport.irrAtYear.prefix': 'TRI à la',
    'propertyReport.irrAtYear.suffix': 'e année',
    'propertyReport.irrInfo': "Le taux de rendement interne (TRI) est le taux d'actualisation qui rend la valeur actuelle nette de l'investissement nulle.",
    'propertyReport.amortization': 'Amortissement',
    'propertyReport.interestRate': "Taux d'intérêt",
    'propertyReport.amortizationYears': "Amortissement (ans)",
    'propertyReport.financingTerm': 'Terme du financement',
    'propertyReport.newScenario': 'Nouveau scénario',
    'propertyReport.generatePdf': 'Générer PDF',
    'propertyReport.generatingPdf': 'Génération du PDF…',
    'propertyReport.reportType': 'Type de rapport',
    'propertyReport.reportType.client': 'Client final',
    'propertyReport.reportType.bank': 'Banque / Underwriting',
    'propertyReport.reportType.advanced': 'Investisseur avancé',
    'propertyReport.pdfModalTitle': 'Générer le rapport PDF',
    'propertyReport.pdfPreparedForLabel': 'Préparé pour (nom du destinataire)',
    'propertyReport.pdfPreparedForPlaceholder': 'ex. Nom du client ou du prêteur',
    'propertyReport.pdfIncludeSubScenario': 'Inclure un sous-scénario (page PDF supplémentaire)',
    'propertyReport.pdfSubScenarioSelect': 'Sous-scénario à inclure',
    'propertyReport.pdfSubScenarioPlaceholder': 'Choisir un sous-scénario…',
    'propertyReport.pdfModalConfirm': 'Télécharger le PDF',
    'propertyReport.pdfSubScenarioPick': 'Choisissez un sous-scénario ou décochez l’option.',
    'propertyReport.pdfSubScenarioInvalid': 'Impossible de générer ce sous-scénario pour le PDF.',
    'propertyReport.pdfGenerateError': 'Échec de la génération du PDF. Réessayez.',
    'propertyReport.chooseScenarioType': 'Choisir le type de scénario',
    'scenario.initialFinancing': 'Financement initial',
    'scenario.refinancing': 'Refinancement',
    'scenario.renewal': 'Renouvellement hypothécaire',
    'scenario.optimization': 'Optimisation',
    'scenario.other': 'Autres',
    'notApplicable': 'S/O',
    'amortization.title': "Tableau d'Amortissement",
    'amortization.noData': "Données d'amortissement indisponibles.",
    'amortization.period': 'Période (mois)',
    'amortization.loanBalance': 'Solde du prêt',
    'amortization.interestPayment': 'Versement intérêt',
    'amortization.principalPayment': 'Versement capital',
    'amortization.cumulativePrincipal': 'Capital cumulé',
    'amortization.propertyValue': 'Valeur immeuble',
    'amortization.equity': 'Équité',
    'keyIndicators.mrb': 'Multiplicateur de revenu brut (MRB)',
    'keyIndicators.mrn': 'Multiplicateur de revenu net (MRN)',
    'keyIndicators.capRate': 'TGA (Cap Rate)',
    'keyIndicators.coc': 'Rendement Cash on Cash (CoC)',
    'keyIndicators.cocEquity': 'Rendement Cash on Cash sur équité (CoC)',
    'keyIndicators.netEquity': 'Équité nette',
    'keyIndicators.cashFlow': 'Cashflow',
    'keyIndicators.loanPaydownReturn': 'Rendement capitalisation du prêt (1an)',
    'keyIndicators.appreciationReturn':
      "Rendement sur la plus value",
    'keyIndicators.valueGeneratedYear1': "Valeur générée après l'an 1",
    'keyIndicators.valueGeneratedAfterInitialTermYear1': "Valeur générée après l'an (terme du financement initial + 1)",
    'keyIndicators.valueGeneratedAfterYearLabel': "Valeur générée après l'an",
    'keyIndicators.totalReturn': 'Rendement global (1 an)',
    'keyIndicators.loanValueRatio': 'Ratio Prêt Valeur (RPV)',
    'keyIndicators.revenueIncrease': 'Augmentation des revenus',
    'keyIndicators.expenseReduction': 'Réduction des dépenses',
    'keyIndicators.noiImprovement': 'Amélioration du NOI',
    'keyIndicators.cocImprovement': 'Amélioration du CoC',
    'keyIndicators.investmentEfficiency': "Rendement sur investissement des travaux",
    'keyIndicators.paybackPeriod': 'Période de récupération',
    'keyIndicators.years': 'ans',
    'keyIndicators.interestRate': "Taux d'intérêt du prêt",
    'keyIndicators.monthlyInterest': "Intérêt mensuel payé",
    'keyIndicators.totalInterest': 'Total des intérêts payables',
    'keyIndicators.capitalRequired': 'Capital nécessaire',
    'financialSummary.title': 'Résumé Financier',
    'financialSummary.grossIncome': 'Revenus bruts',
    'financialSummary.effectiveIncome': 'Revenus effectifs',
    'financialSummary.operatingExpensesSCHL': "Dépenses d'exploitation SCHL",
    'financialSummary.totalExpenses': 'Dépenses totales',
    'financialSummary.netIncomeSCHL': 'Revenu net SCHL (NOI)',
    'financialSummary.effectiveNetIncome': 'Revenu net effectif',
    'financialSummary.netIncome': 'Revenu net (NOI)',
    'financialSummary.annualDebtService': 'Service de dette annuel',
    'financialSummary.annualCashFlow': 'Cash Flow annuel',
    'dealSummary.title': "Synthèse de l'opportunité",
    'dealSummary.verdict.strong': 'Profil intéressant',
    'dealSummary.verdict.medium': 'À analyser en détail',
    'dealSummary.verdict.weak': 'Profil risqué / faible',
    'dealSummary.strengths': 'Points forts',
    'dealSummary.risks': 'Points de vigilance',
    'schlCompliance.title': 'Conformité SCHL / APH',
    'schlCompliance.expensesLabel': "Dépenses d'exploitation vs barème SCHL",
    'schlCompliance.expensesBelow': 'Sous les barèmes',
    'schlCompliance.expensesWithin': 'Conformes aux barèmes',
    'schlCompliance.expensesAbove': 'Au-dessus des barèmes',
    'schlCompliance.ltvAphLabel': 'RPV vs maximum APH',
    'schlCompliance.ltvStandardLabel': 'RPV vs maximum SCHL',
    'schlCompliance.status.ok': 'OK',
    'schlCompliance.status.tooHigh': 'Trop élevé',
    'financingSummary.title': 'Résumé du Financement',
    'financingSummary.financingType': 'Type de financement',
    'financingSummary.aphPoints': 'Points APH',
    'financingSummary.qualificationRate': 'Taux de qualification',
    'financingSummary.interestRate': "Taux d'intérêt",
    'financingSummary.contractualRate': 'Taux contractuel',
    'financingSummary.newInterestRate': 'Nouveau Taux d\'Intérêt',
    'financingSummary.amortization': 'Amortissement',
    'financingSummary.remainingAmortization': 'Amortissement restant',
    'financingSummary.debtCoverageRatio': 'Ratio couverture dette',
    'financingSummary.ltvRatio': 'Ratio prêt-valeur',
    'financingSummary.originationFee': 'Frais de dossier',
    'financingSummary.propertyValue': "Valeur de l'immeuble",
    'financingSummary.purchasePrice': "Prix d'achat",
    'financingSummary.borrowingValue': "Valeur d'emprunt",
    'financingSummary.maxLoan': 'Prêt maximal',
    'financingSummary.assumedLoan': 'Prêt assumé',
    'financingSummary.loanBalance': 'Solde du prêt',
    'financingSummary.cmhcPremium': 'Prime SCHL',
    'financingSummary.totalLoanWithPremium': 'Prêt total incluant prime',
    'financingSummary.downPayment': 'Mise de fonds',
    'financingSummary.trappedEquity': 'Équité figée',
    'financingSummary.financingAndWorksFees': 'Frais de financement et travaux',
    'financingSummary.financingFees': 'Frais de financement',
    'financingSummary.acquisitionFees': "Frais d'acquisition",
    'financingSummary.equityWithdrawal': "Retrait d'équité",
    'financingSummary.totalInvestment': 'Investissement total',
    'financingSummary.monthlyPayment': 'Paiement mensuel',
    'financingSummary.monthlyPaymentInterestOnly': 'Paiement mensuel (intérêts seulement)',
    'financingSummary.conventional': 'Conventionnel',
    'financingSummary.cmhc': 'SCHL Standard',
    'financingSummary.cmhc_aph': 'SCHL APH Select',
    'financingSummary.private': 'Prêt privé',
    'financingSummary.assumption': 'Assumation hypothécaire',
    'financingSummary.existing': 'Financement en place',
    'financingSummary.none': 'Aucune hypothèque',
    'financingSummary.existingLoan': 'Prêt en place',
    'financingSummary.netEquity': 'Équité nette',
    'financingSummary.assumptionRemainingMonths': 'Amortissement restant (mois, estimé)',
    'financingSummary.assumptionTermEnd': 'Fin du terme (notée)',
    'scenarioList.viewReport': 'Voir le rapport',
    'scenarioList.edit': 'Modifier',
    'scenarioList.duplicate': 'Dupliquer',
    'scenarioList.delete': 'Supprimer',
    'basicInfo.title': 'Informations de base',
    'basicInfo.address': 'Adresse',
    'basicInfo.city': 'Ville',
    'basicInfo.province': 'Province',
    'basicInfo.postalCode': 'Code postal',
    'basicInfo.askingPrice': 'Prix demandé',
    'basicInfo.purchasePrice': "Prix d'achat",
    'basicInfo.estimatedMarketValue': 'Valeur estimée actuelle',
    'basicInfo.municipalEvaluation': 'Évaluation municipale',
    'basicInfo.numberOfUnits': "Nombre d'unités",
    'basicInfo.structureType': 'Type de structure',
    'basicInfo.structureType.woodFrame': 'Bois',
    'basicInfo.structureType.concrete': 'Béton',
    'basicInfo.schlBenchmarkVersion': 'Barèmes de dépenses SCHL',
    'basicInfo.schlBenchmarkVersion.2023-2024': '2023–2024 (avant le 8 juin 2026)',
    'basicInfo.schlBenchmarkVersion.2025-2026': '2025–2026 (à partir du 8 juin 2026)',
    'basicInfo.schlBenchmarkVersion.hint': 'Choisissez le cycle de barèmes nationaux SCHL pour les dépenses d\'exploitation (inclut 1 % du RBE pour les autres coûts en 2025–2026).',
    'basicInfo.buildingPhotoLabel': 'Photo de l’immeuble (rapport PDF)',
    'basicInfo.buildingPhotoHint': 'Affichée sur la page de garde du PDF. Enregistrez l’immeuble une première fois, puis téléversez.',
    'basicInfo.buildingPhotoRequiresSave': 'Enregistrez l’immeuble avant d’ajouter une photo.',
    'basicInfo.buildingPhotoError': 'Échec du téléversement. Vérifiez la connexion et réessayez.',
    'basicInfo.buildingPhotosTitle': 'Photos de l’immeuble',
    'basicInfo.buildingPhotosHint':
      'Ajoutez une ou plusieurs photos. La première sert de couverture (PDF, courriel pocket). L’ordre suit le téléversement.',
    'basicInfo.buildingPhotosChooseButton': 'Choisir des photos',
    'basicInfo.buildingPhotosChooseHint':
      'Le téléversement démarre dès que vous choisissez les fichiers (JPEG, PNG, WebP, GIF).',
    'basicInfo.buildingPhotosNoValidImages':
      'Aucun fichier image reconnu (le navigateur peut omettre le type MIME). Essayez un autre fichier ou renommez-le avec l’extension .webp ou .jpg.',
    'basicInfo.buildingPhotosMax': 'Nombre maximum de photos atteint.',
    'basicInfo.buildingPhotoRemove': 'Retirer la photo',
    'basicInfo.buildingPhotoCover': 'Couverture',
    'basicInfo.buildingPhotosUploadSuccess':
      '{count} photo(s) enregistrée(s) — elles s’affichent dans la grille ci-dessous.',
    'basicInfo.buildingPhotosWhereUsed':
      'Ces images servent de couverture PDF, d’aperçu pocket listing et dans les courriels marketing (1re photo = couverture).',
    'basicInfo.buildingPhotosThumbError':
      'Impossible d’afficher cette image (URL, règles Storage ou réseau). Ouvrez les outils développeur (F12) → onglet Réseau.',
    'basicInfo.buildingPhotosReorderUp': 'Remonter (vers la couverture)',
    'basicInfo.buildingPhotosReorderDown': 'Descendre',
    'revenue.title': 'Revenus annuels',
    'revenue.annualRent': 'Loyers annuels',
    'revenue.otherRevenue': 'Autres revenus',
    'revenue.parkingRevenue': 'Revenus de stationnement',
    'revenue.internetRevenue': 'Revenus Internet',
    'revenue.storageRevenue': 'Revenus de rangement',
    'revenue.vacancyRate': 'Vacances / mauvaises créances (%)',
    'revenue.vacancyRateSchlHint': 'Barème SCHL : 3 % du revenu brut potentiel.',
    'revenue.effectiveGrossIncome': 'Revenus effectifs',
    'operatingExpenses.title': "Dépenses d'exploitation",
    'operatingExpenses.insurance': 'Assurance',
    'operatingExpenses.municipalTaxes': 'Taxes municipales',
    'operatingExpenses.schoolTaxes': 'Taxes scolaires',
    'operatingExpenses.electricityHeating': 'Électricité/Chauffage',
    'operatingExpenses.maintenancePerUnit': 'Entretien (par logement)',
    'operatingExpenses.conciergePerUnit': 'Conciergerie (par logement)',
    'operatingExpenses.managementRate': 'Gestion / Administration (%)',
    'operatingExpenses.otherExpenses': 'Autres dépenses',
    'operatingExpenses.totalExpenses': 'Total des dépenses',
    'operatingExpenses.heating': 'Chauffage',
    'operatingExpenses.electricity': 'Électricité',
    'operatingExpenses.landscaping': 'Aménagement paysager',
    'operatingExpenses.snowRemoval': 'Déneigement',
    'operatingExpenses.extermination': 'Extermination',
    'operatingExpenses.fireInspection': 'Inspection incendie',
    'operatingExpenses.advertising': 'Publicité',
    'operatingExpenses.legal': 'Frais légaux',
    'operatingExpenses.accounting': 'Comptabilité',
    'operatingExpenses.elevator': 'Ascenseur',
    'operatingExpenses.cableInternet': 'Câble/Internet',
    'operatingExpenses.garbage': 'Ordures',
    'operatingExpenses.hotWater': 'Eau chaude',
    'operatingExpenses.numHeatPumps': 'Thermopompes murales (nombre)',
    'operatingExpenses.numFridges': 'Réfrigérateurs (nombre)',
    'operatingExpenses.numStoves': 'Cuisinières (nombre)',
    'operatingExpenses.numDishwashers': 'Lave-vaisselles (nombre)',
    'operatingExpenses.numWashers': 'Laveuses (nombre)',
    'operatingExpenses.numDryers': 'Sécheuses (nombre)',
    'operatingExpenses.numElevators': 'Ascenseurs (nombre)',
    'operatingExpenses.replacementReserve': 'Réserve de remplacement',
    'operatingExpenses.includedInOther': 'Inclus dans Autres dépenses',
    'operatingExpenses.appliances': 'Appareils électroménagers',
    'operatingExpenses.washerDryer': 'Laveuse / sécheuse (partagé)',
    'pocketListing.badge': 'Pocket deal',
    'pocketListing.headline': 'Opportunité d’investissement',
    'pocketListing.noPhoto': 'Aucune photo d’immeuble pour cette fiche.',
    'pocketListing.about': 'À propos de ce bien',
    'pocketListing.keyFigures': 'Chiffres clés',
    'pocketListing.vacancyAllowance': 'Provision pour vacances (estimée)',
    'pocketListing.effectiveGross': 'Revenu brut effectif',
    'pocketListing.tapForSources': 'Ouvrir le détail des revenus',
    'pocketListing.tapForDetail': 'Ouvrir le détail des dépenses',
    'pocketListing.expenseTotalNote':
      'Le total correspond à l’analyse financière. Seuls les postes non nuls sont listés.',
    'pocketListing.workspaceSectionTitle': 'Opportunité d’investissement',
    'pocketListing.revenueExpenseSectionTitle': 'Revenus et charges',
    'pocketListing.revenueExpenseSectionHint': 'Ouvrez chaque carte pour le détail par poste.',
    'pocketListing.noBreakdownLines': 'Aucun poste à afficher.',
    'pocketListing.photoPrev': 'Photo précédente',
    'pocketListing.photoNext': 'Photo suivante',
    'fieldHelp.moreInfo': "Afficher l'explication",
    'financing.title': 'Financement',
    'financing.type': 'Type de financement',
    'financing.type.conventional': 'Conventionnel',
    'financing.type.cmhc': 'SCHL Standard',
    'financing.type.cmhc_aph': 'SCHL APH Select',
    'financing.type.private': 'Prêt privé',
    'financing.type.assumption': 'Assumation hypothécaire',
    'financing.assumedLoanBalance': 'Solde restant du prêt (assumation)',
    'financing.assumptionMonthlyPayment': 'Paiement mensuel',
    'financing.assumptionRateHint': "Taux d'intérêt effectif annuel (même convention que le reste de l'appli).",
    'financing.assumptionTermEndOptional': 'Fin du terme (optionnel)',
    'financing.assumptionTermEndHint': "Fin du terme hypothécaire en cours (à titre informatif ; non utilisé dans le modèle).",
    'financing.assumptionDerivedAmortMonths': 'Amortissement restant (mois, estimé)',
    'financing.assumptionDerivedAmortMonthsHint': 'Déduit du solde, du versement et du taux (amortissement canadien, capitalisation semestrielle).',
    'financing.assumptionPaymentTooLow': 'Le versement doit dépasser un mois d’intérêts sur le solde.',
    'financing.aphPoints': 'Points APH',
    'financing.mortgageRate': "Taux d'intérêt (%)",
    'financing.contractualRate': 'Taux contractuel (%)',
    'financing.ltvRatio': 'Ratio Prêt Valeur (RPV)',
    'financing.amortization': 'Amortissement (années)',
    'financing.term': 'Terme (années)',
    'financing.originationFee': 'Frais de dossier',
    'financing.qualificationRate': 'Taux de qualification (%)',
    'financing.debtCoverageRatio': 'Ratio couverture dette',
    'financing.financingCapRate': 'Cap rate de financement (%)',
    'financing.financingCapRateHint':
      "Optionnel. Taux de capitalisation du marché utilisé par le prêteur ; la valeur d'emprunt retenue est le minimum entre le RNM ÷ taux et la valeur basée sur les revenus.",
    'financing.typeHelp':
      'Structure du prêt pour ce scénario : conventionnel (banque, mise de fonds d’environ 20 %), SCHL (mise de fonds plus faible), SCHL APH Select (RPV maximal et prime selon les points), prêt privé, ou assumation d’une hypothèque existante.',
    'financing.contractualRateHelp':
      'Taux d’intérêt réel du prêt. Il sert à calculer le paiement hypothécaire et le cashflow, pas le montant maximal du prêt.',
    'financing.qualificationRateHelp':
      'Taux de qualification (taux « stress ») du prêteur. Avec le ratio de couverture de la dette et l’amortissement, il détermine le prêt maximal. Il est souvent plus élevé que le taux contractuel.',
    'financing.debtCoverageRatioHelp':
      'Revenu net d’exploitation (RNM) minimum exigé par rapport au service de la dette annuel. Par exemple, 1,15 signifie que le RNM doit couvrir 115 % des versements. Un ratio plus élevé diminue le prêt maximal.',
    'financing.amortizationHelp':
      'Nombre d’années pour rembourser le prêt en entier. Un amortissement plus long réduit le versement et peut augmenter le prêt maximal.',
    'financing.termHelp':
      'Durée du contrat hypothécaire avant renouvellement. Utilisé pour les projections sur le terme ; il ne change pas l’échéancier d’amortissement.',
    'financing.ltvRatioHelp':
      'Pourcentage du prix d’achat que le prêteur privé accepte de financer (ratio prêt-valeur).',
    'financing.originationFeeHelp':
      'Frais de dossier du prêteur privé, en % du prêt ou en montant fixe. Ils s’ajoutent aux frais d’acquisition.',
    'financing.financingCapRateHelp':
      'Optionnel. Taux de capitalisation du marché utilisé par le prêteur. La valeur d’emprunt retenue est le minimum entre le RNM ÷ ce taux et la valeur basée sur les revenus.',
    'financing.aphPointsHelp':
      'Points du programme SCHL APH Select. À 70 points, le RPV maximal passe à 90 % ; à 100 points, à 95 %. Des rabais de prime peuvent aussi s’appliquer.',
    'financing.assumedLoanBalanceHelp':
      'Solde restant de l’hypothèque que vous assumez.',
    'financing.assumptionMonthlyPaymentHelp':
      'Versement mensuel de l’hypothèque assumée. Avec le taux et le solde, il sert à estimer l’amortissement restant.',
    'financingSummary.financingCapRate': 'Cap rate de financement',
    'financingFees.environmental1': 'Env. Phase 1',
    'financingFees.appraiser': 'Évaluateur agréé',
    'financingFees.cmhcAnalysis': "Frais d'analyse SCHL",
    'financingFees.cmhcTax': 'Taxe sur la prime SCHL',
    'financingFees.otherFees': 'Autres frais',
    'financingFees.notary': 'Notaire',
    'financingFees.workCost': 'Coût des travaux',
    'financingFees.sectionTitle': 'Frais de financement',
    'financingFees.sectionTitleWithWork': "Frais de financement et travaux d'optimisation",
    'financingFees.total': 'Total frais de financement',
    'financingFees.totalWithWork': 'Total frais de financement et travaux',
    'acquisitionCosts.title': "Frais d'acquisition",
    'acquisitionCosts.expertises': 'Expertises',
    'acquisitionCosts.expertisesInfo': 'Inspection, études environnementales, évaluateur et autres tests de due diligence regroupés en un montant.',
    'acquisitionCosts.notary': 'Notaire',
    'acquisitionCosts.notaryInfo': 'Honoraires du notaire et frais juridiques pour conclure l’achat.',
    'acquisitionCosts.cmhcAnalysis': "Frais d'analyse SCHL",
    'acquisitionCosts.cmhcAnalysisInfo': 'Frais d’analyse SCHL, calculés automatiquement selon le nombre de logements (150 $ par logement).',
    'acquisitionCosts.cmhcTax': 'Taxe sur la prime SCHL',
    'acquisitionCosts.cmhcTaxInfo': 'Taxe provinciale sur la prime d’assurance SCHL (9 %). Calculée automatiquement.',
    'acquisitionCosts.welcomeTax': 'Taxe de bienvenue',
    'acquisitionCosts.welcomeTaxInfo': 'Droit de mutation municipale (taxe de bienvenue), calculé automatiquement à partir du prix d’achat. Vous pouvez le modifier au besoin.',
    'acquisitionCosts.otherFees': 'Autres frais',
    'acquisitionCosts.otherFeesInfo': 'Tous les autres frais de clôture non listés séparément, par exemple des travaux à l’acquisition si vous n’utilisez pas le détail avancé.',
    'acquisitionCosts.total': "Total des Frais d'Acquisition",
    'acquisitionCosts.totalInfo': 'Somme de tous les frais d’acquisition saisis ci-dessus (plus les frais de dossier du prêt privé, le cas échéant).',
    'acquisitionCosts.inspection': 'Inspection',
    'acquisitionCosts.inspectionInfo': 'Frais d’inspection de l’immeuble à l’acquisition.',
    'acquisitionCosts.environmental1': 'Env. Phase I',
    'acquisitionCosts.environmental1Info': 'Étude environnementale de phase I (historique du site et inspection visuelle).',
    'acquisitionCosts.environmental2': 'Env. Phase II',
    'acquisitionCosts.environmental2Info': 'Étude environnementale de phase II (échantillonnage et analyses, si requise).',
    'acquisitionCosts.environmental3': 'Env. Phase III',
    'acquisitionCosts.environmental3Info': 'Travaux environnementaux de phase III (caractérisation détaillée ou réhabilitation, si requis).',
    'acquisitionCosts.appraiser': 'Évaluateur agréé',
    'acquisitionCosts.appraiserInfo': 'Honoraires de l’évaluateur agréé, souvent exigés par le prêteur.',
    'acquisitionCosts.renovations': 'Rénovations',
    'acquisitionCosts.renovationsInfo': 'Travaux ou rénovations prévus à l’acquisition et payés par l’acheteur.',
    'currentFinancing.title': 'Financement en cours',
    'currentFinancing.financingType': 'Type de financement',
    'currentFinancing.bank': 'Institution bancaire',
    'currentFinancing.financedAmount': 'Montant financé',
    'currentFinancing.interestRate': "Taux d'intérêt (%)",
    'currentFinancing.loanStartDate': 'Date de début du prêt',
    'currentFinancing.amortization': "Durée d'amortissement (années)",
    'currentFinancing.termEndDate': 'Fin du terme en cours',
    'scenarioForm.financing.title': 'Scénario de financement',
    'scenarioForm.refinancing.title': 'Scénario de refinancement',
    'scenarioForm.optimization.title': "Scénario d'optimisation",
    'scenarioForm.renewal.title': 'Scénario de renouvellement',
    'scenarioForm.genericTitle': 'Scénario',
    'scenarioForm.parameters': 'Paramètres du scénario',
    'scenarioForm.titleLabel': 'Titre',
    'scenarioForm.titlePlaceholder': 'Scénario',
    'scenarioForm.revenueGrowthPct': 'Croissance des revenus (%)',
    'scenarioForm.expenseGrowthPct': 'Croissance des dépenses (%)',
    'scenarioForm.valueAppreciationPct': 'Appréciation de la valeur (%)',
    'scenarioForm.refinanceYears': 'Refinancement dans (années)',
    'scenarioForm.marketValue': 'Nouvelle valeur marchande',
    'scenarioForm.notPossible': "Ce scénario de refinancement n'est pas possible.",
    'scenarioForm.newInterestRate': "Nouveau taux d'intérêt (%)",
    'scenarioForm.viewSummary': 'Voir les résultats',
    'scenarioForm.editScenario': 'Modifier le scénario',
    'recommendations.title': 'Recommandations',
    'recommendations.capRate.veryLow':
      "Le taux de capitalisation est très faible. Considérez négocier le prix ou augmenter les revenus. Si l'immeuble est très récent ou qu'une forte optimisation est possible, le taux de capitalisation peut être acceptable.",
    'recommendations.capRate.low':
      "Le taux de capitalisation est acceptable mais pourrait être amélioré. Considérer une négociation du prix ou une augmentation des loyers. Si l'immeuble est récent (moins de 15 ans) ou qu'une optimisation est possible, le taux de capitalisation peut être acceptable.",
    'recommendations.capRate.good': 'Bon taux de capitalisation supérieur à 5%.',
    'recommendations.cashFlow.negative':
      "Cash flow négatif - l'immeuble ne s'autofinance pas. Révisez votre stratégie ou prévoyez des fonds supplémentaires.",
    'recommendations.cashFlow.low':
      'Cash flow de positif mais très faible (inférieur à 2% des revenus net). Prévoyez une réserve pour imprévus.',
    'recommendations.cashFlow.good':
      'Cash flow positif permettant une bonne marge de manœuvre.',
    'executiveSummary.title': 'Résumé Exécutif',
    'executiveSummary.p1.part1': 'Cet immeuble de',
    'executiveSummary.units': 'unités',
    'executiveSummary.p1.part2': 'situé au',
    'executiveSummary.addressNotSpecified': 'adresse non spécifiée',
    'executiveSummary.p1.part3': 'représente un investissement de',
    'executiveSummary.p2.part1': "Avec un revenu net d'exploitation (NOI) de",
    'executiveSummary.p2.part2': 'le projet génère un taux de capitalisation de',
    'executiveSummary.p2.part3': 'Le cashflow annuel projeté est de',
    'executiveSummary.p2.part4': 'pour un rendement sur mise de fonds de',
    'executiveSummary.p3.part1': "L'investissement total requis (mise de fonds + frais) s'élève à",
    'executiveSummary.p3.part2': 'avec un paiement hypothécaire mensuel de'
  }
};

const LanguageContext = createContext();

function getBrowserLanguage() {
  const userLang = navigator.language || navigator.userLanguage || 'en';
  return userLang.startsWith('fr') ? 'fr' : 'en';
}

export const LanguageProvider = ({ children }) => {
  const { currentUser, userProfile, updateUserProfile } = useAuth();
  const [language, setLanguageState] = useState(getBrowserLanguage);

  useEffect(() => {
    if (userProfile?.language === 'fr' || userProfile?.language === 'en') {
      setLanguageState(userProfile.language);
    }
  }, [userProfile?.language]);

  const setLanguage = useCallback(
    (lang) => {
      setLanguageState(lang);
      if (currentUser?.uid && (lang === 'fr' || lang === 'en')) {
        updateUserProfile?.({ language: lang });
      }
    },
    [currentUser?.uid, updateUserProfile]
  );

  // Référence stable tant que `language` ne change pas — évite de relancer les effets
  // (ex. SignupPage liaison investisseur) à chaque re-render du provider (profil Firestore, etc.).
  const t = useCallback((key) => translations[language][key] || key, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);

